import { saveRecording } from "./finalizeRecording";
import { chooseRecordingMimeType } from "./recordingFormat";
import { nextWhiteboardZoom } from "./whiteboardZoom";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";

const COLOURS = [
  "#111827",
  "#2563eb",
  "#dc2626",
  "#16a34a",
  "#9333ea",
  "#ea580c"
];

const INITIAL_PAGE_ID = "whiteboard-1";
const LOGICAL_BOARD_WIDTH = 1600;
const LOGICAL_BOARD_HEIGHT = 900;
const GRID_SIZE = 32;
const CAMERA_TILE_WIDTH_RATIO = 0.2;
const CAMERA_TILE_OFFSET_RATIO = 0.015;
const RULER_WIDTH = (15 * GRID_SIZE) / LOGICAL_BOARD_WIDTH;
const RULER_HEIGHT = (3 * GRID_SIZE) / LOGICAL_BOARD_HEIGHT;
const BOARD_EXTENSION_SECTIONS = 1;
const COUNTER_COLOURS = [
  "#ef4444", "#2563eb", "#22c55e", "#facc15",
  "#a855f7", "#f97316", "#ec4899", "#14b8a6"
];

const isGameItem = (item) =>
  ["game-dice", "game-counter", "game-coin", "game-spinner"].includes(
    item?.type
  );

const isSelectableObject = (item) =>
  item?.type === "image" ||
  item?.type === "math" ||
  item?.type === "text" ||
  item?.type === "compass" ||
  isGameItem(item);

const scaleItemVertically = (item, pageId, ratio) => {
  if ((item.pageId || INITIAL_PAGE_ID) !== pageId) return item;

  const scalePoint = (point) =>
    point && typeof point.y === "number"
      ? { ...point, y: point.y * ratio }
      : point;

  return {
    ...item,
    ...(typeof item.y === "number" ? { y: item.y * ratio } : {}),
    ...(typeof item.height === "number" ? { height: item.height * ratio } : {}),
    ...(typeof item.fontScale === "number"
      ? { fontScale: item.fontScale * ratio }
      : {}),
    ...(item.start ? { start: scalePoint(item.start) } : {}),
    ...(item.end ? { end: scalePoint(item.end) } : {}),
    ...(Array.isArray(item.points)
      ? { points: item.points.map(scalePoint) }
      : {})
  };
};

const escapeRichText = (value) =>
  String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");

const getTextItemHtml = (item) => {
  if (item?.richHtml) return item.richHtml;

  let html = escapeRichText(item?.text).replaceAll("\n", "<br>");
  if (item?.underline) html = `<u>${html}</u>`;
  if (item?.italic) html = `<i>${html}</i>`;
  if (item?.bold) html = `<b>${html}</b>`;
  return html;
};

const sanitiseRichTextHtml = (editor) => {
  const serialiseChildren = (parent) => {
    let html = "";
    for (const child of parent.childNodes) {
      const block = child.nodeType === Node.ELEMENT_NODE &&
        ["DIV", "P"].includes(child.tagName);
      if (block && html && !html.endsWith("<br>")) html += "<br>";
      html += serialise(child);
    }
    return html;
  };
  const serialise = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeRichText(node.nodeValue);
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const tag = node.tagName.toLowerCase();
    if (tag === "br") return "<br>";
    const content = serialiseChildren(node);
    if (["b", "strong"].includes(tag)) return `<b>${content}</b>`;
    if (["i", "em"].includes(tag)) return `<i>${content}</i>`;
    if (tag === "u") return `<u>${content}</u>`;
    if (["div", "p"].includes(tag)) return content.endsWith("<br>") ? content : `${content}<br>`;
    if (tag === "span") {
      const styles = [];
      if (["bold", "700", "800", "900"].includes(node.style.fontWeight)) {
        styles.push("font-weight:bold");
      } else if (node.style.fontWeight === "normal" || node.style.fontWeight === "400") {
        styles.push("font-weight:normal");
      }
      if (node.style.fontStyle === "italic") styles.push("font-style:italic");
      if (node.style.fontStyle === "normal") styles.push("font-style:normal");
      if (node.style.textDecoration.includes("underline")) {
        styles.push("text-decoration:underline");
      }
      return styles.length
        ? `<span style="${styles.join(";")}">${content}</span>`
        : content;
    }
    return content;
  };

  return serialiseChildren(editor)
    .replace(/(?:<br>)+$/, "");
};

const getRichTextLines = (item) => {
  const baseStyle = {
    bold: !!item?.bold,
    italic: !!item?.italic,
    underline: !!item?.underline
  };
  if (!item?.richHtml || typeof document === "undefined") {
    return String(item?.text || "").split("\n").map((text) => [
      { text, ...baseStyle }
    ]);
  }

  const root = document.createElement("div");
  root.innerHTML = item.richHtml;
  const lines = [[]];
  const newLine = () => lines.push([]);
  const appendText = (value, style) => {
    String(value || "").split("\n").forEach((part, index) => {
      if (index > 0) newLine();
      if (!part) return;
      const line = lines[lines.length - 1];
      const previous = line[line.length - 1];
      if (
        previous &&
        previous.bold === style.bold &&
        previous.italic === style.italic &&
        previous.underline === style.underline
      ) {
        previous.text += part;
      } else {
        line.push({ text: part, ...style });
      }
    });
  };
  const visit = (node, inherited) => {
    if (node.nodeType === 3) {
      appendText(node.nodeValue, inherited);
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.tagName.toLowerCase();
    if (tag === "br") {
      newLine();
      return;
    }
    const style = { ...inherited };
    if (["b", "strong"].includes(tag)) style.bold = true;
    if (["i", "em"].includes(tag)) style.italic = true;
    if (tag === "u") style.underline = true;
    if (tag === "span") {
      if (["normal", "400"].includes(node.style.fontWeight)) style.bold = false;
      if (["bold", "700", "800", "900"].includes(node.style.fontWeight)) style.bold = true;
      if (node.style.fontStyle === "normal") style.italic = false;
      if (node.style.fontStyle === "italic") style.italic = true;
      if (node.style.textDecoration.includes("underline")) style.underline = true;
    }
    [...node.childNodes].forEach((child) => visit(child, style));
    if (["div", "p"].includes(tag) && lines[lines.length - 1].length) newLine();
  };

  [...root.childNodes].forEach((node) => visit(node, {
    bold: false,
    italic: false,
    underline: false
  }));
  if (lines.length > 1 && lines[lines.length - 1].length === 0) lines.pop();
  return lines.length ? lines : [[]];
};
const PencilCursorIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
    <path
      d="M3 29l3-9L22 4l6 6-16 16z"
      fill="#facc15"
      stroke="#0f172a"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M22 4l2-2a2 2 0 0 1 3 0l3 3a2 2 0 0 1 0 3l-2 2z"
      fill="#f87171"
      stroke="#0f172a"
      strokeWidth="1.5"
    />
    <path
      d="M3 29l3-9 6 6z"
      fill="#fde7c0"
      stroke="#0f172a"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path d="M3 29l3-1-2-2z" fill="#0f172a" />
  </svg>
);

const clamp = (value, minimum, maximum) =>
  Math.max(minimum, Math.min(maximum, value));

const normaliseFixedInstrumentSize = (item, heightSections = 1) =>
  item?.type === "math" && item.mathTool === "ruler"
    ? {
        ...item,
        width: RULER_WIDTH,
        height: RULER_HEIGHT / Math.max(1, heightSections)
      }
    : item;

const createId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}`;
};

const CalculatorIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    style={{ display: "block" }}
  >
    <rect
      x="4"
      y="2"
      width="16"
      height="20"
      rx="2.5"
      fill="#f8fafc"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <rect x="6.5" y="4.5" width="11" height="4" rx="0.8" fill="#94a3b8" />
    {[7.5, 12, 16.5].map((x) =>
      [12, 16, 20].map((y) => (
        <rect
          key={`${x}-${y}`}
          x={x - 1.25}
          y={y - 1.25}
          width="2.5"
          height="2.5"
          rx="0.55"
          fill={x === 16.5 && y === 20 ? "#2563eb" : "#334155"}
        />
      ))
    )}
  </svg>
);

const WhiteboardCameraTile = ({
  participant
}) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    const stream = participant.stream;

    if (!video) {
      return undefined;
    }

    if (video.srcObject !== stream) {
      video.srcObject = stream || null;
    }

    if (stream) {
      video.play().catch(() => {});
    }

    return () => {
      if (video.srcObject === stream) {
        video.srcObject = null;
      }
    };
  }, [participant.stream]);

  const cameraUnavailable =
    participant.cameraEnabled === false ||
    !participant.stream;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        flex: "0 0 auto",
        aspectRatio: "16 / 9",
        borderRadius: "14px",
        overflow: "hidden",
        background: "#020617",
        border: "2px solid rgba(255,255,255,.82)",
        boxShadow: "0 10px 30px rgba(15,23,42,.38)"
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: "100%",
          height: "100%",
          display: cameraUnavailable ? "none" : "block",
          objectFit: "cover",
          background: "#020617"
        }}
      />

      {cameraUnavailable && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,.72)",
            fontSize: "1.6rem"
          }}
        >
          📷
        </div>
      )}

      <div
        style={{
          position: "absolute",
          top: "7px",
          right: "7px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "5px",
          pointerEvents: "none"
        }}
      >
        {participant.micEnabled === false && (
          <span
            title="Microphone muted"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              borderRadius: "999px",
              background: "rgba(153,27,27,.94)",
              border: "1px solid rgba(254,202,202,.75)",
              boxShadow: "0 3px 10px rgba(0,0,0,.35)",
              fontSize: "0.82rem"
            }}
          >
            🔇
          </span>
        )}

        {participant.handRaised === true && (
          <span
            title="Hand raised"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              borderRadius: "999px",
              background: "rgba(180,83,9,.96)",
              border: "1px solid rgba(253,230,138,.85)",
              boxShadow: "0 3px 10px rgba(0,0,0,.35)",
              fontSize: "0.82rem"
            }}
          >
            ✋
          </span>
        )}
      </div>

      <div
        style={{
          position: "absolute",
          left: "8px",
          bottom: "7px",
          width: "max-content",
          maxWidth: "calc(100% - 16px)",
          padding: "0.3rem 0.45rem",
          borderRadius: "7px",
          background: "rgba(2,6,23,.76)",
          color: "white",
          fontSize: "0.7rem",
          fontWeight: "700",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {participant.name || "Participant"}
      </div>
    </div>
  );
};

const RecordingCameraPreview = ({ videoTrack }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoTrack || videoTrack.readyState !== "live") {
      return undefined;
    }

    video.srcObject = new MediaStream([videoTrack]);
    video.play().catch(() => {});

    return () => {
      video.pause();
      video.srcObject = null;
    };
  }, [videoTrack]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        borderRadius: "14px",
        overflow: "hidden",
        background: "#020617",
        border: "2px solid rgba(255,255,255,.82)",
        boxShadow: "0 10px 30px rgba(15,23,42,.38)"
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          objectFit: "cover",
          transform: "scaleX(-1)",
          background: "#020617"
        }}
      />
    </div>
  );
};

const CollaborativeWhiteboard = ({
  open,
  isTutor,
  participantId,
  participantName,
  cameraParticipants = [],
  micEnabled,
  handRaised = false,
  localAudioTrack = null,
  localVideoTrack = null,
  remoteAudioTracks = [],
  cameraEnabled,
  isScreenSharing,
  chatOpen,
  unreadMessages = 0,
  onToggleMicrophone,
  onToggleHandRaised,
  onToggleCamera,
  onStartScreenShare,
  onToggleChat,
  onLeaveLesson,
  command,
  sendMessage,
  onSnapshotChange,
  onTutorClose
}) => {
  const canvasRef = useRef(null);
  const inkCanvasRef = useRef(null);
  const redrawRef = useRef(null);
  const containerRef = useRef(null);
  const workspaceRef = useRef(null);
  const drawingRef = useRef(false);
  const activeStrokeRef = useRef(null);
  const strokesRef = useRef([]);
  const pagesRef = useRef([
    {
      id: INITIAL_PAGE_ID,
      title: "Whiteboard 1",
      boardColour: "#ffffff",
      heightSections: 1
    }
  ]);
  const activePageIdRef = useRef(INITIAL_PAGE_ID);
  const imageCacheRef = useRef(new Map());
  const imageTransfersRef = useRef(new Map());
  const processedCommandIdsRef = useRef(new Set());
  const selectedImageIdRef = useRef(null);
  const imageInteractionRef = useRef(null);
  const arrowKeyHoldRef = useRef({
    key: null,
    startedAt: 0
  });
  const lastImageUpdateSentAtRef = useRef(0);
  const lastLiveStrokeSentAtRef = useRef(0);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const lastCursorSentAtRef = useRef(0);
  const lastScrollSentAtRef = useRef(0);
  const whiteboardViewRef = useRef({
    pageId: INITIAL_PAGE_ID,
    scrollProgress: 0
  });
  const localRecordingCursorRef = useRef(null);
  const recordingCursorVisualRef = useRef(null);
  const cursorTimeoutsRef = useRef(new Map());
  const uploadInputRef = useRef(null);
  const textEditorRef = useRef(null);
  const pencilCursorOverlayRef = useRef(null);
  const editingTextIdRef = useRef(null);
  const editingTextOriginalRef = useRef(null);
  const editingTextFormatRef = useRef(null);
  const textDefaultsRef = useRef({ fontFamily: "Arial, sans-serif", size: 24, fontScale: 24 / 900 });
  const calculatorDragRef = useRef(null);
  const gameAnimationTimersRef = useRef(new Map());
  const lastCalculatorUpdateSentAtRef = useRef(0);
  const recorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingStatusRef = useRef("idle");
  const recordingAudioContextRef = useRef(null);
  const recordingAudioSourcesRef = useRef([]);
  const recordingCompositeCanvasRef = useRef(null);
  const recordingCompositeFrameRef = useRef(null);
  const recordingWebcamVideoRef = useRef(null);
  const remoteRecordingNoticeTimerRef = useRef(null);
  const remoteRecordingNoticeRemoveTimerRef = useRef(null);
  const remoteRecordingStatusRef = useRef("idle");
  const calculatorStateRef = useRef({
    open: false,
    expression: "",
    result: "0",
    angleMode: "DEG",
    position: { x: 92, y: 78 },
    editor: [{ type: "text", value: "" }],
    cursor: { index: 0, field: "value", offset: 0 }
  });

  const [strokes, setStrokes] = useState([]);
  const [pages, setPages] = useState(pagesRef.current);
  const [activePageId, setActivePageId] =
    useState(INITIAL_PAGE_ID);
  const [tool, setTool] = useState("pen");
  const [colour, setColour] = useState("#111827");
  const [size, setSize] = useState(3);
  const [remoteCursors, setRemoteCursors] = useState({});
  const [pasteMessage, setPasteMessage] = useState("");
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [hoveringResizeHandle, setHoveringResizeHandle] =
    useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarPanel, setSidebarPanel] = useState(null);
  const [baseBoardSize, setBoardSize] = useState(null);
  const [boardZoom, setBoardZoom] = useState(1);
  const boardZoomRef = useRef(1);
  const zoomAnchorRef = useRef(null);
  const boardSize = useMemo(() => baseBoardSize && ({
    width: baseBoardSize.width * boardZoom,
    height: baseBoardSize.height * boardZoom
  }), [baseBoardSize, boardZoom]);

  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!open || !workspace) return undefined;
    const zoomWithWheel = (event) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      event.stopPropagation();
      const bounds = containerRef.current?.getBoundingClientRect();
      if (!bounds?.width || !bounds?.height || !Number.isFinite(event.deltaY)) return;
      const nextZoom = nextWhiteboardZoom(boardZoomRef.current, event.deltaY,
        event.deltaMode, workspace.clientHeight);
      if (nextZoom === boardZoomRef.current) return;
      zoomAnchorRef.current = {
        x: (event.clientX - bounds.left) / bounds.width,
        y: (event.clientY - bounds.top) / bounds.height,
        clientX: event.clientX,
        clientY: event.clientY
      };
      boardZoomRef.current = nextZoom;
      setBoardZoom(nextZoom);
    };
    workspace.addEventListener("wheel", zoomWithWheel, { passive: false });
    return () => workspace.removeEventListener("wheel", zoomWithWheel);
  }, [open]);

  useLayoutEffect(() => {
    const anchor = zoomAnchorRef.current;
    const workspace = workspaceRef.current;
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!anchor || !workspace || !bounds) return;
    zoomAnchorRef.current = null;
    workspace.scrollTo({
      left: workspace.scrollLeft + bounds.left + anchor.x * bounds.width - anchor.clientX,
      top: workspace.scrollTop + bounds.top + anchor.y * bounds.height - anchor.clientY,
      behavior: "instant"
    });
    // ResizeObserver may arrive after paint; rebuild pixels before showing zoom.
    redrawRef.current?.();
  }, [boardZoom]);
  const [sidebarAnchor, setSidebarAnchor] = useState({
    left: 14,
    top: 82
  });
  const [cameraAnchor, setCameraAnchor] = useState({
    top: 82,
    right: 14,
    width: 320
  });
  const [recordingCameraAnchor, setRecordingCameraAnchor] = useState({
    top: 560,
    right: 14,
    width: 320
  });
  const [editingTextId, setEditingTextId] = useState(null);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [calculatorExpression, setCalculatorExpression] = useState("");
  const [calculatorResult, setCalculatorResult] = useState("0");
  const [calculatorAngleMode, setCalculatorAngleMode] = useState("DEG");
  const [calculatorPosition, setCalculatorPosition] = useState({
    x: 92,
    y: 78
  });
  const [calculatorEditor, setCalculatorEditor] = useState([
    { type: "text", value: "" }
  ]);
  const [calculatorCursor, setCalculatorCursor] = useState({
    index: 0,
    field: "value",
    offset: 0
  });
  const [recordingStatus, setRecordingStatus] = useState("idle");
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [showRecordingOptions, setShowRecordingOptions] = useState(false);
  const [showPdfOptions, setShowPdfOptions] = useState(false);
  const [includeWebcamInRecording, setIncludeWebcamInRecording] = useState(false);
  const [remoteRecordingStatus, setRemoteRecordingStatus] =
    useState("idle");
  const [remoteRecordingNotice, setRemoteRecordingNotice] =
    useState(null);

  const showRemoteRecordingNotice = (nextStatus) => {
    if (isTutor) return;

    const previousStatus = remoteRecordingStatusRef.current;
    remoteRecordingStatusRef.current = nextStatus;
    setRemoteRecordingStatus(nextStatus);

    const kind =
      nextStatus === "paused"
        ? "paused"
        : nextStatus === "idle"
          ? previousStatus !== "idle" ? "stopped" : null
          : previousStatus === "paused" ? "resumed" : "started";

    if (!kind) return;

    if (remoteRecordingNoticeTimerRef.current) {
      window.clearTimeout(remoteRecordingNoticeTimerRef.current);
    }
    if (remoteRecordingNoticeRemoveTimerRef.current) {
      window.clearTimeout(remoteRecordingNoticeRemoveTimerRef.current);
    }

    setRemoteRecordingNotice({ kind, visible: true, id: Date.now() });
    remoteRecordingNoticeTimerRef.current = window.setTimeout(() => {
      setRemoteRecordingNotice((notice) =>
        notice ? { ...notice, visible: false } : null
      );
      remoteRecordingNoticeRemoveTimerRef.current = window.setTimeout(
        () => setRemoteRecordingNotice(null),
        450
      );
    }, 5000);
  };

  useEffect(() => () => {
    if (remoteRecordingNoticeTimerRef.current) {
      window.clearTimeout(remoteRecordingNoticeTimerRef.current);
    }
    if (remoteRecordingNoticeRemoveTimerRef.current) {
      window.clearTimeout(remoteRecordingNoticeRemoveTimerRef.current);
    }
  }, []);

  const safeName =
    participantName?.trim() ||
    (isTutor ? "Tutor" : "Student");

  const formatRecordingTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const clearRecordingTimer = () => {
    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const updateRecordingStatus = (
    status,
    broadcast = true
  ) => {
    recordingStatusRef.current = status;
    setRecordingStatus(status);
    publishSnapshot();
    if (broadcast && isTutor) {
      sendMessage?.({
        type: "whiteboard-recording-status",
        status
      });
    }
  };

  const stopRecordingTracks = () => {
    // These are the recording-owned canvas and mixed-audio tracks. The
    // original lesson microphone tracks are never stopped here.
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    recordingStreamRef.current = null;
    if (recordingCompositeFrameRef.current) {
      window.cancelAnimationFrame(recordingCompositeFrameRef.current);
      recordingCompositeFrameRef.current = null;
    }
    const webcamVideo = recordingWebcamVideoRef.current;
    if (webcamVideo) {
      webcamVideo.pause();
      webcamVideo.srcObject = null;
    }
    recordingWebcamVideoRef.current = null;
    recordingCompositeCanvasRef.current = null;
    recordingCursorVisualRef.current = null;
    recordingAudioSourcesRef.current.forEach((source) => {
      try {
        source.disconnect();
      } catch (error) {
        console.warn("Could not disconnect a recording audio source:", error);
      }
    });
    recordingAudioSourcesRef.current = [];
    const audioContext = recordingAudioContextRef.current;
    recordingAudioContextRef.current = null;
    if (audioContext && audioContext.state !== "closed") {
      audioContext.close().catch(() => {});
    }
  };

  const downloadWhiteboardRecording = (chunks, mimeType) =>
    saveRecording(chunks, mimeType, "whiteboard-recording", message => {
      setPasteMessage(message);
      if (message !== "Preparing recording...") window.setTimeout(() => setPasteMessage(""), 5000);
    });

  const stopWhiteboardRecording = () => {
    clearRecordingTimer();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    } else {
      stopRecordingTracks();
      recorderRef.current = null;
      updateRecordingStatus("idle");
    }
  };

  const startWhiteboardRecording = async (includeWebcam = false) => {
    if (!isTutor || recordingStatus !== "idle") return;
    const canvas = canvasRef.current;
    if (!canvas?.captureStream || !window.MediaRecorder) {
      setPasteMessage("Whiteboard recording is not supported on this desktop setup");
      window.setTimeout(() => setPasteMessage(""), 3000);
      return;
    }

    try {
      setShowRecordingOptions(false);
      redraw();
      const recordingCanvas = document.createElement("canvas");
      const recordingBoardHeight = LOGICAL_BOARD_HEIGHT;
      recordingCanvas.width = LOGICAL_BOARD_WIDTH;
      recordingCanvas.height = recordingBoardHeight;
      const recordingContext = recordingCanvas.getContext("2d");
      if (!recordingContext) {
        throw new Error("Could not create the recording canvas");
      }
      recordingCompositeCanvasRef.current = recordingCanvas;

      let webcamVideo = null;
      if (
        includeWebcam &&
        cameraEnabled &&
        localVideoTrack?.readyState === "live"
      ) {
        webcamVideo = document.createElement("video");
        webcamVideo.muted = true;
        webcamVideo.autoplay = true;
        webcamVideo.playsInline = true;
        webcamVideo.srcObject = new MediaStream([localVideoTrack]);
        recordingWebcamVideoRef.current = webcamVideo;
        await webcamVideo.play().catch(() => {});
      }

      const renderRecordingFrame = () => {
        const workspace = workspaceRef.current;
        const container = containerRef.current;
        const fullLogicalHeight = getActiveLogicalHeight();
        const renderedBoardHeight = Math.max(
          1,
          container?.getBoundingClientRect().height || boardSize?.height || 1
        );
        const logicalPixelsPerScreenPixel =
          fullLogicalHeight / renderedBoardHeight;
        const visibleSourceY = clamp(
          (workspace?.scrollTop || 0) * logicalPixelsPerScreenPixel,
          0,
          Math.max(0, fullLogicalHeight - LOGICAL_BOARD_HEIGHT)
        );

        recordingContext.clearRect(
          0,
          0,
          LOGICAL_BOARD_WIDTH,
          recordingBoardHeight
        );
        recordingContext.drawImage(
          canvas,
          0,
          visibleSourceY * canvas.height / fullLogicalHeight,
          canvas.width,
          LOGICAL_BOARD_HEIGHT * canvas.height / fullLogicalHeight,
          0,
          0,
          LOGICAL_BOARD_WIDTH,
          LOGICAL_BOARD_HEIGHT
        );

        const recordingCursor = localRecordingCursorRef.current;
        if (recordingCursor) {
          const now = performance.now();
          const shouldShow =
            recordingCursor.visible &&
            now - recordingCursor.lastMovedAt < 1500;
          const targetOpacity = shouldShow
            ? recordingCursor.pointerDown ? 0.25 : 0.68
            : 0;
          const visual = recordingCursorVisualRef.current || {
            x: recordingCursor.x,
            y: recordingCursor.y,
            opacity: 0
          };
          visual.x += (recordingCursor.x - visual.x) * 0.16;
          visual.y += (recordingCursor.y - visual.y) * 0.16;
          visual.opacity += (targetOpacity - visual.opacity) *
            (targetOpacity > visual.opacity ? 0.22 : 0.1);
          recordingCursorVisualRef.current = visual;

          if (visual.opacity > 0.01) {
          const cursorX = visual.x * LOGICAL_BOARD_WIDTH;
          const cursorY =
            visual.y * fullLogicalHeight - visibleSourceY;
          if (cursorY >= 0 && cursorY <= LOGICAL_BOARD_HEIGHT) {
          recordingContext.save();
          recordingContext.globalAlpha = visual.opacity;
          recordingContext.translate(cursorX, cursorY);
          recordingContext.scale(0.7, 0.7);
          recordingContext.beginPath();
          recordingContext.moveTo(0, 0);
          recordingContext.lineTo(7, 29);
          recordingContext.lineTo(14, 20);
          recordingContext.lineTo(23, 30);
          recordingContext.lineTo(29, 24);
          recordingContext.lineTo(19, 15);
          recordingContext.lineTo(30, 10);
          recordingContext.closePath();
          recordingContext.fillStyle = "#3b82f6";
          recordingContext.strokeStyle = "rgba(255,255,255,.95)";
          recordingContext.lineWidth = 2.5;
          recordingContext.lineJoin = "round";
          recordingContext.fill();
          recordingContext.stroke();
          recordingContext.restore();
          }
          }
        }

        if (webcamVideo?.readyState >= 2) {
          // Match the live recording preview exactly, so the tutor can see
          // which part of the whiteboard the saved webcam will cover.
          const cameraWidth =
            LOGICAL_BOARD_WIDTH * CAMERA_TILE_WIDTH_RATIO;
          const cameraHeight = cameraWidth * 9 / 16;
          const cameraOffsetX =
            LOGICAL_BOARD_WIDTH * CAMERA_TILE_OFFSET_RATIO;
          const cameraOffsetY =
            LOGICAL_BOARD_HEIGHT * CAMERA_TILE_OFFSET_RATIO;
          const cameraX = LOGICAL_BOARD_WIDTH - cameraOffsetX - cameraWidth;
          const cameraY =
            LOGICAL_BOARD_HEIGHT - cameraOffsetY - cameraHeight;
          const radius = 18;

          recordingContext.save();
          recordingContext.beginPath();
          recordingContext.roundRect(
            cameraX,
            cameraY,
            cameraWidth,
            cameraHeight,
            radius
          );
          recordingContext.clip();
          recordingContext.translate(cameraX + cameraWidth, cameraY);
          recordingContext.scale(-1, 1);
          const videoWidth = webcamVideo.videoWidth || cameraWidth;
          const videoHeight = webcamVideo.videoHeight || cameraHeight;
          const targetAspect = cameraWidth / cameraHeight;
          const videoAspect = videoWidth / videoHeight;
          const sourceWidth = videoAspect > targetAspect
            ? videoHeight * targetAspect
            : videoWidth;
          const sourceHeight = videoAspect > targetAspect
            ? videoHeight
            : videoWidth / targetAspect;
          const sourceX = (videoWidth - sourceWidth) / 2;
          const sourceY = (videoHeight - sourceHeight) / 2;
          recordingContext.drawImage(
            webcamVideo,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            cameraWidth,
            cameraHeight
          );
          recordingContext.restore();

          recordingContext.save();
          recordingContext.strokeStyle = "rgba(255,255,255,.82)";
          recordingContext.lineWidth = 3;
          recordingContext.beginPath();
          recordingContext.roundRect(
            cameraX,
            cameraY,
            cameraWidth,
            cameraHeight,
            radius
          );
          recordingContext.stroke();
          recordingContext.restore();
        }

        recordingCompositeFrameRef.current =
          window.requestAnimationFrame(renderRecordingFrame);
      };
      renderRecordingFrame();

      const stream = recordingCanvas.captureStream(30);
      recordingStreamRef.current = stream;
      const lessonAudioTracks = [
        localAudioTrack,
        ...remoteAudioTracks
      ].filter(
        (track, index, tracks) =>
          track?.readyState === "live" &&
          tracks.findIndex((candidate) => candidate?.id === track.id) === index
      );
      let hasMixedAudio = false;

      if (lessonAudioTracks.length > 0) {
        const AudioContextClass =
          window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) {
          const audioContext = new AudioContextClass();
          const destination = audioContext.createMediaStreamDestination();
          const sources = lessonAudioTracks.map((track) => {
            const source = audioContext.createMediaStreamSource(
              new MediaStream([track])
            );
            source.connect(destination);
            return source;
          });
          if (audioContext.state === "suspended") {
            await audioContext.resume();
          }
          const mixedTrack = destination.stream.getAudioTracks()[0];
          if (mixedTrack) {
            stream.addTrack(mixedTrack);
            hasMixedAudio = true;
            recordingAudioContextRef.current = audioContext;
            recordingAudioSourcesRef.current = sources;
          } else {
            sources.forEach((source) => source.disconnect());
            audioContext.close().catch(() => {});
          }
        }
      }

      const mimeType = chooseRecordingMimeType(MediaRecorder, {
        hasAudio: hasMixedAudio,
        requireMp4: window.learnWithRyanDesktop?.isDesktopApp === true
      });
      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 5000000,
        audioBitsPerSecond: 128000
      });

      recordingChunksRef.current = [];
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data?.size) recordingChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        clearRecordingTimer();
        void downloadWhiteboardRecording([...recordingChunksRef.current], recorder.mimeType || mimeType);
        recordingChunksRef.current = [];
        stopRecordingTracks();
        recorderRef.current = null;
        updateRecordingStatus("idle");
        setRecordingSeconds(0);
      };
      recorder.start(1000);
      setRecordingSeconds(0);
      updateRecordingStatus("recording");
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingSeconds((seconds) => seconds + 1);
      }, 1000);
    } catch (error) {
      console.error("Could not start whiteboard recording:", error);
      clearRecordingTimer();
      stopRecordingTracks();
      recorderRef.current = null;
      updateRecordingStatus("idle");
      setPasteMessage(error.code === "RECORDING_FORMAT_UNSUPPORTED" ? error.message : "Whiteboard recording could not start");
      window.setTimeout(() => setPasteMessage(""), 2800);
    }
  };

  const toggleWhiteboardRecordingPause = () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    if (recorder.state === "recording") {
      recorder.pause();
      clearRecordingTimer();
      updateRecordingStatus("paused");
    } else if (recorder.state === "paused") {
      recorder.resume();
      updateRecordingStatus("recording");
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingSeconds((seconds) => seconds + 1);
      }, 1000);
    }
  };

  const recipientCameraParticipants = useMemo(() => {
    const candidates = cameraParticipants
      .filter((participant) => {
        const candidateId =
          participant?.id || participant?.participantId;

        return (
          candidateId &&
          candidateId !== participantId &&
          participant?.isLocal !== true &&
          participant?.local !== true
        );
      })
      .sort((first, second) => {
        const score = (participant) =>
          (participant.stream ? 4 : 0) +
          (participant.connected !== false ? 2 : 0) +
          (participant.cameraEnabled !== false ? 1 : 0);

        return score(second) - score(first);
      });

    return candidates.slice(0, 1);
  }, [cameraParticipants, participantId]);

  const canvasCursor = useMemo(
    () =>
      tool === "select"
        ? hoveringResizeHandle
          ? "nwse-resize"
          : selectedImageId
            ? "move"
            : "default"
        : tool === "pen"
          ? "none"
        : tool === "eraser"
          ? "cell"
          : "crosshair",
    [tool, selectedImageId, hoveringResizeHandle]
  );

  const publishSnapshot = (
    nextItems = strokesRef.current,
    nextPages = pagesRef.current,
    nextActivePageId = activePageIdRef.current
  ) => {
    onSnapshotChange?.({
      version: 2,
      pages: nextPages,
      activePageId: nextActivePageId,
      items: nextItems,
      calculator: calculatorStateRef.current,
      recordingStatus: recordingStatusRef.current,
      view: { ...whiteboardViewRef.current }
    });
  };

  const applyScrollProgress = (progress, pageId = activePageIdRef.current) => {
    const safeProgress = clamp(Number(progress) || 0, 0, 1);
    whiteboardViewRef.current = {
      pageId,
      scrollProgress: safeProgress
    };

    window.requestAnimationFrame(() => {
      const workspace = workspaceRef.current;
      if (!workspace) return;
      const maximum = Math.max(0, workspace.scrollHeight - workspace.clientHeight);
      const boardHeight = Math.max(
        1,
        containerRef.current?.offsetHeight || workspace.scrollHeight
      );
      workspace.scrollTop = Math.min(maximum, boardHeight * safeProgress);
    });
  };

  const handleWorkspaceScroll = () => {
    const workspace = workspaceRef.current;
    if (!workspace || !isTutor) return;

    const boardHeight = Math.max(
      1,
      containerRef.current?.offsetHeight || workspace.scrollHeight
    );
    const scrollProgress = clamp(workspace.scrollTop / boardHeight, 0, 1);
    whiteboardViewRef.current = {
      pageId: activePageIdRef.current,
      scrollProgress
    };

    const now = performance.now();
    if (now - lastScrollSentAtRef.current < 50) return;
    lastScrollSentAtRef.current = now;

    sendMessage?.({
      type: "whiteboard-scroll",
      pageId: activePageIdRef.current,
      scrollProgress
    });
    publishSnapshot();
  };

  const getPageSections = (pageId = activePageIdRef.current) =>
    Math.max(
      1,
      pagesRef.current.find((page) => page.id === pageId)?.heightSections || 1
    );

  const getActiveLogicalHeight = () =>
    LOGICAL_BOARD_HEIGHT * getPageSections();

  const applyCalculatorState = (
    patch,
    broadcast = true
  ) => {
    const next = {
      ...calculatorStateRef.current,
      ...patch,
      position: patch.position
        ? { ...patch.position }
        : calculatorStateRef.current.position
    };
    if (!next.editor) {
      next.editor = [{ type: "text", value: next.expression || "" }];
    }
    if (!next.cursor) {
      next.cursor = {
        index: 0,
        field: "value",
        offset: (next.expression || "").length
      };
    }

    calculatorStateRef.current = next;
    setCalculatorOpen(next.open);
    setCalculatorExpression(next.expression);
    setCalculatorResult(next.result);
    setCalculatorAngleMode(next.angleMode);
    setCalculatorPosition(next.position);
    setCalculatorEditor(
      next.editor || [{ type: "text", value: next.expression || "" }]
    );
    setCalculatorCursor(
      next.cursor || {
        index: 0,
        field: "value",
        offset: (next.expression || "").length
      }
    );
    publishSnapshot();

    if (broadcast) {
      sendMessage?.({
        type: "whiteboard-calculator",
        state: next
      });
    }

    return next;
  };

  const updateStrokes = (updater) => {
    // strokesRef is the authoritative whiteboard store. Update and publish it
    // synchronously so a participant joining between React render cycles gets
    // every object, including rapidly added game pieces.
    const requested =
      typeof updater === "function"
        ? updater(strokesRef.current)
        : updater;
    const next = requested.map((item) =>
      normaliseFixedInstrumentSize(
        item,
        getPageSections(item.pageId || INITIAL_PAGE_ID)
      )
    );

    strokesRef.current = next;
    setStrokes(next);
    publishSnapshot(next);
  };

  const recordHistory = (action) => {
    undoStackRef.current = [
      ...undoStackRef.current.slice(-99),
      action
    ];
    redoStackRef.current = [];
  };

  const updatePages = (updater) => {
    setPages((current) => {
      const next =
        typeof updater === "function"
          ? updater(current)
          : updater;

      pagesRef.current = next;
      publishSnapshot(
        strokesRef.current,
        next
      );
      return next;
    });
  };

  const selectPageLocally = (pageId) => {
    if (
      !pagesRef.current.some(
        (page) => page.id === pageId
      )
    ) {
      return;
    }

    activePageIdRef.current = pageId;
    whiteboardViewRef.current = {
      pageId,
      scrollProgress: 0
    };
    setActivePageId(pageId);
    setRemoteCursors({});
    selectedImageIdRef.current = null;
    setSelectedImageId(null);
    publishSnapshot(
      strokesRef.current,
      pagesRef.current,
      pageId
    );
    applyScrollProgress(0, pageId);
  };

  const selectImage = (imageId) => {
    selectedImageIdRef.current = imageId;
    setSelectedImageId(imageId);

    if (!imageId) {
      setHoveringResizeHandle(false);
    }
  };

  const drawStroke = (context, stroke, width, height) => {
    if (stroke?.type === "game-counter") {
      const centreX = (stroke.x + stroke.width / 2) * width;
      const centreY = (stroke.y + stroke.height / 2) * height;
      const radius = Math.min(stroke.width * width, stroke.height * height) / 2;
      context.save();
      const shine = context.createRadialGradient(
        centreX - radius * 0.35,
        centreY - radius * 0.38,
        radius * 0.08,
        centreX,
        centreY,
        radius
      );
      shine.addColorStop(0, "#ffffff");
      shine.addColorStop(0.14, stroke.colour);
      shine.addColorStop(1, stroke.colour);
      context.fillStyle = "rgba(15,23,42,.22)";
      context.beginPath();
      context.ellipse(centreX + 4, centreY + 7, radius, radius * 0.92, 0, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = shine;
      context.strokeStyle = "rgba(15,23,42,.42)";
      context.lineWidth = 3;
      context.beginPath();
      context.arc(centreX, centreY, radius, 0, Math.PI * 2);
      context.fill();
      context.stroke();
      context.restore();
      return;
    }

    if (stroke?.type === "game-dice") {
      const x = stroke.x * width;
      const y = stroke.y * height;
      const boxWidth = stroke.width * width;
      const boxHeight = stroke.height * height;
      const faceSize = Math.min(boxWidth, boxHeight) * 0.74;
      const depth = faceSize * 0.24;
      const centreX = x + boxWidth / 2;
      const centreY = y + boxHeight / 2;
      // Centre the complete projected cube (front face plus its depth), while
      // keeping the dotted face a true square.
      const left = -(faceSize + depth) / 2;
      const top = -(faceSize + depth) / 2;
      const tumble = stroke.rolling ? stroke.tumble || 0 : 0;
      context.save();
      context.translate(centreX, centreY);
      context.rotate(tumble);
      context.scale(
        stroke.rolling ? 1 + Math.sin(stroke.rollFrame || 0) * 0.06 : 1,
        stroke.rolling ? 1 - Math.sin(stroke.rollFrame || 0) * 0.04 : 1
      );
      context.shadowColor = "rgba(15,23,42,.35)";
      context.shadowBlur = 15;
      context.shadowOffsetY = 10;
      context.fillStyle = "#94a3b8";
      context.beginPath();
      context.moveTo(left + faceSize, top + depth);
      context.lineTo(left + faceSize + depth, top);
      context.lineTo(left + faceSize + depth, top + faceSize);
      context.lineTo(left + faceSize, top + depth + faceSize);
      context.closePath();
      context.fill();
      context.shadowColor = "transparent";
      context.fillStyle = "#e2e8f0";
      context.beginPath();
      context.moveTo(left, top + depth);
      context.lineTo(left + depth, top);
      context.lineTo(left + faceSize + depth, top);
      context.lineTo(left + faceSize, top + depth);
      context.closePath();
      context.fill();
      const faceGradient = context.createLinearGradient(left, top, left + faceSize, top + faceSize);
      faceGradient.addColorStop(0, stroke.rolling ? "#eff6ff" : "#ffffff");
      faceGradient.addColorStop(1, "#dbe3ee");
      context.fillStyle = faceGradient;
      context.strokeStyle = "#64748b";
      context.lineWidth = 2.5;
      context.beginPath();
      context.roundRect(left, top + depth, faceSize, faceSize, faceSize * 0.12);
      context.fill();
      context.stroke();
      const positions = {
        1: [[.5,.5]], 2: [[.28,.28],[.72,.72]],
        3: [[.28,.28],[.5,.5],[.72,.72]],
        4: [[.28,.28],[.72,.28],[.28,.72],[.72,.72]],
        5: [[.28,.28],[.72,.28],[.5,.5],[.28,.72],[.72,.72]],
        6: [[.28,.24],[.72,.24],[.28,.5],[.72,.5],[.28,.76],[.72,.76]]
      };
      context.fillStyle = "#111827";
      (positions[stroke.value || 1] || positions[1]).forEach(([px, py]) => {
        context.beginPath();
        context.arc(left + faceSize * px, top + depth + faceSize * py, faceSize * .075, 0, Math.PI * 2);
        context.fill();
      });
      context.fillStyle = "rgba(255,255,255,.65)";
      context.beginPath();
      context.roundRect(
        left + faceSize * .1,
        top + depth + faceSize * .08,
        faceSize * .46,
        faceSize * .08,
        faceSize * .04
      );
      context.fill();
      context.restore();
      return;
    }

    if (stroke?.type === "game-coin") {
      const centreX = (stroke.x + stroke.width / 2) * width;
      const centreY = (stroke.y + stroke.height / 2) * height;
      const radius = Math.min(stroke.width * width, stroke.height * height) / 2;
      context.save();
      context.fillStyle = stroke.rolling ? "#fde68a" : "#fbbf24";
      context.strokeStyle = "#a16207";
      context.lineWidth = 5;
      context.beginPath();
      context.arc(centreX, centreY, radius, 0, Math.PI * 2);
      context.fill();
      context.stroke();
      context.strokeStyle = "rgba(255,255,255,.6)";
      context.lineWidth = 2;
      context.beginPath();
      context.arc(centreX, centreY, radius * .78, 0, Math.PI * 2);
      context.stroke();
      context.fillStyle = "#78350f";
      context.font = `800 ${radius * .66}px Arial, sans-serif`;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(stroke.value || "H", centreX, centreY + 1);
      context.restore();
      return;
    }

    if (stroke?.type === "game-spinner") {
      const centreX = (stroke.x + stroke.width / 2) * width;
      const centreY = (stroke.y + stroke.height / 2) * height;
      const radius = Math.min(stroke.width * width, stroke.height * height) / 2;
      const colours = ["#ef4444", "#f59e0b", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#8b5cf6", "#ec4899", "#f97316", "#06b6d4"];
      context.save();
      for (let index = 0; index < 10; index += 1) {
        const start = -Math.PI / 2 + index * Math.PI / 5;
        context.fillStyle = colours[index];
        context.beginPath();
        context.moveTo(centreX, centreY);
        context.arc(centreX, centreY, radius, start, start + Math.PI / 5);
        context.closePath();
        context.fill();
        const labelAngle = start + Math.PI / 10;
        context.fillStyle = "#ffffff";
        context.font = `800 ${Math.max(13, radius * .2)}px Arial, sans-serif`;
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillText(String(index + 1), centreX + Math.cos(labelAngle) * radius * .7, centreY + Math.sin(labelAngle) * radius * .7);
      }
      context.strokeStyle = "#334155";
      context.lineWidth = 4;
      context.beginPath();
      context.arc(centreX, centreY, radius, 0, Math.PI * 2);
      context.stroke();
      const angle = -Math.PI / 2 + ((stroke.value || 1) - .5) * Math.PI / 5;
      context.translate(centreX, centreY);
      context.rotate(angle);
      context.fillStyle = "#111827";
      context.beginPath();
      context.moveTo(radius * .72, 0);
      context.lineTo(-radius * .13, -radius * .1);
      context.lineTo(-radius * .13, radius * .1);
      context.closePath();
      context.fill();
      context.beginPath();
      context.arc(0, 0, radius * .13, 0, Math.PI * 2);
      context.fill();
      context.restore();
      return;
    }

    if (stroke?.type === "image") {
      // Snapshot sync initially sends image metadata as an in-place layer
      // placeholder. Its data arrives in chunks and replaces this same item,
      // preserving the exact stacking order of objects above the image.
      if (!stroke.dataUrl) {
        return;
      }

      let image = imageCacheRef.current.get(stroke.id);

      if (!image) {
        image = new Image();
        image.onload = redraw;
        image.src = stroke.dataUrl;
        imageCacheRef.current.set(stroke.id, image);
      }

      if (image.complete && image.naturalWidth) {
        context.drawImage(
          image,
          stroke.x * width,
          stroke.y * height,
          stroke.width * width,
          stroke.height * height
        );
      }

      return;
    }

    if (stroke?.type === "text") {
      if (
        stroke.id === editingTextIdRef.current &&
        recordingStatus === "idle"
      ) {
        return;
      }

      // The live board uses the same browser text renderer as the editor so
      // bold, italic and underline cannot change when edit mode closes. Keep
      // canvas text for recordings, PDF export and other off-screen renders.
      if (
        recordingStatus === "idle" &&
        context.canvas === canvasRef.current
      ) {
        return;
      }

      context.save();
      const textWidth = (stroke.width || 0.2) * width;
      const textHeight = (stroke.height || 0.08) * height;
      context.translate(
        stroke.x * width + textWidth / 2,
        stroke.y * height + textHeight / 2
      );
      context.rotate(stroke.rotation || 0);
      context.fillStyle = stroke.colour || "#111827";
      const fontSize =
        (stroke.fontScale || (stroke.size || 22) / 900) *
        height;
      const fontFamily = stroke.fontFamily || "Arial, sans-serif";
      context.textBaseline = "top";
      // Only explicit newline characters create a new line. Never reflow
      // shared text according to the local display or text-box width.
      const lines = getRichTextLines(stroke);
      lines.forEach((lineRuns, index) => {
        const lineY = -textHeight / 2 + index * fontSize * 1.25;
        let runX = -textWidth / 2;
        lineRuns.forEach((run) => {
          context.font = `${run.italic ? "italic" : "normal"} ${
            run.bold ? "700" : "400"
          } ${fontSize}px ${fontFamily}`;
          context.fillText(run.text, runX, lineY);
          const runWidth = context.measureText(run.text).width;
          if (run.underline && run.text) {
            context.save();
            context.strokeStyle = stroke.colour || "#111827";
            context.lineWidth = Math.max(1, fontSize * 0.055);
            context.beginPath();
            context.moveTo(runX, lineY + fontSize * 1.04);
            context.lineTo(runX + runWidth, lineY + fontSize * 1.04);
            context.stroke();
            context.restore();
          }
          runX += runWidth;
        });
      });
      context.restore();
      return;
    }

    if (stroke?.type === "shape") {
      const start = stroke.start;
      const end = stroke.end || start;

      if (!start || !end) return;

      const x1 = start.x * width;
      const y1 = start.y * height;
      const x2 = end.x * width;
      const y2 = end.y * height;
      const left = Math.min(x1, x2);
      const top = Math.min(y1, y2);
      const shapeWidth = Math.abs(x2 - x1);
      const shapeHeight = Math.abs(y2 - y1);

      context.save();
      context.strokeStyle = stroke.colour || "#111827";
      context.fillStyle = stroke.colour || "#111827";
      context.lineWidth = stroke.size || 4;
      context.lineCap = "round";
      context.lineJoin = "round";
      context.beginPath();

      if (stroke.shape === "rectangle") {
        context.strokeRect(left, top, shapeWidth, shapeHeight);
      } else if (stroke.shape === "circle") {
        context.ellipse(
          left + shapeWidth / 2,
          top + shapeHeight / 2,
          Math.max(1, shapeWidth / 2),
          Math.max(1, shapeHeight / 2),
          0,
          0,
          Math.PI * 2
        );
        context.stroke();
      } else if (stroke.shape === "triangle") {
        context.moveTo((x1 + x2) / 2, y1);
        context.lineTo(x2, y2);
        context.lineTo(x1, y2);
        context.closePath();
        context.stroke();
      } else {
        context.moveTo(x1, y1);
        context.lineTo(x2, y2);
        context.stroke();

        if (stroke.shape === "arrow") {
          const angle = Math.atan2(y2 - y1, x2 - x1);
          const head = 14 + (stroke.size || 4);
          context.beginPath();
          context.moveTo(x2, y2);
          context.lineTo(
            x2 - head * Math.cos(angle - Math.PI / 6),
            y2 - head * Math.sin(angle - Math.PI / 6)
          );
          context.lineTo(
            x2 - head * Math.cos(angle + Math.PI / 6),
            y2 - head * Math.sin(angle + Math.PI / 6)
          );
          context.closePath();
          context.fill();
        }
      }

      context.restore();
      return;
    }

    if (stroke?.type === "compass-arc") {
      context.save();
      context.strokeStyle = stroke.colour || "#111827";
      context.lineWidth = stroke.size || 4;
      context.lineCap = "round";
      const segments = stroke.segments?.length
        ? stroke.segments
        : [{
            startAngle: stroke.startAngle,
            sweep: stroke.sweep
          }];
      segments.forEach((segment) => {
        if (!segment?.sweep) return;
        context.beginPath();
        context.arc(
          stroke.x * width,
          stroke.y * height,
          stroke.radius * width,
          segment.startAngle,
          segment.startAngle + segment.sweep,
          segment.sweep < 0
        );
        context.stroke();
      });
      context.restore();
      return;
    }

    if (stroke?.type === "compass") {
      const pivotX = stroke.x * width;
      const pivotY = stroke.y * height;
      const radius = (stroke.radius || 0.14) * width;
      const angle = stroke.angle || 0;
      const pencilX = pivotX + radius * Math.cos(angle);
      const pencilY = pivotY + radius * Math.sin(angle);
      const normalX = -Math.sin(angle);
      const normalY = Math.cos(angle);
      const hingeLift = Math.min(105, radius * 0.52);
      const hingeX = (pivotX + pencilX) / 2 - normalX * hingeLift;
      const hingeY = (pivotY + pencilY) / 2 - normalY * hingeLift;

      context.save();
      context.lineCap = "round";
      context.lineJoin = "round";
      context.strokeStyle = "#64748b";
      context.lineWidth = 18;
      context.beginPath();
      context.moveTo(pivotX, pivotY);
      context.lineTo(hingeX, hingeY);
      context.lineTo(pencilX, pencilY);
      context.stroke();
      context.strokeStyle = "#cbd5e1";
      context.lineWidth = 10;
      context.stroke();
      context.fillStyle = "#475569";
      context.beginPath();
      context.arc(hingeX, hingeY, 15, 0, Math.PI * 2);
      context.fill();
      const rotationHandleX = hingeX - normalX * 42;
      const rotationHandleY = hingeY - normalY * 42;
      context.strokeStyle = "#16a34a";
      context.lineWidth = 3;
      context.beginPath();
      context.moveTo(hingeX, hingeY);
      context.lineTo(rotationHandleX, rotationHandleY);
      context.stroke();
      context.fillStyle = "#16a34a";
      context.beginPath();
      context.arc(rotationHandleX, rotationHandleY, 13, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "#ffffff";
      context.font = "bold 17px Arial, sans-serif";
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText("↻", rotationHandleX, rotationHandleY + 1);
      context.fillStyle = "#ef4444";
      context.beginPath();
      context.arc(pencilX, pencilY, 10, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "#0f172a";
      context.beginPath();
      context.arc(pivotX, pivotY, 9, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "#2563eb";
      context.fillRect(
        (pivotX + pencilX) / 2 - 7,
        (pivotY + pencilY) / 2 - 7,
        14,
        14
      );
      context.restore();
      return;
    }

    if (stroke?.type === "math") {
      const cx = (stroke.x ?? 0.5) * width;
      const cy = (stroke.y ?? 0.5) * height;
      const objectWidth = (stroke.width ?? 0.34) * width;
      const objectHeight = (stroke.height ?? 0.28) * height;
      const left = -objectWidth / 2;
      const top = -objectHeight / 2;

      context.save();
      context.translate(cx, cy);
      context.rotate(stroke.rotation || 0);
      context.strokeStyle = stroke.colour || "#334155";
      context.fillStyle = stroke.colour || "#334155";
      context.lineWidth = 2;
      context.lineCap = "round";

      if (stroke.mathTool === "axes") {
        context.strokeStyle = "#111827";
        context.fillStyle = "#111827";
        context.beginPath();
        context.moveTo(left, 0);
        context.lineTo(left + objectWidth, 0);
        context.moveTo(0, top);
        context.lineTo(0, top + objectHeight);
        context.stroke();
        for (let i = 0; i <= 20; i += 1) {
          const x = left + (objectWidth * i) / 20;
          const y = top + (objectHeight * i) / 20;
          context.beginPath();
          context.moveTo(x, -4);
          context.lineTo(x, 4);
          context.moveTo(-4, y);
          context.lineTo(4, y);
          context.stroke();
        }
      } else if (stroke.mathTool === "ruler") {
        context.fillStyle = "rgba(250,204,21,.3)";
        context.strokeStyle = "#92400e";
        context.lineWidth = 2;
        context.fillRect(left, top, objectWidth, objectHeight);
        context.strokeRect(left, top, objectWidth, objectHeight);
        context.fillStyle = "#78350f";
        context.font = `${Math.max(10, objectHeight * 0.2)}px Arial, sans-serif`;
        context.textAlign = "center";
        context.textBaseline = "top";
        for (let i = 0; i <= 150; i += 1) {
          const x = left + (objectWidth * i) / 150;
          const tick =
            i % 10 === 0
              ? objectHeight * 0.42
              : i % 5 === 0
                ? objectHeight * 0.3
                : objectHeight * 0.18;
          context.beginPath();
          context.moveTo(x, top);
          context.lineTo(x, top + tick);
          context.stroke();
          if (i % 10 === 0) {
            context.fillText(String(i / 10), x, top + objectHeight * 0.47);
          }
        }
        context.textAlign = "right";
        context.fillText("cm", left + objectWidth - 5, top + objectHeight * 0.72);
      } else if (stroke.mathTool === "protractor") {
        const radius = objectWidth / 2;
        const baselineY = objectHeight * 0.38;
        context.fillStyle = "rgba(96,165,250,.22)";
        context.strokeStyle = "#1d4ed8";
        context.lineWidth = 2;
        context.beginPath();
        context.moveTo(-radius, baselineY);
        context.arc(0, baselineY, radius, Math.PI, 0);
        context.closePath();
        context.fill();
        context.stroke();
        context.beginPath();
        context.arc(0, baselineY, radius * 0.22, Math.PI, 0);
        context.stroke();
        context.fillStyle = "#1e3a8a";
        context.font = `${Math.max(8, objectHeight * 0.07)}px Arial, sans-serif`;
        context.textAlign = "center";
        context.textBaseline = "middle";
        for (let angle = 0; angle <= 180; angle += 1) {
          const radians = Math.PI + (angle * Math.PI) / 180;
          const outer = radius;
          const inner = outer -
            (angle % 10 === 0
              ? objectHeight * 0.1
              : angle % 5 === 0
                ? objectHeight * 0.065
                : objectHeight * 0.035);
          context.beginPath();
          context.moveTo(inner * Math.cos(radians), baselineY + inner * Math.sin(radians));
          context.lineTo(outer * Math.cos(radians), baselineY + outer * Math.sin(radians));
          context.stroke();
          if (angle % 10 === 0) {
            const labelRadius = radius - objectHeight * 0.17;
            const lx = labelRadius * Math.cos(radians);
            const ly = baselineY + labelRadius * Math.sin(radians);
            context.fillText(String(angle), lx, ly);
            if (angle !== 90) {
              const innerLabelRadius = radius - objectHeight * 0.28;
              context.fillText(
                String(180 - angle),
                innerLabelRadius * Math.cos(radians),
                baselineY + innerLabelRadius * Math.sin(radians)
              );
            }
          }
        }
        context.beginPath();
        context.arc(0, baselineY, 4, 0, Math.PI * 2);
        context.fill();
      } else if (stroke.mathTool === "compass") {
        context.beginPath();
        context.arc(0, 0, Math.min(objectWidth, objectHeight) * 0.38, 0, Math.PI * 2);
        context.stroke();
        context.beginPath();
        context.moveTo(0, top);
        context.lineTo(-objectWidth * 0.16, top + objectHeight);
        context.moveTo(0, top);
        context.lineTo(objectWidth * 0.16, top + objectHeight);
        context.stroke();
        context.beginPath();
        context.arc(0, top, 5, 0, Math.PI * 2);
        context.fill();
      }

      context.restore();
      return;
    }

    if (!stroke?.points?.length) {
      return;
    }

    context.save();
    context.globalAlpha =
      stroke.tool === "highlighter" ? 0.3 : 1;
    context.globalCompositeOperation =
      stroke.tool === "eraser" ? "destination-out" : "source-over";
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = stroke.tool === "eraser" ? "#000000" : stroke.colour;
    context.lineWidth = stroke.size;

    context.beginPath();
    stroke.points.forEach((point, index) => {
      const x = point.x * width;
      const y = point.y * height;

      if (index === 0) {
        context.moveTo(x, y);
      } else {
        context.lineTo(x, y);
      }
    });

    if (stroke.points.length === 1) {
      const point = stroke.points[0];
      context.lineTo(
        point.x * width + 0.01,
        point.y * height + 0.01
      );
    }

    context.stroke();
    context.restore();
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;

    if (!canvas || !container) {
      return;
    }

    const width = LOGICAL_BOARD_WIDTH;
    const activePage = pagesRef.current.find(
      (page) => page.id === activePageIdRef.current
    );
    const height =
      LOGICAL_BOARD_HEIGHT * Math.max(1, activePage?.heightSections || 1);

    // Keep coordinates in logical board units, but rasterise at screen density.
    // Bound allocation for very tall boards and high-density displays.
    const displayedWidth = container.getBoundingClientRect().width;
    const requestedScale = Math.max(1, displayedWidth * (window.devicePixelRatio || 1) / width);
    const resolutionScale = Math.min(requestedScale, 16384 / height,
      16384 / width, Math.sqrt(64 * 1024 * 1024 / (width * height)));
    const pixelWidth = Math.max(1, Math.ceil(width * resolutionScale));
    const pixelHeight = Math.max(1, Math.ceil(height * resolutionScale));

    if (
      canvas.width !== pixelWidth ||
      canvas.height !== pixelHeight
    ) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }

    const context = canvas.getContext("2d");
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.setTransform(pixelWidth / width, 0, 0, pixelHeight / height, 0, 0);
    const boardColour = activePage?.boardColour || "#ffffff";
    context.fillStyle = boardColour;
    context.fillRect(0, 0, width, height);

    const activeBackground = activePage?.background || "square";
    const gridSize = GRID_SIZE;

    const parsedRed = Number.parseInt(boardColour.slice(1, 3), 16);
    const parsedGreen = Number.parseInt(boardColour.slice(3, 5), 16);
    const parsedBlue = Number.parseInt(boardColour.slice(5, 7), 16);
    const red = Number.isNaN(parsedRed) ? 255 : parsedRed;
    const green = Number.isNaN(parsedGreen) ? 255 : parsedGreen;
    const blue = Number.isNaN(parsedBlue) ? 255 : parsedBlue;
    const isDarkBoard = (red * 299 + green * 587 + blue * 114) / 1000 < 145;

    context.strokeStyle = isDarkBoard
      ? "rgba(255,255,255,.2)"
      : "rgba(100,116,139,.18)";
    context.fillStyle = isDarkBoard
      ? "rgba(255,255,255,.34)"
      : "rgba(100,116,139,.38)";
    context.lineWidth = 1;

    if (activeBackground === "square") {
      for (let x = gridSize; x < width; x += gridSize) {
        context.beginPath();
        context.moveTo(x, 0);
        context.lineTo(x, height);
        context.stroke();
      }
      for (let y = gridSize; y < height; y += gridSize) {
        context.beginPath();
        context.moveTo(0, y);
        context.lineTo(width, y);
        context.stroke();
      }
    } else if (activeBackground === "lined") {
      for (let y = gridSize; y < height; y += gridSize) {
        context.beginPath();
        context.moveTo(0, y);
        context.lineTo(width, y);
        context.stroke();
      }
    } else if (activeBackground === "dotted") {
      for (let x = gridSize; x < width; x += gridSize) {
        for (let y = gridSize; y < height; y += gridSize) {
          context.beginPath();
          context.arc(x, y, 1.5, 0, Math.PI * 2);
          context.fill();
        }
      }
    }

    // Erasers clear this transparent layer, leaving the paper and grid intact.
    const inkCanvas = inkCanvasRef.current || document.createElement("canvas");
    inkCanvasRef.current = inkCanvas;
    if (inkCanvas.width !== pixelWidth || inkCanvas.height !== pixelHeight) {
      inkCanvas.width = pixelWidth;
      inkCanvas.height = pixelHeight;
    }
    const inkContext = inkCanvas.getContext("2d");
    inkContext.setTransform(1, 0, 0, 1, 0, 0);
    inkContext.clearRect(0, 0, pixelWidth, pixelHeight);
    inkContext.setTransform(pixelWidth / width, 0, 0, pixelHeight / height, 0, 0);
    strokesRef.current
      .filter(
        (stroke) =>
          (stroke.pageId || INITIAL_PAGE_ID) ===
          activePageIdRef.current
      )
      .forEach((stroke) =>
        drawStroke(inkContext, stroke, width, height)
      );

    if (activeStrokeRef.current) {
      drawStroke(
        inkContext,
        activeStrokeRef.current,
        width,
        height
      );
    }

    context.drawImage(inkCanvas, 0, 0, pixelWidth, pixelHeight, 0, 0, width, height);

    const selectedItem =
      strokesRef.current.find(
        (item) =>
          item.id ===
            selectedImageIdRef.current &&
          isSelectableObject(item) &&
          (item.pageId || INITIAL_PAGE_ID) ===
            activePageIdRef.current
      );

    if (selectedItem) {
      if (selectedItem.type === "compass") {
        const pivotX = selectedItem.x * width;
        const pivotY = selectedItem.y * height;
        const radius = (selectedItem.radius || 0.14) * width;
        const pencilX = pivotX + radius * Math.cos(selectedItem.angle || 0);
        const pencilY = pivotY + radius * Math.sin(selectedItem.angle || 0);
        context.save();
        context.strokeStyle = "#2563eb";
        context.lineWidth = 2;
        context.setLineDash([7, 5]);
        context.beginPath();
        context.arc(pivotX, pivotY, radius, 0, Math.PI * 2);
        context.stroke();
        context.setLineDash([]);
        context.fillStyle = "#2563eb";
        context.fillRect(
          (pivotX + pencilX) / 2 - 8,
          (pivotY + pencilY) / 2 - 8,
          16,
          16
        );
        context.restore();
        return;
      }
      const isMath = selectedItem.type === "math";
      const canRotate =
        isMath || selectedItem.type === "text";
      const itemWidth =
        (selectedItem.width || 0.2) * width;
      const itemHeight =
        (selectedItem.height || 0.08) * height;
      const centreX = isMath
        ? selectedItem.x * width
        : selectedItem.x * width + itemWidth / 2;
      const centreY = isMath
        ? selectedItem.y * height
        : selectedItem.y * height + itemHeight / 2;

      context.save();
      context.translate(centreX, centreY);
      context.rotate(canRotate ? selectedItem.rotation || 0 : 0);
      const hidesGameSelectionBox = [
        "game-dice",
        "game-coin",
        "game-spinner"
      ].includes(selectedItem.type);

      if (!hidesGameSelectionBox) {
        context.strokeStyle = "#2563eb";
        context.lineWidth = 2;
        context.setLineDash([7, 5]);
        context.strokeRect(
          -itemWidth / 2,
          -itemHeight / 2,
          itemWidth,
          itemHeight
        );
        context.setLineDash([]);
      }
      if (
        (!isGameItem(selectedItem) || selectedItem.type === "game-counter") &&
        !(isMath && selectedItem.mathTool === "ruler")
      ) {
        context.fillStyle = "#2563eb";
        context.fillRect(
          itemWidth / 2 - 7,
          itemHeight / 2 - 7,
          14,
          14
        );
        context.fillStyle = "#ffffff";
        context.fillRect(
          itemWidth / 2 - 3,
          itemHeight / 2 - 3,
          6,
          6
        );
      }

      if (canRotate) {
        context.strokeStyle = "#2563eb";
        context.beginPath();
        context.moveTo(0, -itemHeight / 2);
        context.lineTo(0, -itemHeight / 2 - 28);
        context.stroke();
        context.fillStyle = "#2563eb";
        context.beginPath();
        context.arc(0, -itemHeight / 2 - 32, 8, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = "white";
        context.beginPath();
        context.arc(0, -itemHeight / 2 - 32, 3, 0, Math.PI * 2);
        context.fill();
      }
      context.restore();
    }
  };

  redrawRef.current = redraw;

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const loadImage = (source) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(
        new Error("The clipboard image could not be read.")
      );
      image.src = source;
    });

  const createPastedImage = async (file) => {
    const source = await readFileAsDataUrl(file);
    const sourceImage = await loadImage(source);
    const maximumDimension = 1200;
    const initialScale = Math.min(
      1,
      maximumDimension /
        Math.max(
          sourceImage.naturalWidth,
          sourceImage.naturalHeight
        )
    );

    let outputWidth = Math.max(
      1,
      Math.round(sourceImage.naturalWidth * initialScale)
    );
    let outputHeight = Math.max(
      1,
      Math.round(sourceImage.naturalHeight * initialScale)
    );
    let dataUrl = "";

    // Keep worksheet text clear while avoiding unnecessarily large
    // chunked transfers over the WebRTC data channel.
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const compressionCanvas = document.createElement("canvas");
      compressionCanvas.width = outputWidth;
      compressionCanvas.height = outputHeight;

      const compressionContext =
        compressionCanvas.getContext("2d");

      compressionContext.fillStyle = "#ffffff";
      compressionContext.fillRect(
        0,
        0,
        outputWidth,
        outputHeight
      );
      compressionContext.drawImage(
        sourceImage,
        0,
        0,
        outputWidth,
        outputHeight
      );

      dataUrl = compressionCanvas.toDataURL(
        "image/webp",
        Math.max(0.58, 0.84 - attempt * 0.05)
      );

      if (dataUrl.length <= 450000) {
        break;
      }

      outputWidth = Math.max(
        1,
        Math.round(outputWidth * 0.82)
      );
      outputHeight = Math.max(
        1,
        Math.round(outputHeight * 0.82)
      );
    }

    const board =
      canvasRef.current?.getBoundingClientRect();
    const boardWidth = Math.max(1, board?.width || 1200);
    const boardHeight = Math.max(1, board?.height || 700);
    let normalisedWidth = Math.min(
      0.72,
      outputWidth / boardWidth
    );
    let normalisedHeight =
      normalisedWidth *
      (outputHeight / outputWidth) *
      (boardWidth / boardHeight);

    if (normalisedHeight > 0.76) {
      const reduction = 0.76 / normalisedHeight;
      normalisedHeight *= reduction;
      normalisedWidth *= reduction;
    }

    return {
      id: createId(),
      type: "image",
      pageId: activePageIdRef.current,
      ownerId: participantId || "unknown",
      ownerName: safeName,
      dataUrl,
      x: (1 - normalisedWidth) / 2,
      y: (1 - normalisedHeight) / 2,
      width: normalisedWidth,
      height: normalisedHeight
    };
  };

  const sendImageInChunks = (image) => {
    const dataUrl = image?.dataUrl || "";

    if (!dataUrl) {
      return;
    }

    const metadata = { ...image };
    delete metadata.dataUrl;

    const chunkSize = 12000;
    const totalChunks = Math.ceil(
      dataUrl.length / chunkSize
    );

    sendMessage?.({
      type: "whiteboard-image-start",
      image: metadata,
      totalChunks
    });

    for (let index = 0; index < totalChunks; index += 1) {
      sendMessage?.({
        type: "whiteboard-image-chunk",
        imageId: image.id,
        index,
        data: dataUrl.slice(
          index * chunkSize,
          (index + 1) * chunkSize
        )
      });
    }

    sendMessage?.({
      type: "whiteboard-image-end",
      imageId: image.id
    });
  };

  useEffect(() => {
    strokesRef.current = strokes;
    redraw();
  }, [strokes, open, activePageId, selectedImageId, recordingStatus]);

  useEffect(() => {
    if (!editingTextId) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const editor = textEditorRef.current;
      const item = strokesRef.current.find(
        (entry) => entry.id === editingTextId
      );

      if (editor) {
        editor.innerHTML = getTextItemHtml(item);
        editor.focus();
      }

      if (editor) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [editingTextId]);

  useEffect(() => {
    if (!open || !workspaceRef.current) {
      return undefined;
    }

    const fitBoard = () => {
      const bounds =
        workspaceRef.current?.getBoundingClientRect();

      if (!bounds?.width || !bounds?.height) {
        return;
      }

      const targetRatio = 16 / 9;
      const availableWidth = Math.max(1, bounds.width - 24);
      const availableHeight = Math.max(1, bounds.height - 24);
      const sections = getPageSections();
      let width = availableWidth;
      let sectionHeight = width / targetRatio;

      if (sectionHeight > availableHeight) {
        sectionHeight = availableHeight;
        width = sectionHeight * targetRatio;
      }

      setBoardSize({
        width: Math.max(1, Math.floor(width)),
        height: Math.max(1, Math.floor(sectionHeight * sections))
      });
      setSidebarAnchor({
        left: Math.max(14, Math.floor(bounds.left + (bounds.width - width) / 2 + 14)),
        top: Math.max(14, Math.floor(bounds.top + 14))
      });
      const boardLeft = bounds.left + (bounds.width - width) / 2;
      const cameraOffset = width * CAMERA_TILE_OFFSET_RATIO;
      const cameraWidth = Math.max(
        1,
        Math.floor(width * CAMERA_TILE_WIDTH_RATIO)
      );
      setCameraAnchor({
        top: Math.max(14, Math.floor(bounds.top + cameraOffset)),
        right: Math.max(14, Math.floor(window.innerWidth - boardLeft - width + cameraOffset)),
        width: cameraWidth
      });
      setRecordingCameraAnchor({
        top: Math.max(
          14,
          Math.floor(
            bounds.top + sectionHeight - cameraOffset - cameraWidth * 9 / 16
          )
        ),
        right: Math.max(
          14,
          Math.floor(window.innerWidth - boardLeft - width + cameraOffset)
        ),
        width: cameraWidth
      });
    };

    const observer = new ResizeObserver(fitBoard);
    observer.observe(workspaceRef.current);
    fitBoard();

    return () => observer.disconnect();
  }, [open, activePageId, pages]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const observer = new ResizeObserver(redraw);

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    window.addEventListener("resize", redraw);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", redraw);
      inkCanvasRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const isEditingField = (event) => {
      const target = event.target;
      return target?.isContentEditable || target?.closest?.('[contenteditable="true"]') ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName);
    };
    const textMime = "application/x-learnwithryan-whiteboard-text";
    const handleCopy = (event) => {
      if (isEditingField(event) || editingTextIdRef.current) return;
      const item = strokesRef.current.find(entry => entry.id === selectedImageIdRef.current &&
        entry.type === "text" && (entry.pageId || INITIAL_PAGE_ID) === activePageIdRef.current);
      if (!item || !event.clipboardData) return;
      event.clipboardData.setData(textMime, JSON.stringify(item));
      event.clipboardData.setData("text/plain", item.text || "");
      event.preventDefault();
    };
    const handlePaste = async (event) => {
      if (isEditingField(event) || editingTextIdRef.current) return;
      const payload = event.clipboardData?.getData(textMime);
      if (payload) {
        try {
          const copied = JSON.parse(payload);
          if (copied.type !== "text" || typeof copied.text !== "string") return;
          const textRoot = document.createElement("div");
          textRoot.innerHTML = getTextItemHtml(copied);
          const item = {
            id: createId(), type: "text", pageId: activePageIdRef.current,
            ownerId: participantId || "unknown", ownerName: safeName,
            text: copied.text, richHtml: sanitiseRichTextHtml(textRoot),
            x: clamp((Number(copied.x) || 0) + 0.025, 0, Math.max(0, 1 - (Number(copied.width) || 0.2))),
            y: clamp((Number(copied.y) || 0) + 0.025, 0, Math.max(0, 1 - (Number(copied.height) || 0.08))),
            width: clamp(Number(copied.width) || 0.2, 0.001, 1),
            height: clamp(Number(copied.height) || 0.08, 0.001, 1),
            colour: copied.colour || "#111827", fontFamily: copied.fontFamily || "Arial, sans-serif",
            size: Number(copied.size) || 24, fontScale: Number(copied.fontScale) || (Number(copied.size) || 24) / 900,
            bold: !!copied.bold, italic: !!copied.italic, underline: !!copied.underline,
            rotation: Number(copied.rotation) || 0
          };
          event.preventDefault();
          recordHistory({ type: "add", item });
          updateStrokes(current => [...current, item]);
          sendAddedItem(item);
          selectImage(item.id);
          setTool("select");
          return;
        } catch (error) {
          console.warn("Could not paste whiteboard textbox:", error);
        }
      }
      const items = Array.from(
        event.clipboardData?.items || []
      );
      const imageItem = items.find((item) =>
        item.type.startsWith("image/")
      );

      if (!imageItem) {
        return;
      }

      const file = imageItem.getAsFile();

      if (!file) {
        return;
      }

      event.preventDefault();
      setPasteMessage("Adding screenshot…");

      try {
        const pastedImage = await createPastedImage(file);

        recordHistory({
          type: "add",
          item: pastedImage
        });

        updateStrokes((current) => [
          ...current,
          pastedImage
        ]);

        sendImageInChunks(pastedImage);
        selectImage(pastedImage.id);
        setTool("select");

        setPasteMessage("Screenshot pasted");
        window.setTimeout(
          () => setPasteMessage(""),
          1800
        );
      } catch (error) {
        console.error(
          "Could not paste image:",
          error
        );
        setPasteMessage("Could not paste that image");
        window.setTimeout(
          () => setPasteMessage(""),
          2500
        );
      }
    };

    window.addEventListener("copy", handleCopy);
    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("copy", handleCopy);
      window.removeEventListener("paste", handlePaste);
    };
  }, [open, participantId, participantName, isTutor]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleWhiteboardKey = (event) => {
      const target = event.target;
      const tagName =
        target?.tagName?.toLowerCase?.() || "";

      if (
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        target?.isContentEditable
      ) {
        return;
      }

      if (calculatorStateRef.current.open) {
        return;
      }

      const shortcutKey =
        event.key.toLowerCase();
      const modifierPressed =
        event.ctrlKey ||
        event.metaKey;

      if (
        modifierPressed &&
        shortcutKey === "z"
      ) {
        event.preventDefault();

        if (event.shiftKey) {
          redoLastAction();
        } else {
          undoLastAction();
        }
        return;
      }

      if (
        modifierPressed &&
        shortcutKey === "y"
      ) {
        event.preventDefault();
        redoLastAction();
        return;
      }

      const toolShortcuts = {
        s: "select",
        p: "pen",
        e: "eraser",
        l: "shape-line",
        h: "highlighter",
        t: "text"
      };
      const shortcutTool = toolShortcuts[shortcutKey];

      if (
        shortcutTool &&
        !modifierPressed &&
        !event.altKey &&
        !event.repeat
      ) {
        event.preventDefault();
        if (shortcutTool !== "select") {
          selectImage(null);
        }
        setSidebarPanel(null);
        setTool(shortcutTool);
        return;
      }

      const arrowMovement = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1]
      }[event.key];

      if (arrowMovement && !modifierPressed && !event.altKey) {
        const itemId = selectedImageIdRef.current;
        const selectedItem = itemId
          ? strokesRef.current.find(
              (item) =>
                item.id === itemId &&
                isSelectableObject(item) &&
                (item.pageId || INITIAL_PAGE_ID) ===
                  activePageIdRef.current
            )
          : null;

        if (!selectedItem) {
          return;
        }

        event.preventDefault();

        // Keyboard movement uses canonical board pixels, so one key press
        // moves an object exactly the same distance on desktop and web.
        const now = performance.now();
        const heldArrow = arrowKeyHoldRef.current;
        if (heldArrow.key !== event.key || !event.repeat) {
          heldArrow.key = event.key;
          heldArrow.startedAt = now;
        }
        const heldFor = Math.max(0, now - heldArrow.startedAt);
        const step = event.shiftKey
          ? clamp(8 + Math.floor(heldFor / 150) * 2, 8, 20)
          : clamp(1 + Math.floor(heldFor / 180), 1, 10);
        const deltaX = (arrowMovement[0] * step) / LOGICAL_BOARD_WIDTH;
        const deltaY = (arrowMovement[1] * step) / getActiveLogicalHeight();
        const nextX = selectedItem.x + deltaX;
        const nextY = selectedItem.y + deltaY;
        const constrainedPosition =
          selectedItem.type === "compass"
            ? {
                x: clamp(nextX, 0.02, 0.98),
                y: clamp(nextY, 0.02, 0.98)
              }
            : selectedItem.type === "math"
              ? {
                  x: clamp(
                    nextX,
                    (selectedItem.width || 0) / 2,
                    1 - (selectedItem.width || 0) / 2
                  ),
                  y: clamp(
                    nextY,
                    (selectedItem.height || 0) / 2,
                    1 - (selectedItem.height || 0) / 2
                  )
                }
              : {
                  x: clamp(
                    nextX,
                    0,
                    Math.max(0, 1 - (selectedItem.width || 0))
                  ),
                  y: clamp(
                    nextY,
                    0,
                    Math.max(0, 1 - (selectedItem.height || 0))
                  )
                };
        const movedItem = {
          ...selectedItem,
          ...constrainedPosition
        };

        if (
          movedItem.x === selectedItem.x &&
          movedItem.y === selectedItem.y
        ) {
          return;
        }

        recordHistory({
          type: "update",
          before: selectedItem,
          after: movedItem
        });
        updateStrokes((current) =>
          current.map((item) =>
            item.id === itemId ? movedItem : item
          )
        );
        sendUpdatedItem(movedItem);
        return;
      }

      if (
        event.key !== "Delete" &&
        event.key !== "Backspace"
      ) {
        return;
      }

      const itemId =
        selectedImageIdRef.current;

      if (!itemId) {
        return;
      }

      const selectedItem =
        strokesRef.current.find(
          (item) =>
            item.id === itemId &&
            isSelectableObject(item) &&
            (item.pageId || INITIAL_PAGE_ID) ===
              activePageIdRef.current
        );

      if (!selectedItem) {
        return;
      }

      event.preventDefault();
      recordHistory({
        type: "remove",
        item: selectedItem
      });
      updateStrokes((current) =>
        current.filter(
          (item) => item.id !== itemId
        )
      );
      imageCacheRef.current.delete(itemId);
      selectImage(null);
      sendMessage?.({
        type: "whiteboard-remove",
        strokeId: itemId
      });
      setPasteMessage(
        selectedItem.type === "compass"
          ? "Compass deleted"
          : selectedItem.type === "math"
          ? "Instrument deleted"
          : selectedItem.type === "text"
            ? "Text deleted"
            : "Image deleted"
      );
      window.setTimeout(
        () => setPasteMessage(""),
        1400
      );
    };

    const releaseWhiteboardArrow = (event) => {
      if (
        !event ||
        arrowKeyHoldRef.current.key === event.key
      ) {
        arrowKeyHoldRef.current = {
          key: null,
          startedAt: 0
        };
      }
    };

    window.addEventListener("keydown", handleWhiteboardKey);
    window.addEventListener("keyup", releaseWhiteboardArrow);
    window.addEventListener("blur", releaseWhiteboardArrow);

    return () => {
      window.removeEventListener("keydown", handleWhiteboardKey);
      window.removeEventListener("keyup", releaseWhiteboardArrow);
      window.removeEventListener("blur", releaseWhiteboardArrow);
    };
  }, [open]);

  const processWhiteboardCommand = (commandEntry) => {
    if (!commandEntry?.nonce || !commandEntry.message) {
      return;
    }

    const { message, remoteParticipantId } = commandEntry;

    if (
      message.type === "whiteboard-recording-status" &&
      ["idle", "recording", "paused"].includes(message.status)
    ) {
      if (!isTutor) {
        showRemoteRecordingNotice(message.status);
      }
      return;
    }

    if (
      message.type === "whiteboard-calculator" &&
      message.state
    ) {
      applyCalculatorState(message.state, false);
      return;
    }

    if (
      message.type === "whiteboard-game-item" &&
      message.item?.id &&
      isGameItem(message.item)
    ) {
      updateStrokes((current) =>
        current.some((item) => item.id === message.item.id)
          ? current.map((item) =>
              item.id === message.item.id
                ? { ...item, ...message.item }
                : item
            )
          : [...current, message.item]
      );
      return;
    }

    if (message.type === "whiteboard-stroke" && message.stroke) {
      updateStrokes((current) => {
        if (current.some((stroke) => stroke.id === message.stroke.id)) {
          return current.map((stroke) =>
            stroke.id === message.stroke.id
              ? message.stroke
              : stroke
          );
        }

        return [...current, message.stroke];
      });
      return;
    }

    if (
      message.type === "whiteboard-live-stroke" &&
      message.stroke?.id
    ) {
      updateStrokes((current) => {
        const liveStroke = message.stroke;
        const existingIndex = current.findIndex(
          (stroke) => stroke.id === liveStroke.id
        );

        if (existingIndex === -1) {
          return [...current, liveStroke];
        }

        return current.map((stroke, index) =>
          index === existingIndex ? liveStroke : stroke
        );
      });
      return;
    }

    if (
      message.type === "whiteboard-item-update" &&
      message.item?.id
    ) {
      updateStrokes((current) =>
        current.map((item) =>
          item.id === message.item.id
            ? {
                ...item,
                ...message.item,
                dataUrl:
                  message.item.dataUrl ||
                  item.dataUrl
              }
            : item
        )
      );
      return;
    }

    if (
      message.type === "whiteboard-image-start" &&
      message.image?.id
    ) {
      imageTransfersRef.current.set(
        message.image.id,
        {
          image: message.image,
          totalChunks:
            message.totalChunks || 0,
          chunks: []
        }
      );
      return;
    }

    if (
      message.type === "whiteboard-image-chunk" &&
      message.imageId
    ) {
      const transfer =
        imageTransfersRef.current.get(
          message.imageId
        );

      if (transfer) {
        transfer.chunks[message.index] =
          message.data || "";
      }
      return;
    }

    if (
      message.type === "whiteboard-image-end" &&
      message.imageId
    ) {
      const transfer =
        imageTransfersRef.current.get(
          message.imageId
        );

      if (
        transfer &&
        transfer.chunks.filter(Boolean).length ===
          transfer.totalChunks
      ) {
        const completedImage = {
          ...transfer.image,
          dataUrl:
            transfer.chunks.join("")
        };

        imageTransfersRef.current.delete(
          message.imageId
        );
        updateStrokes((current) =>
          current.some(
            (item) =>
              item.id ===
              completedImage.id
          )
            ? current.map((item) =>
                item.id === completedImage.id
                  ? completedImage
                  : item
              )
            : [...current, completedImage]
        );
      }
      return;
    }

    if (message.type === "whiteboard-remove" && message.strokeId) {
      if (
        selectedImageIdRef.current ===
        message.strokeId
      ) {
        selectImage(null);
      }
      updateStrokes((current) =>
        current.filter((stroke) => stroke.id !== message.strokeId)
      );
      return;
    }

    if (message.type === "whiteboard-clear") {
      const pageId =
        message.pageId ||
        activePageIdRef.current;

      const selectedItem =
        strokesRef.current.find(
          (item) =>
            item.id === selectedImageIdRef.current
        );

      if (
        selectedItem &&
        (selectedItem.pageId || INITIAL_PAGE_ID) === pageId
      ) {
        selectImage(null);
      }

      updateStrokes((current) =>
        current.filter(
          (item) =>
            (item.pageId || INITIAL_PAGE_ID) !==
            pageId
        )
      );
      return;
    }

    if (
      message.type === "whiteboard-sync"
    ) {
      if (Array.isArray(message.strokes)) {
        const legacyItems = message.strokes.map(
          (item) => ({
            ...item,
            pageId:
              item.pageId ||
              INITIAL_PAGE_ID
          })
        );

        pagesRef.current = [
          {
            id: INITIAL_PAGE_ID,
            title: "Whiteboard 1",
            boardColour: "#ffffff",
            heightSections: 1
          }
        ];
        setPages(pagesRef.current);
        activePageIdRef.current = INITIAL_PAGE_ID;
        whiteboardViewRef.current = {
          pageId: INITIAL_PAGE_ID,
          scrollProgress: 0
        };
        setActivePageId(INITIAL_PAGE_ID);
        updateStrokes(legacyItems);
        return;
      }

      const snapshot = message.strokes;

      if (
        snapshot &&
        Array.isArray(snapshot.pages) &&
        Array.isArray(snapshot.items)
      ) {
        const nextPages =
          snapshot.pages.length > 0
            ? snapshot.pages
            : [
                {
                  id: INITIAL_PAGE_ID,
                  title: "Whiteboard 1",
                  boardColour: "#ffffff",
                  heightSections: 1
                }
              ];
        const requestedPageId =
          snapshot.activePageId;
        const nextActivePageId =
          nextPages.some(
            (page) => page.id === requestedPageId
          )
            ? requestedPageId
            : nextPages[0].id;

        pagesRef.current = nextPages;
        setPages(nextPages);
        activePageIdRef.current = nextActivePageId;
        setActivePageId(nextActivePageId);
        if (snapshot.calculator) {
          applyCalculatorState(snapshot.calculator, false);
        }
        if (
          !isTutor &&
          ["idle", "recording", "paused"].includes(
            snapshot.recordingStatus
          )
        ) {
          showRemoteRecordingNotice(snapshot.recordingStatus);
        }
        updateStrokes(snapshot.items);
        if (
          snapshot.view?.pageId === nextActivePageId &&
          Number.isFinite(snapshot.view?.scrollProgress)
        ) {
          applyScrollProgress(
            snapshot.view.scrollProgress,
            nextActivePageId
          );
        } else {
          applyScrollProgress(0, nextActivePageId);
        }
      }

      return;
    }

    if (
      message.type === "whiteboard-page-add" &&
      message.page?.id
    ) {
      updatePages((current) =>
        current.some(
          (page) => page.id === message.page.id
        )
          ? current
          : [...current, message.page]
      );

      if (message.activePageId) {
        window.setTimeout(
          () => selectPageLocally(message.activePageId),
          0
        );
      }
      return;
    }

    if (
      message.type === "whiteboard-page-delete" &&
      message.pageId
    ) {
      const nextPages = pagesRef.current.filter(
        (page) => page.id !== message.pageId
      );

      if (nextPages.length === 0) {
        return;
      }

      const nextActivePageId = nextPages.some(
        (page) => page.id === message.activePageId
      )
        ? message.activePageId
        : nextPages[0].id;
      const nextItems = strokesRef.current.filter(
        (item) =>
          (item.pageId || INITIAL_PAGE_ID) !== message.pageId
      );

      pagesRef.current = nextPages;
      setPages(nextPages);
      activePageIdRef.current = nextActivePageId;
      whiteboardViewRef.current = {
        pageId: nextActivePageId,
        scrollProgress: 0
      };
      setActivePageId(nextActivePageId);
      selectImage(null);
      setRemoteCursors({});
      updateStrokes(nextItems);
      return;
    }

    if (
      message.type === "whiteboard-page-select" &&
      message.pageId
    ) {
      selectPageLocally(message.pageId);
      applyScrollProgress(
        Number.isFinite(message.scrollProgress)
          ? message.scrollProgress
          : 0,
        message.pageId
      );
      return;
    }

    if (
      message.type === "whiteboard-scroll" &&
      message.pageId === activePageIdRef.current &&
      Number.isFinite(message.scrollProgress)
    ) {
      applyScrollProgress(message.scrollProgress, message.pageId);
      return;
    }

    if (
      message.type === "whiteboard-page-extend" &&
      message.pageId &&
      Number.isFinite(message.heightSections)
    ) {
      extendWhiteboardLocally(
        message.pageId,
        Math.max(1, Math.floor(message.heightSections))
      );
      return;
    }

    if (
      message.type === "whiteboard-background" &&
      message.pageId &&
      (message.background || message.boardColour)
    ) {
      updatePages((current) =>
        current.map((page) =>
          page.id === message.pageId
            ? {
                ...page,
                ...(message.background
                  ? { background: message.background }
                  : {}),
                ...(message.boardColour
                  ? { boardColour: message.boardColour }
                  : {})
              }
            : page
        )
      );
      window.setTimeout(redraw, 0);
      return;
    }

    if (message.type === "whiteboard-cursor" && remoteParticipantId) {
      setRemoteCursors((current) => ({
        ...current,
        [remoteParticipantId]: {
          x: clamp(message.x, 0, 1),
          y: clamp(message.y, 0, 1),
          name: message.name || "Participant",
          colour: message.colour || "#2563eb",
          pageId:
            message.pageId ||
            activePageIdRef.current
        }
      }));

      const existingTimeout =
        cursorTimeoutsRef.current.get(remoteParticipantId);

      if (existingTimeout) {
        window.clearTimeout(existingTimeout);
      }

      cursorTimeoutsRef.current.set(
        remoteParticipantId,
        window.setTimeout(() => {
          setRemoteCursors((current) => {
            const next = { ...current };
            delete next[remoteParticipantId];
            return next;
          });
        }, 1800)
      );
    }
  };

  useEffect(() => {
    const commands = Array.isArray(command)
      ? command
      : [command];

    commands.forEach((entry) => {
      if (
        !entry?.nonce ||
        processedCommandIdsRef.current.has(
          entry.nonce
        )
      ) {
        return;
      }

      processedCommandIdsRef.current.add(
        entry.nonce
      );
      processWhiteboardCommand(entry);
    });

    if (
      processedCommandIdsRef.current.size >
      2500
    ) {
      const recentIds = commands
        .slice(-1000)
        .map((entry) => entry?.nonce)
        .filter(Boolean);

      processedCommandIdsRef.current =
        new Set(recentIds);
    }
  }, [command]);

  useEffect(
    () => () => {
      cursorTimeoutsRef.current.forEach((timeout) =>
        window.clearTimeout(timeout)
      );
      cursorTimeoutsRef.current.clear();
    },
    []
  );

  const getPoint = (event) => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return null;
    }

    const bounds = canvas.getBoundingClientRect();

    if (!bounds.width || !bounds.height) {
      return null;
    }

    const point = {
      x: clamp((event.clientX - bounds.left) / bounds.width, 0, 1),
      y: clamp((event.clientY - bounds.top) / bounds.height, 0, 1)
    };
    const now = performance.now();
    const previousCursor = localRecordingCursorRef.current;
    const shouldSamplePosition =
      !previousCursor ||
      event.type === "pointerdown" ||
      now - (previousCursor.lastSampledAt || 0) >= 66;
    localRecordingCursorRef.current = {
      ...(previousCursor || point),
      ...(shouldSamplePosition ? point : {}),
      colour,
      pointerDown: previousCursor?.pointerDown || event.type === "pointerdown",
      visible: true,
      lastMovedAt: now,
      lastSampledAt: shouldSamplePosition
        ? now
        : previousCursor?.lastSampledAt || now
    };
    return point;
  };

  const updatePencilCursorOverlay = (event, visible = true) => {
    const overlay = pencilCursorOverlayRef.current;
    const canvas = canvasRef.current;
    if (!overlay || !canvas || tool !== "pen") return;

    if (!visible) {
      overlay.style.display = "none";
      return;
    }

    const bounds = canvas.getBoundingClientRect();
    if (
      event.clientX < bounds.left ||
      event.clientX > bounds.right ||
      event.clientY < bounds.top ||
      event.clientY > bounds.bottom
    ) {
      return;
    }
    overlay.style.display = "block";
    overlay.style.transform = `translate(${event.clientX - bounds.left - 3}px, ${
      event.clientY - bounds.top - 29
    }px)`;
  };

  const sendCursor = (point) => {
    const now = performance.now();

    if (now - lastCursorSentAtRef.current < 50) {
      return;
    }

    lastCursorSentAtRef.current = now;
    sendMessage?.({
      type: "whiteboard-cursor",
      x: point.x,
      y: point.y,
      name: safeName,
      colour,
      pageId:
        activePageIdRef.current
    });
  };

  const sendLiveStroke = (stroke, force = false) => {
    if (!stroke) {
      return;
    }

    const now = performance.now();
    if (!force && now - lastLiveStrokeSentAtRef.current < 40) {
      return;
    }

    lastLiveStrokeSentAtRef.current = now;
    sendMessage?.({
      type: "whiteboard-live-stroke",
      stroke: {
        ...stroke,
        points: stroke.points
          ? stroke.points.map((point) => ({ ...point }))
          : undefined,
        segments: stroke.segments
          ? stroke.segments.map((segment) => ({ ...segment }))
          : undefined
      }
    });
  };

  const getMathLocalPoint = (point, item) => {
    const bounds = canvasRef.current?.getBoundingClientRect();
    const boardWidth = Math.max(1, bounds?.width || 1);
    const boardHeight = Math.max(1, bounds?.height || 1);
    const itemWidth = (item.width || 0.2) * boardWidth;
    const itemHeight = (item.height || 0.08) * boardHeight;
    const centreX =
      item.type === "math"
        ? item.x * boardWidth
        : item.x * boardWidth + itemWidth / 2;
    const centreY =
      item.type === "math"
        ? item.y * boardHeight
        : item.y * boardHeight + itemHeight / 2;
    const dx = point.x * boardWidth - centreX;
    const dy = point.y * boardHeight - centreY;
    const rotation = -(item.rotation || 0);

    return {
      x: dx * Math.cos(rotation) - dy * Math.sin(rotation),
      y: dx * Math.sin(rotation) + dy * Math.cos(rotation),
      width: itemWidth,
      height: itemHeight
    };
  };

  const getCompassGeometry = (item) => {
    const bounds = canvasRef.current?.getBoundingClientRect();
    const boardWidth = Math.max(1, bounds?.width || 1);
    const boardHeight = Math.max(1, bounds?.height || 1);
    const pivot = {
      x: item.x * boardWidth,
      y: item.y * boardHeight
    };
    const radius = (item.radius || 0.14) * boardWidth;
    const angle = item.angle || 0;
    const pencil = {
      x: pivot.x + radius * Math.cos(angle),
      y: pivot.y + radius * Math.sin(angle)
    };
    const normal = {
      x: -Math.sin(angle),
      y: Math.cos(angle)
    };
    const logicalScale = boardWidth / LOGICAL_BOARD_WIDTH;
    const hingeLift = Math.min(105 * logicalScale, radius * 0.52);
    const hinge = {
      x: (pivot.x + pencil.x) / 2 - normal.x * hingeLift,
      y: (pivot.y + pencil.y) / 2 - normal.y * hingeLift
    };
    const rotationHandle = {
      x: hinge.x - normal.x * 42 * logicalScale,
      y: hinge.y - normal.y * 42 * logicalScale
    };

    return {
      boardWidth,
      boardHeight,
      pivot,
      pencil,
      hinge,
      rotationHandle,
      radius,
      midpoint: {
        x: (pivot.x + pencil.x) / 2,
        y: (pivot.y + pencil.y) / 2
      }
    };
  };

  const getCompassHandle = (point, item) => {
    const geometry = getCompassGeometry(item);
    const pointer = {
      x: point.x * geometry.boardWidth,
      y: point.y * geometry.boardHeight
    };
    const distanceToSegment = (start, end) => {
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const lengthSquared = dx * dx + dy * dy || 1;
      const amount = clamp(
        ((pointer.x - start.x) * dx + (pointer.y - start.y) * dy) /
          lengthSquared,
        0,
        1
      );
      return Math.hypot(
        pointer.x - (start.x + amount * dx),
        pointer.y - (start.y + amount * dy)
      );
    };

    if (Math.hypot(pointer.x - geometry.pencil.x, pointer.y - geometry.pencil.y) <= 24) {
      return "compass-draw";
    }
    if (
      Math.hypot(
        pointer.x - geometry.rotationHandle.x,
        pointer.y - geometry.rotationHandle.y
      ) <= 25
    ) {
      return "compass-rotate";
    }
    if (Math.hypot(pointer.x - geometry.hinge.x, pointer.y - geometry.hinge.y) <= 25) {
      return "compass-move";
    }
    if (Math.hypot(pointer.x - geometry.midpoint.x, pointer.y - geometry.midpoint.y) <= 22) {
      return "compass-radius";
    }
    if (
      Math.hypot(pointer.x - geometry.pivot.x, pointer.y - geometry.pivot.y) <= 25 ||
      distanceToSegment(geometry.pivot, geometry.pencil) <= 20
    ) {
      return "compass-move";
    }
    return null;
  };

  const getSelectableItemAtPoint = (point) =>
    [...strokesRef.current]
      .reverse()
      .find((item) => {
        if (
          (item.pageId || INITIAL_PAGE_ID) !==
          activePageIdRef.current
        ) {
          return false;
        }

        if (item.type === "image") {
          const itemWidth = item.width || 0.2;
          const itemHeight = item.height || 0.08;
          return (
            point.x >= item.x &&
            point.x <= item.x + itemWidth &&
            point.y >= item.y &&
            point.y <= item.y + itemHeight
          );
        }

        if (isGameItem(item)) {
          return (
            point.x >= item.x &&
            point.x <= item.x + item.width &&
            point.y >= item.y &&
            point.y <= item.y + item.height
          );
        }

        if (item.type === "math" || item.type === "text") {
          const local = getMathLocalPoint(point, item);
          return (
            Math.abs(local.x) <= local.width / 2 &&
            Math.abs(local.y) <= local.height / 2
          );
        }

        if (item.type === "compass") {
          return !!getCompassHandle(point, item);
        }

        return false;
      }) || null;

  const isResizeHandle = (point, image) => {
    const canvas = canvasRef.current;
    const bounds = canvas?.getBoundingClientRect();
    const toleranceX = 26 / Math.max(1, bounds?.width || 1);
    const toleranceY = 26 / Math.max(1, bounds?.height || 1);

    return (
      Math.abs(point.x - (image.x + (image.width || 0.2))) <= toleranceX &&
      Math.abs(point.y - (image.y + (image.height || 0.08))) <= toleranceY
    );
  };

  const getMathHandle = (point, item) => {
    const local = getMathLocalPoint(point, item);
    const resizeDistance = Math.hypot(
      local.x - local.width / 2,
      local.y - local.height / 2
    );
    const rotateDistance = Math.hypot(
      local.x,
      local.y + local.height / 2 + 32
    );

    if (rotateDistance <= 16) return "rotate";
    if (
      item.mathTool !== "ruler" &&
      resizeDistance <= 18
    ) return "resize";
    return null;
  };

  const getItemHandle = (point, item) =>
    item?.type === "compass"
      ? getCompassHandle(point, item)
      : item?.type === "game-counter"
        ? (isResizeHandle(point, item) ? "resize" : null)
      : isGameItem(item)
        ? null
      : item?.type === "math" || item?.type === "text"
      ? getMathHandle(point, item)
      : isResizeHandle(point, item)
        ? "resize"
        : null;

  const finishTextEditing = (cancel = false) => {
    const textId = editingTextIdRef.current;

    if (!textId) {
      return;
    }

    const storedItem = strokesRef.current.find(
      (entry) => entry.id === textId
    );
    const pendingFormat = editingTextFormatRef.current;
    const item = storedItem && pendingFormat && !cancel
      ? { ...storedItem, ...pendingFormat }
      : storedItem;
    const original = editingTextOriginalRef.current;

    if (!cancel && item && storedItem !== item) {
      strokesRef.current = strokesRef.current.map((entry) =>
        entry.id === textId ? item : entry
      );
      setStrokes([...strokesRef.current]);
      publishSnapshot(strokesRef.current);
    }

    editingTextIdRef.current = null;
    editingTextOriginalRef.current = null;
    editingTextFormatRef.current = null;
    setEditingTextId(null);

    if (cancel && original) {
      updateStrokes((current) =>
        current.map((entry) =>
          entry.id === textId ? original : entry
        )
      );
      sendMessage?.({
        type: "whiteboard-item-update",
        item: original
      });
    } else if (cancel || !item?.text?.trim()) {
      updateStrokes((current) =>
        current.filter((entry) => entry.id !== textId)
      );
      sendMessage?.({
        type: "whiteboard-remove",
        strokeId: textId
      });
      if (original) {
        recordHistory({ type: "remove", item: original });
      }
    } else if (original) {
      recordHistory({
        type: "update",
        before: original,
        after: item
      });
      sendMessage?.({
        type: "whiteboard-item-update",
        item
      });
    } else {
      recordHistory({ type: "add", item });
    }

    window.setTimeout(redraw, 0);
  };

  const editTextAtPoint = (event) => {
    if (tool !== "select") {
      return;
    }

    const point = getPoint(event);
    const item = point
      ? getSelectableItemAtPoint(point)
      : null;

    if (item?.type !== "text") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    finishTextEditing();
    imageInteractionRef.current = null;
    selectImage(null);
    editingTextOriginalRef.current = { ...item };
    editingTextFormatRef.current = {
      bold: !!item.bold,
      italic: !!item.italic,
      underline: !!item.underline,
      fontFamily: item.fontFamily || "Arial, sans-serif",
      colour: item.colour || "#111827",
      size: item.size,
      fontScale: item.fontScale
    };
    editingTextIdRef.current = item.id;
    setEditingTextId(item.id);
    window.setTimeout(redraw, 0);
  };

  const startDrawing = (event) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }

    const point = getPoint(event);

    if (!point) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);

    // A click anywhere else on the board completes the active text edit.
    // Do not let the same click immediately create or select another item.
    if (editingTextIdRef.current) {
      finishTextEditing();
      imageInteractionRef.current = null;
      selectImage(null);
      return;
    }

    if (tool === "compass") {
      const compass = [...strokesRef.current]
        .reverse()
        .find(
          (item) =>
            item.type === "compass" &&
            (item.pageId || INITIAL_PAGE_ID) === activePageIdRef.current &&
            getCompassHandle(point, item)
        );

      if (!compass) {
        selectImage(null);
        redraw();
        return;
      }

      const mode = getCompassHandle(point, compass);
      const geometry = getCompassGeometry(compass);
      selectImage(compass.id);
      imageInteractionRef.current = {
        mode,
        startPoint: point,
        original: { ...compass },
        lastAngle: Math.atan2(
          point.y * geometry.boardHeight - geometry.pivot.y,
          point.x * geometry.boardWidth - geometry.pivot.x
        ),
        totalSweep: 0,
        totalDistance: 0
      };

      if (mode === "compass-draw") {
        drawingRef.current = true;
        activeStrokeRef.current = {
          id: createId(),
          type: "compass-arc",
          pageId: activePageIdRef.current,
          ownerId: participantId || "unknown",
          ownerName: safeName,
          colour,
          size,
          x: compass.x,
          y: compass.y,
          radius: compass.radius,
          startAngle: compass.angle || 0,
          sweep: 0,
          drawnLength: 0,
          segments: []
        };
        sendAddedItem(activeStrokeRef.current);
      }
      redraw();
      return;
    }

    if (tool === "text") {
      finishTextEditing();
      const boardBounds =
        canvasRef.current?.getBoundingClientRect();
      const textSize = textDefaultsRef.current.fontScale * Math.max(1, boardBounds?.height || 900);
      const textMeasurementContext =
        document.createElement("canvas").getContext("2d");
      textMeasurementContext.font =
        `${textSize}px ${textDefaultsRef.current.fontFamily}`;
      const oneCharacterWidth =
        textMeasurementContext.measureText("M").width /
        Math.max(1, boardBounds?.width || 1);
      const textItem = {
        id: createId(),
        type: "text",
        pageId: activePageIdRef.current,
        ownerId: participantId || "unknown",
        ownerName: safeName,
        text: "",
        x: point.x,
        y: point.y,
        colour,
        size: textDefaultsRef.current.size,
        fontFamily: textDefaultsRef.current.fontFamily,
        bold: false,
        italic: false,
        underline: false,
        fontScale:
          textSize /
          Math.max(1, boardBounds?.height || 1),
        rotation: 0,
        width: Math.min(
          oneCharacterWidth,
          Math.max(0.001, 1 - point.x)
        ),
        height: Math.min(
          0.12,
          (textSize * 1.25) /
            Math.max(1, boardBounds?.height || 1)
        )
      };
      updateStrokes((current) => [...current, textItem]);
      sendAddedItem(textItem);
      editingTextIdRef.current = textItem.id;
      editingTextOriginalRef.current = null;
      editingTextFormatRef.current = {
        bold: false,
        italic: false,
        underline: false,
        fontFamily: textItem.fontFamily,
        colour: textItem.colour,
        size: textItem.size,
        fontScale: textItem.fontScale
      };
      setEditingTextId(textItem.id);
      return;
    }

    if (tool === "select") {
      // The resize handle is centred on the image corner, so part of its
      // clickable area lies outside the image. Check the selected handle
      // before checking whether the pointer is inside an image.
      const selectedItem =
        strokesRef.current.find(
          (item) =>
            item.id === selectedImageIdRef.current &&
            isSelectableObject(item) &&
            (item.pageId || INITIAL_PAGE_ID) ===
              activePageIdRef.current
        );
      const selectedHandle = selectedItem
        ? getItemHandle(point, selectedItem)
        : null;

      if (
        selectedItem &&
        selectedHandle
      ) {
        if (selectedItem.type === "compass") {
          const geometry = getCompassGeometry(selectedItem);
          imageInteractionRef.current = {
            mode: selectedHandle,
            startPoint: point,
            original: { ...selectedItem },
            lastAngle: Math.atan2(
              point.y * geometry.boardHeight - geometry.pivot.y,
              point.x * geometry.boardWidth - geometry.pivot.x
            ),
            totalSweep: 0,
            totalDistance: 0
          };
          if (selectedHandle === "compass-draw") {
            drawingRef.current = true;
            activeStrokeRef.current = {
              id: createId(),
              type: "compass-arc",
              pageId: activePageIdRef.current,
              ownerId: participantId || "unknown",
              ownerName: safeName,
              colour,
              size,
              x: selectedItem.x,
              y: selectedItem.y,
              radius: selectedItem.radius,
              startAngle: selectedItem.angle || 0,
              sweep: 0,
              drawnLength: 0,
              segments: []
            };
            sendAddedItem(activeStrokeRef.current);
          }
          redraw();
          return;
        }
        imageInteractionRef.current = {
          mode: selectedHandle,
          startPoint: point,
          original: {
            ...selectedItem,
            width:
              selectedItem.width ||
              (selectedItem.type === "text" ? 0.2 : selectedItem.width),
            height:
              selectedItem.height ||
              (selectedItem.type === "text" ? 0.08 : selectedItem.height)
          }
        };
        redraw();
        return;
      }

      const item = getSelectableItemAtPoint(point);

      if (item) {
        if (
          ["game-dice", "game-coin", "game-spinner"].includes(item.type)
        ) {
          selectImage(item.id);
          imageInteractionRef.current = {
            mode: "game-activate",
            startPoint: point,
            original: { ...item }
          };
          redraw();
          return;
        }

        if (item.type === "compass") {
          const compassMode =
            getCompassHandle(point, item) || "compass-move";
          const geometry = getCompassGeometry(item);
          selectImage(item.id);
          imageInteractionRef.current = {
            mode: compassMode,
            startPoint: point,
            original: { ...item },
            lastAngle: Math.atan2(
              point.y * geometry.boardHeight - geometry.pivot.y,
              point.x * geometry.boardWidth - geometry.pivot.x
            ),
            totalSweep: 0,
            totalDistance: 0
          };

          if (compassMode === "compass-draw") {
            drawingRef.current = true;
            activeStrokeRef.current = {
              id: createId(),
              type: "compass-arc",
              pageId: activePageIdRef.current,
              ownerId: participantId || "unknown",
              ownerName: safeName,
              colour,
              size,
              x: item.x,
              y: item.y,
              radius: item.radius,
              startAngle: item.angle || 0,
              sweep: 0,
              drawnLength: 0,
              segments: []
            };
            sendAddedItem(activeStrokeRef.current);
          }
          redraw();
          return;
        }
        selectImage(item.id);
        imageInteractionRef.current = {
          mode: getItemHandle(point, item) || "move",
          startPoint: point,
          original: {
            ...item,
            width:
              item.width ||
              (item.type === "text" ? 0.2 : item.width),
            height:
              item.height ||
              (item.type === "text" ? 0.08 : item.height)
          }
        };
        redraw();
        return;
      }

      selectImage(null);
      redraw();
      return;
    }

    selectImage(null);
    drawingRef.current = true;
    activeStrokeRef.current = tool.startsWith("shape-")
      ? {
          id: createId(),
          type: "shape",
          shape: tool.replace("shape-", ""),
          pageId: activePageIdRef.current,
          ownerId: participantId || "unknown",
          ownerName: safeName,
          colour,
          size,
          start: point,
          end: point
        }
      : {
          id: createId(),
          pageId: activePageIdRef.current,
          ownerId: participantId || "unknown",
          ownerName: safeName,
          tool,
          colour,
          size:
            tool === "eraser"
              ? Math.max(12, size * 3)
              : tool === "highlighter"
                ? Math.max(16, size * 3)
                : size,
          points: [point]
        };
    sendLiveStroke(activeStrokeRef.current, true);
    redraw();
  };

  const continueDrawing = (event) => {
    const point = getPoint(event);

    if (!point) {
      return;
    }

    sendCursor(point);

    if (
      tool === "select" &&
      !imageInteractionRef.current &&
      !drawingRef.current
    ) {
      const selectedItem =
        strokesRef.current.find(
          (item) =>
            item.id === selectedImageIdRef.current &&
            isSelectableObject(item)
        );

      setHoveringResizeHandle(
        !!selectedItem &&
        !!getItemHandle(point, selectedItem)
      );
    }

    if (
      imageInteractionRef.current?.original?.type === "compass"
    ) {
      const interaction = imageInteractionRef.current;
      const original = interaction.original;
      const geometry = getCompassGeometry(original);
      const pointerX = point.x * geometry.boardWidth;
      const pointerY = point.y * geometry.boardHeight;
      let updates = {};

      if (interaction.mode === "compass-move") {
        updates = {
          x: clamp(original.x + point.x - interaction.startPoint.x, 0.02, 0.98),
          y: clamp(original.y + point.y - interaction.startPoint.y, 0.02, 0.98)
        };
      } else {
        const currentCompass =
          strokesRef.current.find((item) => item.id === original.id) || original;
        const currentGeometry = getCompassGeometry(currentCompass);
        const angle = Math.atan2(
          pointerY - currentGeometry.pivot.y,
          pointerX - currentGeometry.pivot.x
        );

        if (interaction.mode === "compass-rotate") {
          updates = {
            angle:
              angle +
              (original.angle || 0) -
              interaction.lastAngle
          };
        } else if (interaction.mode === "compass-radius") {
          updates = {
            radius: clamp(
              Math.hypot(
                pointerX - currentGeometry.pivot.x,
                pointerY - currentGeometry.pivot.y
              ) / currentGeometry.boardWidth,
              0.035,
              0.44
            ),
            angle
          };
        } else if (interaction.mode === "compass-draw") {
          const segmentStartAngle = interaction.lastAngle;
          let angleDelta = angle - interaction.lastAngle;
          if (angleDelta > Math.PI) angleDelta -= Math.PI * 2;
          if (angleDelta < -Math.PI) angleDelta += Math.PI * 2;
          interaction.totalSweep += angleDelta;
          interaction.totalDistance += Math.abs(angleDelta);
          interaction.lastAngle = angle;
          updates = { angle };
          if (activeStrokeRef.current) {
            activeStrokeRef.current.sweep = interaction.totalSweep;
            activeStrokeRef.current.drawnLength = interaction.totalDistance;
            if (Math.abs(angleDelta) >= 0.0005) {
              const segments = activeStrokeRef.current.segments;
              const previousSegment = segments[segments.length - 1];
              if (
                previousSegment &&
                Math.sign(previousSegment.sweep) === Math.sign(angleDelta)
              ) {
                previousSegment.sweep += angleDelta;
              } else {
                segments.push({
                  startAngle: segmentStartAngle,
                  sweep: angleDelta
                });
              }
            }
          }
        }
      }

      strokesRef.current = strokesRef.current.map((item) =>
        item.id === original.id ? { ...item, ...updates } : item
      );

      const now = performance.now();
      if (now - lastImageUpdateSentAtRef.current >= 60) {
        const updated = strokesRef.current.find((item) => item.id === original.id);
        if (updated) sendUpdatedItem(updated);
        if (
          interaction.mode === "compass-draw" &&
          activeStrokeRef.current
        ) {
          sendUpdatedItem(activeStrokeRef.current);
        }
        lastImageUpdateSentAtRef.current = now;
      }
      redraw();
      return;
    }

    if (imageInteractionRef.current) {
      const interaction =
        imageInteractionRef.current;
      const deltaX =
        point.x - interaction.startPoint.x;
      const deltaY =
        point.y - interaction.startPoint.y;
      const original = interaction.original;
      let updates = {};

      if (interaction.mode === "game-activate") {
        if (Math.hypot(deltaX, deltaY) < 0.004) {
          return;
        }
        interaction.mode = "move";
      }

      if (interaction.mode === "move") {
        updates = original.type === "math"
          ? {
              x: clamp(
                original.x + deltaX,
                original.width / 2,
                1 - original.width / 2
              ),
              y: clamp(
                original.y + deltaY,
                original.height / 2,
                1 - original.height / 2
              )
            }
          : {
              x: clamp(
                original.x + deltaX,
                0,
                1 - original.width
              ),
              y: clamp(
                original.y + deltaY,
                0,
                1 - original.height
              )
            };
      } else if (
        interaction.mode === "rotate" &&
        (original.type === "math" || original.type === "text")
      ) {
        const bounds = canvasRef.current?.getBoundingClientRect();
        const pointerX = point.x * Math.max(1, bounds?.width || 1);
        const pointerY = point.y * Math.max(1, bounds?.height || 1);
        const boardWidth = Math.max(1, bounds?.width || 1);
        const boardHeight = Math.max(1, bounds?.height || 1);
        const centreX =
          original.type === "math"
            ? original.x * boardWidth
            : (original.x + original.width / 2) * boardWidth;
        const centreY =
          original.type === "math"
            ? original.y * boardHeight
            : (original.y + original.height / 2) * boardHeight;
        updates = {
          rotation:
            Math.atan2(pointerY - centreY, pointerX - centreX) +
            Math.PI / 2
        };
      } else if (
        original.type === "math" ||
        original.type === "text"
      ) {
        const local = getMathLocalPoint(point, original);
        const board = canvasRef.current?.getBoundingClientRect();
        const ratio = original.height / Math.max(0.001, original.width);
        const centreX =
          original.type === "math"
            ? original.x
            : original.x + original.width / 2;
        const centreY =
          original.type === "math"
            ? original.y
            : original.y + original.height / 2;
        const nextWidth = clamp(
          (Math.abs(local.x) * 2) / Math.max(1, board?.width || 1),
          original.type === "text" ? 0.04 : 0.14,
          Math.min(0.92, 2 * Math.min(centreX, 1 - centreX))
        );
        const nextHeight = clamp(
          nextWidth * ratio,
          original.type === "text" ? 0.025 : 0.08,
          Math.min(0.9, 2 * Math.min(centreY, 1 - centreY))
        );
        updates = {
          width: nextWidth,
          height: nextHeight,
          ...(original.type === "text"
            ? {
                x: centreX - nextWidth / 2,
                y: centreY - nextHeight / 2,
                size: Math.max(
                  8,
                  original.size *
                    (nextWidth /
                      Math.max(0.001, original.width))
                ),
                fontScale:
                  (original.fontScale || original.size / 900) *
                  (nextWidth /
                    Math.max(0.001, original.width))
              }
            : {})
        };
      } else {
        const ratio =
          original.height /
          Math.max(0.001, original.width);
        let nextWidth = clamp(
          original.width + deltaX,
          original.type === "game-counter" ? 0.015 : 0.06,
          1 - original.x
        );
        let nextHeight = nextWidth * ratio;

        if (nextHeight > 1 - original.y) {
          nextHeight = 1 - original.y;
          nextWidth = nextHeight / ratio;
        }

        updates = {
          width: nextWidth,
          height: nextHeight
        };
      }

      strokesRef.current =
        strokesRef.current.map((item) =>
          item.id === original.id
            ? {
                ...item,
                ...updates
              }
            : item
        );

      const now = performance.now();

      if (
        now - lastImageUpdateSentAtRef.current >= 60
      ) {
        const updatedImage =
          strokesRef.current.find(
            (item) => item.id === original.id
          );

        if (updatedImage) {
          sendUpdatedItem(updatedImage);
        }

        lastImageUpdateSentAtRef.current = now;
      }
      redraw();
      return;
    }

    if (!drawingRef.current || !activeStrokeRef.current) {
      return;
    }

    if (activeStrokeRef.current.type === "shape") {
      activeStrokeRef.current.end = point;
      sendLiveStroke(activeStrokeRef.current);
      redraw();
      return;
    }

    const points = activeStrokeRef.current.points;
    const previous = points[points.length - 1];
    const distance = Math.hypot(point.x - previous.x, point.y - previous.y);

    if (distance < 0.001) {
      return;
    }

    points.push(point);
    sendLiveStroke(activeStrokeRef.current);
    redraw();
  };

  const finishDrawing = (event) => {
    if (
      imageInteractionRef.current?.original?.type === "compass"
    ) {
      event?.preventDefault?.();
      const interaction = imageInteractionRef.current;
      const original = interaction.original;
      const updated = strokesRef.current.find((item) => item.id === original.id);
      const arc = activeStrokeRef.current;

      imageInteractionRef.current = null;
      activeStrokeRef.current = null;
      drawingRef.current = false;

      if (updated) {
        const changed =
          original.x !== updated.x ||
          original.y !== updated.y ||
          original.radius !== updated.radius ||
          original.angle !== updated.angle;
        if (changed) {
          recordHistory({ type: "update", before: original, after: updated });
          sendUpdatedItem(updated);
        }
      }

      if (arc && (arc.drawnLength || Math.abs(arc.sweep)) >= 0.015) {
        const completedArc = { ...arc };
        strokesRef.current = [...strokesRef.current, completedArc];
        recordHistory({ type: "add", item: completedArc });
        sendAddedItem(completedArc);
        sendUpdatedItem(completedArc);
      } else if (arc) {
        sendMessage?.({
          type: "whiteboard-remove",
          strokeId: arc.id
        });
      }

      setStrokes([...strokesRef.current]);
      publishSnapshot();
      redraw();
      return;
    }

    if (imageInteractionRef.current) {
      event?.preventDefault?.();
      const completedInteraction = imageInteractionRef.current;
      const originalImage =
        completedInteraction.original;
      const imageId =
        originalImage.id;
      const updatedImage =
        strokesRef.current.find(
          (item) => item.id === imageId
        );

      imageInteractionRef.current = null;

      if (completedInteraction.mode === "game-activate") {
        const gameItem =
          strokesRef.current.find((item) => item.id === imageId) || originalImage;
        animateGameItem(gameItem);
        return;
      }

      setStrokes([...strokesRef.current]);
      publishSnapshot();

      if (updatedImage) {
        const changed =
          originalImage.x !== updatedImage.x ||
          originalImage.y !== updatedImage.y ||
          originalImage.width !== updatedImage.width ||
          originalImage.height !== updatedImage.height ||
          originalImage.size !== updatedImage.size ||
          originalImage.fontScale !== updatedImage.fontScale ||
          originalImage.rotation !== updatedImage.rotation;

        if (changed) {
          recordHistory({
            type: "update",
            before: originalImage,
            after: updatedImage
          });
        }

        sendUpdatedItem(updatedImage);
      }
      return;
    }

    if (!drawingRef.current || !activeStrokeRef.current) {
      return;
    }

    event?.preventDefault?.();
    const completedStroke = activeStrokeRef.current;
    drawingRef.current = false;
    activeStrokeRef.current = null;

    recordHistory({
      type: "add",
      item: completedStroke
    });
    updateStrokes((current) =>
      current.some((item) => item.id === completedStroke.id)
        ? current.map((item) =>
            item.id === completedStroke.id ? completedStroke : item
          )
        : [...current, completedStroke]
    );
    sendMessage?.({
      type: "whiteboard-stroke",
      stroke: completedStroke
    });
  };

  const sendAddedItem = (item) => {
    if (item.type === "image") {
      sendImageInChunks(item);
    } else if (isGameItem(item)) {
      sendMessage?.({
        type: "whiteboard-game-item",
        item
      });
    } else {
      sendMessage?.({
        type: "whiteboard-stroke",
        stroke: item
      });
    }
  };

  const sendUpdatedItem = (item) => {
    const lightweightItem = {
      ...item
    };
    delete lightweightItem.dataUrl;

    sendMessage?.({
      type: isGameItem(item)
        ? "whiteboard-game-item"
        : "whiteboard-item-update",
      item: lightweightItem
    });
  };

  const applyHistoryAction = (
    action,
    direction
  ) => {
    if (!action) {
      return;
    }

    const undoing = direction === "undo";

    if (action.type === "add") {
      if (undoing) {
        updateStrokes((current) =>
          current.filter(
            (item) => item.id !== action.item.id
          )
        );
        sendMessage?.({
          type: "whiteboard-remove",
          strokeId: action.item.id
        });
      } else {
        updateStrokes((current) =>
          current.some(
            (item) => item.id === action.item.id
          )
            ? current
            : [...current, action.item]
        );
        sendAddedItem(action.item);
      }
      selectImage(null);
      return;
    }

    if (action.type === "remove") {
      if (undoing) {
        updateStrokes((current) =>
          current.some(
            (item) => item.id === action.item.id
          )
            ? current
            : [...current, action.item]
        );
        sendAddedItem(action.item);
      } else {
        updateStrokes((current) =>
          current.filter(
            (item) => item.id !== action.item.id
          )
        );
        sendMessage?.({
          type: "whiteboard-remove",
          strokeId: action.item.id
        });
      }
      selectImage(null);
      return;
    }

    if (action.type === "update") {
      const item = undoing
        ? action.before
        : action.after;

      updateStrokes((current) =>
        current.map((existing) =>
          existing.id === item.id
            ? item
            : existing
        )
      );
      sendUpdatedItem(item);
      selectImage(item.id);
      return;
    }

    if (action.type === "clear") {
      if (undoing) {
        updateStrokes((current) => {
          const existingIds = new Set(
            current.map((item) => item.id)
          );

          return [
            ...current,
            ...action.items.filter(
              (item) => !existingIds.has(item.id)
            )
          ];
        });
        action.items.forEach(sendAddedItem);
      } else {
        updateStrokes((current) =>
          current.filter(
            (item) =>
              (item.pageId || INITIAL_PAGE_ID) !==
              action.pageId
          )
        );
        sendMessage?.({
          type: "whiteboard-clear",
          pageId: action.pageId
        });
      }
      selectImage(null);
    }
  };

  const undoLastAction = () => {
    const action =
      undoStackRef.current.pop();

    if (!action) {
      return;
    }

    redoStackRef.current.push(action);
    applyHistoryAction(action, "undo");
    setPasteMessage("Undone");
    window.setTimeout(
      () => setPasteMessage(""),
      1000
    );
  };

  const redoLastAction = () => {
    const action =
      redoStackRef.current.pop();

    if (!action) {
      return;
    }

    undoStackRef.current.push(action);
    applyHistoryAction(action, "redo");
    setPasteMessage("Redone");
    window.setTimeout(
      () => setPasteMessage(""),
      1000
    );
  };

  const clearBoard = () => {
    if (!isTutor) {
      return;
    }

    const pageId =
      activePageIdRef.current;
    const removedItems =
      strokesRef.current.filter(
        (item) =>
          (item.pageId || INITIAL_PAGE_ID) ===
          pageId
      );

    if (removedItems.length === 0) {
      return;
    }

    recordHistory({
      type: "clear",
      pageId,
      items: removedItems
    });

    selectImage(null);

    updateStrokes((current) =>
      current.filter(
        (item) =>
          (item.pageId || INITIAL_PAGE_ID) !==
          pageId
      )
    );
    sendMessage?.({
      type: "whiteboard-clear",
      pageId
    });
  };

  const selectPage = (pageId) => {
    if (!isTutor) {
      return;
    }

    selectPageLocally(pageId);
    sendMessage?.({
      type: "whiteboard-page-select",
      pageId,
      scrollProgress: 0
    });
  };

  const extendWhiteboardLocally = (pageId, nextSections) => {
    const currentSections = getPageSections(pageId);
    if (nextSections <= currentSections) return;

    const ratio = currentSections / nextSections;
    const nextPages = pagesRef.current.map((page) =>
      page.id === pageId
        ? { ...page, heightSections: nextSections }
        : page
    );
    const nextItems = strokesRef.current.map((item) =>
      scaleItemVertically(item, pageId, ratio)
    );

    pagesRef.current = nextPages;
    strokesRef.current = nextItems;
    setPages(nextPages);
    setStrokes(nextItems);
    publishSnapshot(nextItems, nextPages);

    window.requestAnimationFrame(() => {
      redraw();
      window.requestAnimationFrame(() => {
        workspaceRef.current?.scrollTo({
          top: workspaceRef.current.scrollHeight,
          behavior: "smooth"
        });
      });
    });
  };

  const extendWhiteboard = () => {
    if (!isTutor) return;

    const pageId = activePageIdRef.current;
    const heightSections =
      getPageSections(pageId) + BOARD_EXTENSION_SECTIONS;

    extendWhiteboardLocally(pageId, heightSections);
    sendMessage?.({
      type: "whiteboard-page-extend",
      pageId,
      heightSections
    });
  };

  const addWhiteboard = () => {
    if (!isTutor) {
      return;
    }

    const page = {
      id: createId(),
      title: `Whiteboard ${pagesRef.current.length + 1}`,
      background: "square",
      boardColour: "#ffffff",
      heightSections: 1
    };
    const nextPages = [
      ...pagesRef.current,
      page
    ];

    pagesRef.current = nextPages;
    setPages(nextPages);
    activePageIdRef.current = page.id;
    whiteboardViewRef.current = {
      pageId: page.id,
      scrollProgress: 0
    };
    setActivePageId(page.id);
    setRemoteCursors({});
    publishSnapshot(
      strokesRef.current,
      nextPages,
      page.id
    );

    sendMessage?.({
      type: "whiteboard-page-add",
      page,
      activePageId: page.id
    });
  };

  const deleteCurrentWhiteboard = () => {
    if (!isTutor || pagesRef.current.length <= 1) {
      return;
    }

    const pageId = activePageIdRef.current;
    const pageIndex = pagesRef.current.findIndex(
      (page) => page.id === pageId
    );
    const boardNumber = Math.max(1, pageIndex + 1);

    if (
      !window.confirm(
        `Delete Board ${boardNumber} and everything on it? This cannot be undone.`
      )
    ) {
      return;
    }

    const nextPages = pagesRef.current.filter(
      (page) => page.id !== pageId
    );
    const nextPage =
      nextPages[Math.min(Math.max(0, pageIndex - 1), nextPages.length - 1)];
    const removedItems = strokesRef.current.filter(
      (item) => (item.pageId || INITIAL_PAGE_ID) === pageId
    );
    const nextItems = strokesRef.current.filter(
      (item) => (item.pageId || INITIAL_PAGE_ID) !== pageId
    );

    removedItems.forEach((item) => {
      if (item.type === "image") {
        imageCacheRef.current.delete(item.id);
      }
    });

    pagesRef.current = nextPages;
    setPages(nextPages);
    activePageIdRef.current = nextPage.id;
    whiteboardViewRef.current = {
      pageId: nextPage.id,
      scrollProgress: 0
    };
    setActivePageId(nextPage.id);
    strokesRef.current = nextItems;
    setStrokes(nextItems);
    selectImage(null);
    setRemoteCursors({});
    undoStackRef.current = [];
    redoStackRef.current = [];
    publishSnapshot(nextItems, nextPages, nextPage.id);

    sendMessage?.({
      type: "whiteboard-page-delete",
      pageId,
      activePageId: nextPage.id
    });
  };

  const addMathTool = (mathTool) => {
    if (mathTool === "compass") {
      const item = {
        id: createId(),
        type: "compass",
        pageId: activePageIdRef.current,
        ownerId: participantId || "unknown",
        ownerName: safeName,
        colour,
        x: 0.45,
        y: 0.5,
        radius: 0.16,
        angle: -Math.PI / 4
      };

      recordHistory({ type: "add", item });
      updateStrokes((current) => [...current, item]);
      sendAddedItem(item);
      selectImage(item.id);
      setTool("compass");
      setSidebarPanel(null);
      setPasteMessage(
        "Red draws, blue changes radius, green rotates, grey joint moves"
      );
      window.setTimeout(() => setPasteMessage(""), 3200);
      return;
    }

    const item = {
      id: createId(),
      type: "math",
      mathTool,
      pageId: activePageIdRef.current,
      ownerId: participantId || "unknown",
      ownerName: safeName,
      colour,
      x: 0.5,
      y: 0.48,
      width:
        mathTool === "axes"
          ? 0.84
          : mathTool === "ruler"
            ? RULER_WIDTH
            : mathTool === "protractor"
              ? 0.5
              : 0.34,
      height:
        mathTool === "axes"
          ? 0.72
          : mathTool === "ruler"
            ? RULER_HEIGHT
            : mathTool === "protractor"
              ? 0.32
              : 0.3,
      rotation: 0
    };

    recordHistory({ type: "add", item });
    updateStrokes((current) => [...current, item]);
    sendAddedItem(item);
    selectImage(item.id);
    setTool("select");
    setSidebarPanel(null);
  };

  const getRandomGameValue = (type) => {
    if (type === "game-coin") {
      return Math.random() < 0.5 ? "H" : "T";
    }

    return Math.floor(Math.random() * (type === "game-spinner" ? 10 : 6)) + 1;
  };

  const animateGameItem = (item) => {
    if (!item || !["game-dice", "game-coin", "game-spinner"].includes(item.type)) {
      return;
    }

    const existingTimer = gameAnimationTimersRef.current.get(item.id);
    if (existingTimer) {
      window.clearInterval(existingTimer);
    }

    const original = { ...item };
    const finalValue = getRandomGameValue(item.type);
    let frame = 0;
    const timer = window.setInterval(() => {
      frame += 1;
      const finished = frame >= 8;
      const value = finished ? finalValue : getRandomGameValue(item.type);
      const updated = {
        ...item,
        value,
        rolling: !finished,
        rollToken: Date.now(),
        rollFrame: finished ? 0 : frame,
        tumble: finished
          ? 0
          : frame * (item.type === "game-dice" ? 0.78 : 0.42)
      };

      updateStrokes((current) =>
        current.map((entry) => (entry.id === item.id ? updated : entry))
      );
      sendUpdatedItem(updated);

      if (finished) {
        window.clearInterval(timer);
        gameAnimationTimersRef.current.delete(item.id);
        recordHistory({ type: "update", before: original, after: updated });
      }
    }, 70);

    gameAnimationTimersRef.current.set(item.id, timer);
  };

  const addGameTool = (game) => {
    const pageId = activePageIdRef.current;
    const existingCounters = strokesRef.current.filter(
      (item) =>
        item.type === "game-counter" &&
        (item.pageId || INITIAL_PAGE_ID) === pageId
    ).length;
    const existingGames = strokesRef.current.filter(
      (item) => isGameItem(item) && (item.pageId || INITIAL_PAGE_ID) === pageId
    ).length;
    const offset = (existingGames % 7) * 0.025;
    const dimensions = game === "spinner"
      ? { width: 0.15, height: 0.267 }
      : game === "counter"
        ? { width: 0.03, height: 0.0533 }
        : { width: 0.072, height: 0.128 };
    const item = {
      id: createId(),
      type: `game-${game}`,
      pageId,
      ownerId: participantId || "unknown",
      ownerName: safeName,
      x: clamp(0.44 + offset, 0.02, 1 - dimensions.width),
      y: clamp(0.38 + offset, 0.02, 1 - dimensions.height),
      ...dimensions,
      ...(game === "counter"
        ? { colour: COUNTER_COLOURS[existingCounters % COUNTER_COLOURS.length] }
        : game === "coin"
          ? { value: "H" }
          : { value: 1 })
    };

    recordHistory({ type: "add", item });
    updateStrokes((current) => [...current, item]);
    sendAddedItem(item);
    selectImage(item.id);
    setTool("select");
    setSidebarPanel(null);
  };

  const changeBackground = (background) => {
    const pageId = activePageIdRef.current;
    updatePages((current) =>
      current.map((page) =>
        page.id === pageId
          ? { ...page, background }
          : page
      )
    );
    sendMessage?.({
      type: "whiteboard-background",
      pageId,
      background
    });
    setSidebarPanel(null);
    window.setTimeout(redraw, 0);
  };

  const changeBoardColour = (boardColour) => {
    const pageId = activePageIdRef.current;
    updatePages((current) =>
      current.map((page) =>
        page.id === pageId
          ? { ...page, boardColour }
          : page
      )
    );
    sendMessage?.({
      type: "whiteboard-background",
      pageId,
      boardColour
    });
    window.setTimeout(redraw, 0);
  };

  const addImageFile = async (file) => {
    if (!file?.type?.startsWith("image/")) {
      setPasteMessage("Please choose an image file");
      return;
    }

    setPasteMessage("Uploading image…");

    try {
      const image = await createPastedImage(file);
      recordHistory({ type: "add", item: image });
      updateStrokes((current) => [...current, image]);
      sendImageInChunks(image);
      selectImage(image.id);
      setTool("select");
      setSidebarPanel(null);
      setPasteMessage("Image added");
    } catch (error) {
      console.error("Could not upload image:", error);
      setPasteMessage("Could not add that image");
    }

    window.setTimeout(() => setPasteMessage(""), 2000);
  };

  const encodeText = (value) =>
    new TextEncoder().encode(value);

  const decodeBase64 = (value) => {
    const binary = window.atob(value);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
  };

  const createImagesPdf = (images) => {
    const pageWidth = 842;
    const pageHeight = 595;
    const margin = 20;
    const parts = [
      new Uint8Array([
        0x25, 0x50, 0x44, 0x46, 0x2d, 0x31, 0x2e, 0x34,
        0x0a, 0x25, 0xff, 0xff, 0xff, 0xff, 0x0a
      ])
    ];
    const offsets = [0];
    let byteLength = parts[0].length;

    const append = (bytes) => {
      parts.push(bytes);
      byteLength += bytes.length;
    };

    const addObject = (number, objectParts) => {
      offsets[number] = byteLength;
      append(encodeText(`${number} 0 obj\n`));
      objectParts.forEach(append);
      append(encodeText("\nendobj\n"));
    };

    addObject(1, [
      encodeText("<< /Type /Catalog /Pages 2 0 R >>")
    ]);
    const pageReferences = images.map(
      (_, index) => `${3 + index * 3} 0 R`
    );
    addObject(2, [
      encodeText(
        `<< /Type /Pages /Kids [${pageReferences.join(" ")}] /Count ${images.length} >>`
      )
    ]);

    images.forEach((image, index) => {
      const pageObject = 3 + index * 3;
      const imageObject = pageObject + 1;
      const contentObject = pageObject + 2;
      const jpegBytes = decodeBase64(
        image.jpegDataUrl.split(",")[1]
      );
      const scale = Math.min(
        (pageWidth - margin * 2) / image.imageWidth,
        (pageHeight - margin * 2) / image.imageHeight
      );
      const drawWidth = image.imageWidth * scale;
      const drawHeight = image.imageHeight * scale;
      const drawX = (pageWidth - drawWidth) / 2;
      const drawY = (pageHeight - drawHeight) / 2;
      const content =
        `q\n${drawWidth.toFixed(2)} 0 0 ${drawHeight.toFixed(2)} ` +
        `${drawX.toFixed(2)} ${drawY.toFixed(2)} cm\n/Im0 Do\nQ\n`;
      const contentBytes = encodeText(content);

      addObject(pageObject, [
        encodeText(
          `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] ` +
          `/Resources << /XObject << /Im0 ${imageObject} 0 R >> >> /Contents ${contentObject} 0 R >>`
        )
      ]);
      addObject(imageObject, [
        encodeText(
          `<< /Type /XObject /Subtype /Image /Width ${image.imageWidth} ` +
          `/Height ${image.imageHeight} /ColorSpace /DeviceRGB ` +
          `/BitsPerComponent 8 /Filter /DCTDecode /Length ${jpegBytes.length} >>\nstream\n`
        ),
        jpegBytes,
        encodeText("\nendstream")
      ]);
      addObject(contentObject, [
        encodeText(`<< /Length ${contentBytes.length} >>\nstream\n`),
        contentBytes,
        encodeText("endstream")
      ]);
    });

    const xrefOffset = byteLength;
    const objectCount = 2 + images.length * 3;
    let xref = `xref\n0 ${objectCount + 1}\n0000000000 65535 f \n`;

    for (let number = 1; number <= objectCount; number += 1) {
      xref += `${String(offsets[number]).padStart(10, "0")} 00000 n \n`;
    }

    xref +=
      `trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\n` +
      `startxref\n${xrefOffset}\n%%EOF`;
    append(encodeText(xref));

    return new Blob(parts, {
      type: "application/pdf"
    });
  };

  const waitForExportImages = (items) =>
    Promise.all(
      items
        .filter((item) => item.type === "image" && item.dataUrl)
        .map(
          (item) =>
            new Promise((resolve) => {
              const cached = imageCacheRef.current.get(item.id);

              if (cached?.complete && cached.naturalWidth) {
                resolve();
                return;
              }

              const image = cached || new Image();
              image.onload = resolve;
              image.onerror = resolve;
              image.src = item.dataUrl;
              imageCacheRef.current.set(item.id, image);
            })
        )
    );

  const saveWhiteboardsAsPdf = async (allBoards = false) => {
    if (!isTutor || !canvasRef.current) {
      return;
    }

    try {
      const canvas = canvasRef.current;
      const pageIndex = Math.max(
        0,
        pagesRef.current.findIndex(
          (page) =>
            page.id === activePageIdRef.current
        )
      );
      const pagesToExport = allBoards
        ? pagesRef.current
        : [pagesRef.current[pageIndex]];
      const originalPageId = activePageIdRef.current;
      const originalSelectionId = selectedImageIdRef.current;
      const originalActiveStroke = activeStrokeRef.current;
      const exportItems = strokesRef.current.filter((item) =>
        pagesToExport.some(
          (page) => page.id === (item.pageId || INITIAL_PAGE_ID)
        )
      );

      await waitForExportImages(exportItems);

      let images;
      try {
        selectedImageIdRef.current = null;
        activeStrokeRef.current = null;
        images = pagesToExport.map((page) => {
          activePageIdRef.current = page.id;
          redraw();
          return {
            jpegDataUrl: canvas.toDataURL("image/jpeg", 0.94),
            imageWidth: canvas.width,
            imageHeight: canvas.height
          };
        });
      } finally {
        activePageIdRef.current = originalPageId;
        selectedImageIdRef.current = originalSelectionId;
        activeStrokeRef.current = originalActiveStroke;
        redraw();
      }

      const pdf = createImagesPdf(images);
      const downloadUrl = URL.createObjectURL(pdf);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = allBoards
        ? "all-whiteboards.pdf"
        : `whiteboard-${pageIndex + 1}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(
        () => URL.revokeObjectURL(downloadUrl),
        1000
      );
      setPasteMessage(
        allBoards ? "All boards saved as one PDF" : "Current board PDF saved"
      );
      window.setTimeout(
        () => setPasteMessage(""),
        1800
      );
    } catch (error) {
      console.error(
        "Could not save whiteboard PDF:",
        error
      );
      setPasteMessage("Could not save this whiteboard");
      window.setTimeout(
        () => setPasteMessage(""),
        2500
      );
    } finally {
      setShowPdfOptions(false);
    }
  };

  const activePageIndex = Math.max(
    0,
    pages.findIndex(
      (page) => page.id === activePageId
    )
  );
  const editingTextItem = strokes.find(
    (item) => item.id === editingTextId
  );
  const editingFontSize = editingTextItem
    ? editingTextItem.fontScale
      ? editingTextItem.fontScale *
        Math.max(1, boardSize?.height || 1)
      : (editingTextItem.size / 900) *
        Math.max(1, boardSize?.height || 1)
    : 18;

  const updateEditingTextFormat = (changes) => {
    const current = strokesRef.current.find(
      (item) => item.id === editingTextIdRef.current
    );
    if (!current) return;

    if (changes.fontFamily || changes.size || changes.fontScale) {
      textDefaultsRef.current = {
        ...textDefaultsRef.current,
        ...(changes.fontFamily ? { fontFamily: changes.fontFamily } : {}),
        ...(changes.size ? { size: changes.size } : {}),
        ...(changes.fontScale ? { fontScale: changes.fontScale } : {})
      };
    }
    const updatedItem = { ...current, ...changes };
    editingTextFormatRef.current = {
      ...(editingTextFormatRef.current || {}),
      ...changes
    };
    const canvasBounds = canvasRef.current?.getBoundingClientRect();
    const boardWidth = Math.max(1, canvasBounds?.width || boardSize?.width || 1);
    const boardHeight = Math.max(1, canvasBounds?.height || boardSize?.height || 1);
    const fontSize =
      (updatedItem.fontScale || (updatedItem.size || 22) / 900) *
      boardHeight;
    const measurementContext =
      document.createElement("canvas").getContext("2d");
    measurementContext.font = `${updatedItem.italic ? "italic" : "normal"} ${
      updatedItem.bold ? "700" : "400"
    } ${fontSize}px ${updatedItem.fontFamily || "Arial, sans-serif"}`;
    const lines = String(updatedItem.text || "").split("\n");
    const oneCharacterWidth = measurementContext.measureText("M").width;
    const measuredWidths = lines.map((line) =>
      measurementContext.measureText(line).width
    );
    const availableWidth = Math.max(
      oneCharacterWidth,
      (1 - updatedItem.x) * boardWidth
    );
    const fittedWidth = Math.min(
      availableWidth,
      Math.max(oneCharacterWidth, ...measuredWidths)
    );
    const visualLines = Math.max(1, lines.length);
    updatedItem.width = clamp(
      fittedWidth / boardWidth,
      oneCharacterWidth / boardWidth,
      1 - updatedItem.x
    );
    updatedItem.height = clamp(
      (visualLines * fontSize * 1.25) / boardHeight,
      (fontSize * 1.25) / boardHeight,
      1 - updatedItem.y
    );
    updateStrokes((items) =>
      items.map((item) =>
        item.id === updatedItem.id ? updatedItem : item
      )
    );
    sendUpdatedItem(updatedItem);
    window.setTimeout(() => {
      textEditorRef.current?.focus();
      redraw();
    }, 0);
  };

  const applySelectedTextFormat = (property) => {
    const editor = textEditorRef.current;
    if (!editor) return;

    editor.focus();
    const command = {
      bold: "bold",
      italic: "italic",
      underline: "underline"
    }[property];
    if (!command) return;

    document.execCommand(command, false);
    const text = editor.innerText || "";
    const richHtml = sanitiseRichTextHtml(editor);
    const current = strokesRef.current.find(
      (item) => item.id === editingTextIdRef.current
    );
    if (!current) return;

    const updatedItem = {
      ...current,
      text,
      richHtml,
      // Rich HTML now owns inline B/I/U state. These flags remain false to
      // prevent the containing text box applying a style to every character.
      bold: false,
      italic: false,
      underline: false
    };
    editingTextFormatRef.current = {
      ...(editingTextFormatRef.current || {}),
      richHtml,
      bold: false,
      italic: false,
      underline: false
    };
    updateStrokes((items) =>
      items.map((item) => item.id === updatedItem.id ? updatedItem : item)
    );
    sendUpdatedItem(updatedItem);
    window.setTimeout(() => editor.focus(), 0);
  };

  const calculatorEditorToPlainText = (editor) =>
    editor.map((part) =>
      part.type === "fraction"
        ? `(${part.numerator || "□"})/(${part.denominator || "□"})`
        : part.value
    ).join("");

  const calculatorEditorToExpression = (editor) =>
    editor.map((part) =>
      part.type === "fraction"
        ? `((${part.numerator || "NaN"})/(${part.denominator || "NaN"}))`
        : part.value
    ).join("");

  const commitCalculatorEditor = (
    editor,
    cursor,
    extra = {}
  ) => applyCalculatorState({
    editor,
    cursor,
    expression: calculatorEditorToPlainText(editor),
    ...extra
  });

  const insertCalculatorValue = (value) => {
    const state = calculatorStateRef.current;
    const editor = (state.editor || [
      { type: "text", value: state.expression || "" }
    ]).map((part) => ({ ...part }));
    const cursor = state.cursor || {
      index: 0,
      field: "value",
      offset: editor[0]?.value?.length || 0
    };
    const part = editor[cursor.index];

    if (!part) return;
    const field = part.type === "fraction"
      ? cursor.field
      : "value";
    const current = part[field] || "";
    part[field] =
      current.slice(0, cursor.offset) +
      value +
      current.slice(cursor.offset);
    commitCalculatorEditor(editor, {
      ...cursor,
      field,
      offset: cursor.offset + value.length
    });
  };

  const insertCalculatorFraction = () => {
    const state = calculatorStateRef.current;
    const editor = (state.editor || [
      { type: "text", value: state.expression || "" }
    ]).map((part) => ({ ...part }));
    const cursor = state.cursor || {
      index: 0,
      field: "value",
      offset: 0
    };
    const part = editor[cursor.index];

    if (!part || part.type === "fraction") {
      return;
    }

    const before = part.value.slice(0, cursor.offset);
    const after = part.value.slice(cursor.offset);
    editor.splice(
      cursor.index,
      1,
      { type: "text", value: before },
      { type: "fraction", numerator: "", denominator: "" },
      { type: "text", value: after }
    );
    commitCalculatorEditor(editor, {
      index: cursor.index + 1,
      field: "numerator",
      offset: 0
    });
  };

  const moveCalculatorCursor = (direction) => {
    const state = calculatorStateRef.current;
    const editor = state.editor || [
      { type: "text", value: state.expression || "" }
    ];
    const cursor = { ...(state.cursor || {
      index: 0,
      field: "value",
      offset: 0
    }) };
    const part = editor[cursor.index];
    if (!part) return;

    const field = part.type === "fraction" ? cursor.field : "value";
    const value = part[field] || "";

    if (direction === "up" && part.type === "fraction") {
      cursor.field = "numerator";
      cursor.offset = Math.min(cursor.offset, part.numerator.length);
    } else if (direction === "down" && part.type === "fraction") {
      cursor.field = "denominator";
      cursor.offset = Math.min(cursor.offset, part.denominator.length);
    } else if (direction === "left") {
      if (cursor.offset > 0) {
        cursor.offset -= 1;
      } else if (part.type === "fraction" && field === "denominator") {
        cursor.field = "numerator";
        cursor.offset = part.numerator.length;
      } else if (cursor.index > 0) {
        const previous = editor[cursor.index - 1];
        cursor.index -= 1;
        cursor.field = previous.type === "fraction" ? "denominator" : "value";
        cursor.offset = previous.type === "fraction"
          ? previous.denominator.length
          : previous.value.length;
      }
    } else if (direction === "right") {
      if (cursor.offset < value.length) {
        cursor.offset += 1;
      } else if (cursor.index < editor.length - 1) {
        const next = editor[cursor.index + 1];
        cursor.index += 1;
        cursor.field = next.type === "fraction" ? "numerator" : "value";
        cursor.offset = 0;
      }
    }

    commitCalculatorEditor(editor, cursor);
  };

  const deleteCalculatorCharacter = () => {
    const state = calculatorStateRef.current;
    const editor = (state.editor || [
      { type: "text", value: state.expression || "" }
    ]).map((part) => ({ ...part }));
    const cursor = { ...(state.cursor || {
      index: 0,
      field: "value",
      offset: 0
    }) };
    const part = editor[cursor.index];
    if (!part) return;
    const field = part.type === "fraction" ? cursor.field : "value";
    const value = part[field] || "";

    if (cursor.offset > 0) {
      part[field] =
        value.slice(0, cursor.offset - 1) +
        value.slice(cursor.offset);
      cursor.offset -= 1;
    } else if (part.type === "fraction" && field === "denominator") {
      cursor.field = "numerator";
      cursor.offset = part.numerator.length;
    } else if (part.type === "fraction") {
      const before = editor[cursor.index - 1]?.value || "";
      const after = editor[cursor.index + 1]?.value || "";
      editor.splice(
        Math.max(0, cursor.index - 1),
        3,
        { type: "text", value: before + after }
      );
      cursor.index = Math.max(0, cursor.index - 1);
      cursor.field = "value";
      cursor.offset = before.length;
    } else if (cursor.index > 0) {
      const previous = editor[cursor.index - 1];
      cursor.index -= 1;
      cursor.field = previous.type === "fraction" ? "denominator" : "value";
      cursor.offset = previous.type === "fraction"
        ? previous.denominator.length
        : previous.value.length;
    }

    commitCalculatorEditor(editor, cursor);
  };

  const evaluateCalculator = () => {
    const state = calculatorStateRef.current;
    const editor = state.editor || [
      { type: "text", value: state.expression || "" }
    ];
    const expression = calculatorEditorToExpression(editor);
    if (!expression.trim()) return;

    try {
      const normalised = expression
        .replaceAll("×", "*")
        .replaceAll("÷", "/")
        .replaceAll("−", "-")
        .replaceAll("π", "pi")
        .replaceAll("^", "**");
      const toRadians = (value) =>
        state.angleMode === "DEG"
          ? value * Math.PI / 180
          : value;
      const calculate = Function(
        "sin",
        "cos",
        "tan",
        "log",
        "ln",
        "sqrt",
        "abs",
        "pi",
        `"use strict"; return (${normalised});`
      );
      const value = calculate(
        (number) => Math.sin(toRadians(number)),
        (number) => Math.cos(toRadians(number)),
        (number) => Math.tan(toRadians(number)),
        Math.log10,
        Math.log,
        Math.sqrt,
        Math.abs,
        Math.PI
      );

      if (!Number.isFinite(value)) {
        throw new Error("Invalid result");
      }

      const rounded = Number.parseFloat(value.toPrecision(12));
      const result = String(rounded);
      commitCalculatorEditor(
        [{ type: "text", value: result }],
        { index: 0, field: "value", offset: result.length },
        { result }
      );
    } catch {
      applyCalculatorState({ result: "Error" });
    }
  };

  const pressCalculatorKey = (key) => {
    if (key === "AC") {
      commitCalculatorEditor(
        [{ type: "text", value: "" }],
        { index: 0, field: "value", offset: 0 },
        { result: "0" }
      );
      return;
    }
    if (key === "⌫") {
      deleteCalculatorCharacter();
      return;
    }
    if (key === "a⁄b") {
      insertCalculatorFraction();
      return;
    }
    if (key === "←") {
      moveCalculatorCursor("left");
      return;
    }
    if (key === "→") {
      moveCalculatorCursor("right");
      return;
    }
    if (key === "↑") {
      moveCalculatorCursor("up");
      return;
    }
    if (key === "↓") {
      moveCalculatorCursor("down");
      return;
    }
    if (key === "=") {
      evaluateCalculator();
      return;
    }
    if (key === "x²") {
      insertCalculatorValue("^2");
      return;
    }
    if (key === "±") {
      insertCalculatorValue("−");
      return;
    }

    const insertions = {
      sin: "sin(",
      cos: "cos(",
      tan: "tan(",
      log: "log(",
      ln: "ln(",
      "√": "sqrt(",
      "|x|": "abs(",
      "xʸ": "^"
    };
    insertCalculatorValue(insertions[key] || key);
  };

  const moveCalculator = (event) => {
    const drag = calculatorDragRef.current;
    if (!drag) return;
    const workspace = workspaceRef.current?.getBoundingClientRect();
    const nextPosition = {
      x: clamp(
        drag.x + event.clientX - drag.clientX,
        8,
        Math.max(8, (workspace?.width || 900) - 340)
      ),
      y: clamp(
        drag.y + event.clientY - drag.clientY,
        8,
        Math.max(8, (workspace?.height || 650) - 530)
      )
    };
    calculatorStateRef.current = {
      ...calculatorStateRef.current,
      position: nextPosition
    };
    setCalculatorPosition(nextPosition);

    const now = performance.now();
    if (now - lastCalculatorUpdateSentAtRef.current >= 60) {
      sendMessage?.({
        type: "whiteboard-calculator",
        state: calculatorStateRef.current
      });
      lastCalculatorUpdateSentAtRef.current = now;
    }
  };

  const renderCalculatorField = (
    value,
    index,
    field
  ) => {
    const active =
      calculatorCursor.index === index &&
      calculatorCursor.field === field;
    const offset = clamp(
      calculatorCursor.offset,
      0,
      value.length
    );

    return React.createElement(
      "span",
      { style: { whiteSpace: "pre" } },
      value.slice(0, offset),
      active
        ? React.createElement("span", {
            "aria-hidden": "true",
            style: {
              display: "inline-block",
              height: "1em",
              borderLeft: "2px solid #2563eb",
              verticalAlign: "-0.12em"
            }
          })
        : null,
      value.slice(offset) ||
        (!active && !value && field !== "value" ? "□" : "")
    );
  };

  const renderCalculatorExpression = () =>
    React.createElement(
      "span",
      { style: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: "1px"
      } },
      ...calculatorEditor.map((part, index) =>
        part.type === "fraction"
          ? React.createElement(
              "span",
              {
                key: `fraction-${index}`,
                style: {
              display: "inline-flex",
              minWidth: "38px",
              flexDirection: "column",
              alignItems: "stretch",
              textAlign: "center",
              lineHeight: 1,
              verticalAlign: "middle",
              fontSize: "1.18rem",
              fontWeight: 700,
              margin: "0 3px"
                }
              },
              React.createElement(
                "span",
                { style: {
                minHeight: "1.05em",
                padding: "0 5px 3px",
                borderBottom: "2px solid currentColor"
                } },
                renderCalculatorField(part.numerator, index, "numerator")
              ),
              React.createElement(
                "span",
                { style: { minHeight: "1.05em", padding: "3px 5px 0" } },
                renderCalculatorField(part.denominator, index, "denominator")
              )
            )
          : React.createElement(
              "span",
              { key: `text-${index}` },
              renderCalculatorField(part.value, index, "value")
            )
      )
    );

  useEffect(() => {
    if (!open || !calculatorOpen) {
      return undefined;
    }

    const handleCalculatorKeyboard = (event) => {
      const target = event.target;
      if (
        target?.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName) ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      ) {
        return;
      }

      const specialKeys = {
        ArrowLeft: "←",
        ArrowRight: "→",
        ArrowUp: "↑",
        ArrowDown: "↓",
        Backspace: "⌫",
        Enter: "=",
        "=": "=",
        "*": "×",
        "/": "÷",
        "-": "−"
      };
      const key = specialKeys[event.key] ||
        (/^[0-9.+()^]$/.test(event.key) ? event.key : null);

      if (!key) return;
      event.preventDefault();
      pressCalculatorKey(key);
    };

    window.addEventListener("keydown", handleCalculatorKeyboard);
    return () =>
      window.removeEventListener("keydown", handleCalculatorKeyboard);
  }, [open, calculatorOpen]);

  useEffect(() => {
    if (!open && recorderRef.current?.state !== "inactive") {
      stopWhiteboardRecording();
    }
    if (!open && !isTutor) {
      setRemoteRecordingStatus("idle");
    }
  }, [open, isTutor]);

  useEffect(() => () => {
    clearRecordingTimer();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    } else {
      stopRecordingTracks();
    }
  }, []);

  if (!open) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 12000,
        background: "#e2e8f0",
        color: "#0f172a",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <header
        style={{
          minHeight: "68px",
          padding: "0.65rem 1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.75rem",
          flexWrap: "wrap",
          background: "#0f172a",
          color: "white",
          boxShadow: "0 4px 18px rgba(15,23,42,.25)"
        }}
      >
        <div>
          <strong style={{ fontSize: "1.05rem" }}>
            ✏️ Shared Whiteboard
          </strong>
          <div style={{ fontSize: "0.72rem", opacity: 0.68 }}>
            {isTutor
              ? "You control when the whiteboard closes"
              : "The tutor has opened the whiteboard"}
            {" • Ctrl+V pastes • Ctrl+Z undo • Ctrl+Y redo • Delete removes"}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.45rem",
            flexWrap: "wrap"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
              padding: "0.25rem 0.4rem",
              borderRadius: "10px",
              background: "rgba(255,255,255,.08)"
            }}
          >
            {isTutor && (
              <button
                type="button"
                aria-label="Previous whiteboard"
                disabled={activePageIndex === 0}
                onClick={() =>
                  selectPage(
                    pages[activePageIndex - 1]?.id
                  )
                }
                style={toolbarButtonStyle(
                  false,
                  activePageIndex === 0
                )}
              >
                ‹
              </button>
            )}

            {isTutor ? (
              <select
                aria-label="Go to whiteboard"
                title="Choose a board"
                value={activePageId}
                onChange={(event) =>
                  selectPage(event.target.value)
                }
                style={{
                  minWidth: "124px",
                  height: "36px",
                  padding: "0 1.8rem 0 0.65rem",
                  border: "1px solid rgba(255,255,255,.24)",
                  borderRadius: "8px",
                  outline: "none",
                  background: "#1e293b",
                  color: "white",
                  fontSize: "0.76rem",
                  fontWeight: 700,
                  cursor: "pointer"
                }}
              >
                {pages.map((page, index) => (
                  <option
                    key={page.id}
                    value={page.id}
                  >
                    Board {index + 1} of {pages.length}
                  </option>
                ))}
              </select>
            ) : (
              <strong
                style={{
                  minWidth: "92px",
                  textAlign: "center",
                  fontSize: "0.76rem"
                }}
              >
                Board {activePageIndex + 1} of {pages.length}
              </strong>
            )}

            {isTutor && (
              <button
                type="button"
                aria-label="Next whiteboard"
                disabled={activePageIndex >= pages.length - 1}
                onClick={() =>
                  selectPage(
                    pages[activePageIndex + 1]?.id
                  )
                }
                style={toolbarButtonStyle(
                  false,
                  activePageIndex >= pages.length - 1
                )}
              >
                ›
              </button>
            )}

            {isTutor && (
              <button
                type="button"
                onClick={addWhiteboard}
                style={toolbarButtonStyle(false)}
              >
                ＋ New
              </button>
            )}

            {isTutor && pages.length > 1 && (
              <button
                type="button"
                aria-label={`Delete Board ${activePageIndex + 1}`}
                title="Delete this board and all of its contents"
                onClick={deleteCurrentWhiteboard}
                style={{
                  ...toolbarButtonStyle(false),
                  background: "#b91c1c"
                }}
              >
                🗑 Delete
              </button>
            )}

            {isTutor && (
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() =>
                    setShowPdfOptions((current) => !current)
                  }
                  style={{
                    ...toolbarButtonStyle(false),
                    background: "#047857"
                  }}
                >
                  PDF ▾
                </button>

                {showPdfOptions && (
                  <div
                    role="menu"
                    aria-label="PDF download options"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 8px)",
                      right: 0,
                      zIndex: 80,
                      width: "220px",
                      padding: "8px",
                      borderRadius: "12px",
                      background: "#ffffff",
                      border: "1px solid #cbd5e1",
                      boxShadow: "0 12px 30px rgba(15,23,42,.24)",
                      color: "#0f172a"
                    }}
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => saveWhiteboardsAsPdf(false)}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: 0,
                        borderRadius: "8px",
                        background: "transparent",
                        color: "#0f172a",
                        textAlign: "left",
                        cursor: "pointer",
                        fontWeight: 700
                      }}
                    >
                      Current board
                      <span style={{ display: "block", marginTop: "2px", color: "#64748b", fontSize: "11px", fontWeight: 500 }}>
                        Download Board {activePageIndex + 1} only
                      </span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => saveWhiteboardsAsPdf(true)}
                      style={{
                        width: "100%",
                        marginTop: "4px",
                        padding: "10px 12px",
                        border: 0,
                        borderRadius: "8px",
                        background: "#ecfdf5",
                        color: "#065f46",
                        textAlign: "left",
                        cursor: "pointer",
                        fontWeight: 700
                      }}
                    >
                      All boards
                      <span style={{ display: "block", marginTop: "2px", color: "#047857", fontSize: "11px", fontWeight: 500 }}>
                        One PDF · {pages.length} {pages.length === 1 ? "page" : "pages"}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {isTutor && recordingStatus === "idle" && (
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setShowRecordingOptions((current) => !current)}
                  style={{
                    ...toolbarButtonStyle(false),
                    background: "#b91c1c"
                  }}
                  title="Record the whiteboard"
                >
                  ● Record
                </button>

                {showRecordingOptions && (
                  <div
                    role="dialog"
                    aria-label="Whiteboard recording options"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 8px)",
                      right: 0,
                      // Above the floating tools (12001) and bottom controls (12002).
                      zIndex: 12004,
                      width: "230px",
                      padding: "12px",
                      borderRadius: "12px",
                      background: "#ffffff",
                      color: "#0f172a",
                      border: "1px solid #cbd5e1",
                      boxShadow: "0 14px 35px rgba(15,23,42,.3)"
                    }}
                  >
                    <strong style={{ display: "block", fontSize: "0.82rem", marginBottom: "9px" }}>
                      Recording options
                    </strong>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "0.76rem",
                        cursor: cameraEnabled && localVideoTrack ? "pointer" : "not-allowed"
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={includeWebcamInRecording}
                        disabled={!cameraEnabled || localVideoTrack?.readyState !== "live"}
                        onChange={(event) => setIncludeWebcamInRecording(event.target.checked)}
                      />
                      Include my webcam
                    </label>
                    {(!cameraEnabled || localVideoTrack?.readyState !== "live") && (
                      <span style={{ display: "block", marginTop: "5px", color: "#64748b", fontSize: "0.68rem" }}>
                        Turn your camera on to include it.
                      </span>
                    )}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "7px", marginTop: "12px" }}>
                      <button
                        type="button"
                        onClick={() => setShowRecordingOptions(false)}
                        style={{ ...toolbarButtonStyle(false), color: "white" }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => startWhiteboardRecording(includeWebcamInRecording)}
                        style={{ ...toolbarButtonStyle(false), background: "#b91c1c", color: "white" }}
                      >
                        ● Start
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isTutor && recordingStatus !== "idle" && (
              <>
                <span
                  aria-live="polite"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    minWidth: "76px",
                    color: recordingStatus === "paused" ? "#fde68a" : "#fecaca",
                    fontSize: "0.76rem",
                    fontWeight: 800,
                    fontVariantNumeric: "tabular-nums"
                  }}
                >
                  <span style={{ color: "#ef4444", fontSize: "1rem" }}>●</span>
                  {formatRecordingTime(recordingSeconds)}
                </span>
                <button
                  type="button"
                  onClick={toggleWhiteboardRecordingPause}
                  style={{
                    ...toolbarButtonStyle(false),
                    background: recordingStatus === "paused" ? "#047857" : "#a16207"
                  }}
                >
                  {recordingStatus === "paused" ? "▶ Resume" : "Ⅱ Pause"}
                </button>
                <button
                  type="button"
                  onClick={stopWhiteboardRecording}
                  style={{
                    ...toolbarButtonStyle(false),
                    background: "#b91c1c"
                  }}
                >
                  ■ Stop & Save
                </button>
              </>
            )}
          </div>

          <button
            type="button"
            aria-label="Select and move objects"
            title="Select, move, resize, rotate or delete objects"
            onClick={() => setTool("select")}
            style={toolbarButtonStyle(tool === "select")}
          >
            ↖ Select
          </button>

          <button
            type="button"
            onClick={() => {
              selectImage(null);
              setTool("pen");
            }}
            style={toolbarButtonStyle(tool === "pen")}
          >
            ✏ Pen
          </button>

          <button
            type="button"
            onClick={() => {
              selectImage(null);
              setTool("eraser");
            }}
            style={toolbarButtonStyle(tool === "eraser")}
          >
            ◻ Eraser
          </button>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
              padding: "0.25rem 0.45rem",
              borderRadius: "10px",
              background: "rgba(255,255,255,.08)"
            }}
          >
            {COLOURS.map((option) => (
              <button
                key={option}
                type="button"
                aria-label={`Use ${option}`}
                onClick={() => {
                  setColour(option);
                }}
                style={{
                  width: "24px",
                  height: "24px",
                  padding: 0,
                  borderRadius: "50%",
                  border:
                    colour === option
                      ? "3px solid white"
                      : "2px solid rgba(255,255,255,.35)",
                  background: option,
                  cursor: "pointer"
                }}
              />
            ))}
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.35rem",
              padding: "0.35rem 0.55rem",
              borderRadius: "10px",
              background: "rgba(255,255,255,.08)",
              fontSize: "0.75rem"
            }}
          >
            Size
            <input
              type="range"
              min="2"
              max="14"
              value={size}
              onChange={(event) => setSize(Number(event.target.value))}
            />
          </label>

          <button
            type="button"
            onClick={undoLastAction}
            style={toolbarButtonStyle(false)}
          >
            ↶ Undo
          </button>

          <button
            type="button"
            onClick={redoLastAction}
            style={toolbarButtonStyle(false)}
          >
            ↷ Redo
          </button>

          {isTutor && (
            <button
              type="button"
              onClick={clearBoard}
              style={{
                ...toolbarButtonStyle(false),
                background: "#92400e"
              }}
            >
              Clear
            </button>
          )}

          {isTutor && (
            <button
              type="button"
              onClick={onTutorClose}
              style={{
                ...toolbarButtonStyle(false),
                background: "#dc2626"
              }}
            >
              Close Whiteboard
            </button>
          )}
        </div>
      </header>

      <div
        ref={workspaceRef}
        aria-label="Whiteboard workspace"
        onScroll={handleWorkspaceScroll}
        style={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          padding: "12px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          overflowX: "auto",
          overflowY: "auto",
          scrollBehavior: "smooth"
        }}
      >
      <div
        ref={containerRef}
        style={{
          position: "relative",
          margin: "0 auto",
          flex: "0 0 auto",
          width: boardSize ? `${boardSize.width}px` : "100%",
          height: boardSize ? `${boardSize.height}px` : "100%",
          aspectRatio: `16 / ${9 * getPageSections()}`,
          overflow: "hidden",
          borderRadius: "14px",
          border: "1px solid #cbd5e1",
          boxShadow: "0 10px 30px rgba(15,23,42,.18)",
          background: "white"
        }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={(event) => {
            if (event.pointerType !== "pen") {
              updatePencilCursorOverlay(event);
            }
            if (localRecordingCursorRef.current) {
              localRecordingCursorRef.current.pointerDown = true;
            }
            startDrawing(event);
          }}
          onDoubleClick={editTextAtPoint}
          onPointerMove={(event) => {
            updatePencilCursorOverlay(event);
            continueDrawing(event);
          }}
          onPointerUp={(event) => {
            if (localRecordingCursorRef.current) {
              localRecordingCursorRef.current.pointerDown = false;
            }
            finishDrawing(event);
          }}
          onPointerCancel={(event) => {
            updatePencilCursorOverlay(event, false);
            if (localRecordingCursorRef.current) {
              localRecordingCursorRef.current.pointerDown = false;
            }
            finishDrawing(event);
          }}
          onPointerLeave={(event) => {
            updatePencilCursorOverlay(event, false);
            if (
              drawingRef.current ||
              imageInteractionRef.current
            ) {
              finishDrawing(event);
            }
            if (localRecordingCursorRef.current) {
              localRecordingCursorRef.current = {
                ...localRecordingCursorRef.current,
                visible: false,
                pointerDown: false
              };
            }
          }}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            cursor: canvasCursor,
            touchAction: "none",
            userSelect: "none"
          }}
        />

        {recordingStatus === "idle" &&
          strokes
            .filter(
              (item) =>
                item.type === "text" &&
                item.id !== editingTextId &&
                (item.pageId || INITIAL_PAGE_ID) === activePageId
            )
            .map((item) => {
              const renderedFontSize =
                (item.fontScale || (item.size || 22) / 900) *
                Math.max(1, boardSize?.height || 1);

              return (
                <div
                  key={`rendered-text-${item.id}`}
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: `${item.x * 100}%`,
                    top: `${item.y * 100}%`,
                    zIndex: 5,
                    width: `${(item.width || 0.2) * 100}%`,
                    minHeight: `${(item.height || 0.08) * 100}%`,
                    pointerEvents: "none",
                    userSelect: "none",
                    color: item.colour || "#111827",
                    fontFamily: item.fontFamily || "Arial, sans-serif",
                    fontSize: `${renderedFontSize}px`,
                    fontWeight: item.richHtml ? 400 : item.bold ? 700 : 400,
                    fontStyle: item.richHtml
                      ? "normal"
                      : item.italic ? "italic" : "normal",
                    textDecorationLine: item.richHtml
                      ? "none"
                      : item.underline ? "underline" : "none",
                    textDecorationColor: item.colour || "#111827",
                    textDecorationThickness: "0.055em",
                    textUnderlineOffset: "0.08em",
                    lineHeight: 1.25,
                    whiteSpace: "pre",
                    overflow: "visible",
                    transform: `rotate(${item.rotation || 0}rad)`,
                    transformOrigin: "center center"
                  }}
                  dangerouslySetInnerHTML={{
                    __html: getTextItemHtml(item)
                  }}
                />
              );
            })}

        {tool === "pen" && (
          <div
            ref={pencilCursorOverlayRef}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              zIndex: 12,
              display: "none",
              width: "32px",
              height: "32px",
              pointerEvents: "none",
              willChange: "transform"
            }}
          >
            <PencilCursorIcon />
          </div>
        )}

        {!isTutor && remoteRecordingNotice && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "absolute",
              top: "12px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 14,
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              maxWidth: "calc(100% - 32px)",
              padding: "8px 13px",
              borderRadius: "999px",
              border: remoteRecordingNotice.kind === "paused"
                ? "1px solid #fbbf24"
                : remoteRecordingNotice.kind === "stopped"
                  ? "1px solid #4ade80"
                  : "1px solid #f87171",
              background: remoteRecordingNotice.kind === "paused"
                ? "rgba(120,53,15,.94)"
                : remoteRecordingNotice.kind === "stopped"
                  ? "rgba(20,83,45,.94)"
                  : "rgba(127,29,29,.94)",
              color: "white",
              boxShadow: "0 5px 18px rgba(15,23,42,.28)",
              fontSize: "0.76rem",
              fontWeight: 800,
              whiteSpace: "nowrap",
              pointerEvents: "none",
              opacity: remoteRecordingNotice.visible ? 1 : 0,
              transition: "opacity 420ms ease"
            }}
          >
            <span
              style={{
                color: remoteRecordingNotice.kind === "paused"
                  ? "#fbbf24"
                  : remoteRecordingNotice.kind === "stopped"
                    ? "#86efac"
                    : "#f87171",
                fontSize: "1rem",
                lineHeight: 1
              }}
            >
              {remoteRecordingNotice.kind === "paused"
                ? "Ⅱ"
                : remoteRecordingNotice.kind === "stopped"
                  ? "■"
                  : "●"}
            </span>
            {remoteRecordingNotice.kind === "paused"
              ? "Whiteboard Recording Paused"
              : remoteRecordingNotice.kind === "stopped"
                ? "Whiteboard Recording Stopped"
                : remoteRecordingNotice.kind === "resumed"
                  ? "Whiteboard Recording Resumed"
                  : "This whiteboard is being recorded"}
          </div>
        )}

        {editingTextItem &&
          (editingTextItem.pageId || INITIAL_PAGE_ID) === activePageId && (
          <>
          <div
            role="toolbar"
            aria-label="Text formatting"
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
            style={{
              position: "absolute",
              left: `${editingTextItem.x * 100}%`,
              top: `calc(${editingTextItem.y * 100}% - 48px)`,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "5px 7px",
              maxWidth: "min(680px, calc(100% - 16px))",
              overflowX: "auto",
              whiteSpace: "nowrap",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              background: "rgba(255,255,255,.98)",
              boxShadow: "0 4px 14px rgba(15,23,42,.18)",
              // Keep the controls above the box even when the text is near
              // the top edge; never cover the words being typed.
              transform: "none"
            }}
          >
            {[
              ["B", "bold", "Bold"],
              ["I", "italic", "Italic"],
              ["U", "underline", "Underline"]
            ].map(([label, property, title]) => (
              <button
                key={property}
                type="button"
                title={title}
                aria-pressed={!!editingTextItem[property]}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => applySelectedTextFormat(property)}
                style={{
                  width: "30px",
                  height: "30px",
                  minWidth: "30px",
                  padding: 0,
                  border: "1px solid #cbd5e1",
                  borderRadius: "5px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: editingTextItem[property]
                    ? "#dbeafe"
                    : "#fff",
                  color: "#0f172a",
                  fontWeight: property === "bold" ? 800 : 600,
                  fontStyle: property === "italic" ? "italic" : "normal",
                  textDecoration: property === "underline" ? "underline" : "none",
                  fontSize: "17px",
                  lineHeight: 1,
                  cursor: "pointer"
                }}
              >
                {label}
              </button>
            ))}
            <select
              aria-label="Font"
              title="Font"
              value={editingTextItem.fontFamily || "Arial, sans-serif"}
              onChange={(event) =>
                updateEditingTextFormat({ fontFamily: event.target.value })
              }
              style={{ height: "30px", border: "1px solid #cbd5e1", borderRadius: "5px" }}
            >
              <option value="Arial, sans-serif">Arial</option>
              <option value="Verdana, sans-serif">Verdana</option>
              <option value="Georgia, serif">Georgia</option>
              <option value="'Times New Roman', serif">Times New Roman</option>
              <option value="'Trebuchet MS', sans-serif">Trebuchet</option>
              <option value="'Comic Sans MS', cursive">Comic Sans</option>
            </select>
            <select
              aria-label="Font size"
              title="Font size"
              value={Math.round((editingTextItem.fontScale || editingTextItem.size / 900) * 900)}
              onChange={(event) => {
                const logicalSize = Number(event.target.value);
                updateEditingTextFormat({
                  size: logicalSize,
                  fontScale: logicalSize / 900
                });
              }}
              style={{ height: "30px", border: "1px solid #cbd5e1", borderRadius: "5px" }}
            >
              {[16, 18, 20, 22, 24, 28, 32, 40, 48, 64, 72].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
            <label
              title="Text colour"
              style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" }}
            >
              Colour
              <input
                type="color"
                aria-label="Text colour"
                value={editingTextItem.colour || "#111827"}
                onChange={(event) =>
                  updateEditingTextFormat({ colour: event.target.value })
                }
                style={{ width: "32px", height: "30px", padding: "2px", border: "1px solid #cbd5e1", borderRadius: "5px" }}
              />
            </label>
          </div>
          <div
            key={editingTextItem.id}
            ref={textEditorRef}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-label="Whiteboard text box"
            data-placeholder="Type here…"
            onPointerDown={(event) => event.stopPropagation()}
            onInput={(event) => {
              const text = event.currentTarget.innerText || "";
              const richHtml = sanitiseRichTextHtml(event.currentTarget);
              const measurementCanvas =
                document.createElement("canvas");
              const measurementContext =
                measurementCanvas.getContext("2d");
              measurementContext.font = `${
                editingTextItem.italic ? "italic" : "normal"
              } ${editingTextItem.bold ? "700" : "400"} ${
                editingFontSize
              }px ${editingTextItem.fontFamily || "Arial, sans-serif"}`;
              const oneCharacterWidth =
                measurementContext.measureText("M").width;
              const measuredLineWidths = text
                .split("\n")
                .map((line) =>
                  measurementContext.measureText(line).width
                );
              const editorWidth = Math.max(
                oneCharacterWidth,
                ...measuredLineWidths
              );
              const canvasBounds =
                canvasRef.current?.getBoundingClientRect();
              const visualLineCount = Math.max(
                1,
                text.split("\n").length
              );
              const editorHeight =
                visualLineCount * editingFontSize * 1.25;
              const updatedItem = {
                ...editingTextItem,
                text,
                richHtml,
                fontScale:
                  editingTextItem.fontScale ||
                  editingTextItem.size / 900,
                width: clamp(
                  editorWidth /
                    Math.max(1, canvasBounds?.width || 1),
                  oneCharacterWidth /
                    Math.max(1, canvasBounds?.width || 1),
                  1 - editingTextItem.x
                ),
                height: clamp(
                  editorHeight /
                    Math.max(1, canvasBounds?.height || 1),
                  0.02,
                  1 - editingTextItem.y
                )
              };

              editingTextFormatRef.current = {
                ...(editingTextFormatRef.current || {}),
                richHtml,
                bold: false,
                italic: false,
                underline: false
              };

              updateStrokes((current) =>
                current.map((item) =>
                  item.id === editingTextItem.id
                    ? updatedItem
                    : item
                )
              );
              sendMessage?.({
                type: "whiteboard-item-update",
                item: updatedItem
              });
            }}
            onBlur={(event) => {
              if (event.relatedTarget?.closest?.('[role="toolbar"]')) {
                return;
              }
              finishTextEditing();
            }}
            onKeyDown={(event) => {
              if ((event.ctrlKey || event.metaKey) && !event.altKey) {
                const formattingShortcut = {
                  b: "bold",
                  i: "italic",
                  u: "underline"
                }[event.key.toLowerCase()];

                if (formattingShortcut) {
                  event.preventDefault();
                  applySelectedTextFormat(formattingShortcut);
                  return;
                }
              }

              if (event.key === "Escape") {
                event.preventDefault();
                finishTextEditing(true);
                return;
              }

              if (
                event.key === "Enter" &&
                (event.ctrlKey || event.metaKey)
              ) {
                event.preventDefault();
                finishTextEditing();
              }
            }}
            onPaste={(event) => {
              event.preventDefault();
              document.execCommand(
                "insertText",
                false,
                event.clipboardData.getData("text/plain")
              );
            }}
            style={{
              position: "absolute",
              left: `${editingTextItem.x * 100}%`,
              top: `${editingTextItem.y * 100}%`,
              zIndex: 8,
              display: "inline-block",
              width: `calc(${
                (editingTextItem.width ||
                  editingFontSize * 0.62 /
                    Math.max(1, boardSize?.width || 1)) * 100
              }% + 4px)`,
              minWidth: "calc(1ch + 4px)",
              maxWidth: `calc(${(1 - editingTextItem.x) * 100}% - 12px)`,
              minHeight: `${editingFontSize * 1.25}px`,
              padding: 0,
              boxSizing: "border-box",
              border: "none",
              borderRadius: "4px",
              outline: "2px solid rgba(37,99,235,.18)",
              background:
                recordingStatus === "idle"
                  ? "rgba(255,255,255,.72)"
                  : "#ffffff",
              color: editingTextItem.colour,
              fontFamily: editingTextItem.fontFamily || "Arial, sans-serif",
              fontSize: `${editingFontSize}px`,
              fontWeight: editingTextItem.richHtml
                ? 400
                : editingTextItem.bold ? 700 : 400,
              fontStyle: editingTextItem.richHtml
                ? "normal"
                : editingTextItem.italic ? "italic" : "normal",
              textDecoration: editingTextItem.richHtml
                ? "none"
                : editingTextItem.underline ? "underline" : "none",
              lineHeight: 1.25,
              whiteSpace: "pre",
              overflowWrap: "normal",
              wordBreak: "normal",
              transform: `rotate(${editingTextItem.rotation || 0}rad)`,
              transformOrigin: "center center",
              cursor: "text"
            }}
          />
          </>
        )}

        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) addImageFile(file);
            event.target.value = "";
          }}
        />

        <aside
          style={{
            position: "fixed",
            top: `${sidebarAnchor.top}px`,
            left: `${sidebarAnchor.left}px`,
            zIndex: 12001,
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
            maxHeight: `calc(100vh - ${sidebarAnchor.top + 104}px)`,
            overflowY: "auto",
            overflowX: "visible",
            paddingRight: "4px",
            pointerEvents: "auto",
            scrollbarWidth: "thin"
          }}
        >
          <div
            style={{
              width: sidebarOpen ? "58px" : "44px",
              padding: "6px",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              borderRadius: "15px",
              background: "rgba(15,23,42,.94)",
              boxShadow: "0 12px 30px rgba(15,23,42,.3)",
              backdropFilter: "blur(8px)"
            }}
          >
            <button
              type="button"
              aria-label={sidebarOpen ? "Collapse tools" : "Expand tools"}
              title={sidebarOpen ? "Collapse tools" : "Expand tools"}
              onClick={() => {
                setSidebarOpen((current) => !current);
                setSidebarPanel(null);
              }}
              style={sidebarToolStyle(false)}
            >
              {sidebarOpen ? "◀" : "▶"}
            </button>

            {sidebarOpen && (
              <>
                <button
                  type="button"
                  title="Highlighter"
                  aria-label="Highlighter"
                  onClick={() => {
                    selectImage(null);
                    setSidebarPanel(null);
                    setTool("highlighter");
                  }}
                  style={sidebarToolStyle(tool === "highlighter")}
                >
                  🖍️
                </button>

                <button
                  type="button"
                  title="Text box"
                  aria-label="Text box"
                  onClick={() => {
                    selectImage(null);
                    setSidebarPanel(null);
                    setTool("text");
                  }}
                  style={sidebarToolStyle(tool === "text")}
                >
                  T
                </button>

                <button
                  type="button"
                  title="Shapes"
                  aria-label="Shapes"
                  onClick={() =>
                    setSidebarPanel((current) =>
                      current === "shapes" ? null : "shapes"
                    )
                  }
                  style={sidebarToolStyle(
                    tool.startsWith("shape-") || sidebarPanel === "shapes"
                  )}
                >
                  ◯
                </button>

                <button
                  type="button"
                  title="Maths tools"
                  aria-label="Maths tools"
                  onClick={() =>
                    setSidebarPanel((current) =>
                      current === "maths" ? null : "maths"
                    )
                  }
                  style={sidebarToolStyle(sidebarPanel === "maths")}
                >
                  📐
                </button>

                <button
                  type="button"
                  title="Games"
                  aria-label="Games"
                  onClick={() =>
                    setSidebarPanel((current) =>
                      current === "games" ? null : "games"
                    )
                  }
                  style={sidebarToolStyle(sidebarPanel === "games")}
                >
                  🎲
                </button>

                <button
                  type="button"
                  title="Background"
                  aria-label="Background"
                  onClick={() =>
                    setSidebarPanel((current) =>
                      current === "background" ? null : "background"
                    )
                  }
                  style={sidebarToolStyle(sidebarPanel === "background")}
                >
                  ▦
                </button>

                <button
                  type="button"
                  title="Scientific calculator"
                  aria-label="Scientific calculator"
                  onClick={() => {
                    setSidebarPanel(null);
                    applyCalculatorState({
                      open: !calculatorStateRef.current.open
                    });
                  }}
                  style={sidebarToolStyle(calculatorOpen)}
                >
                  <CalculatorIcon />
                </button>

                <button
                  type="button"
                  title="Upload image"
                  aria-label="Upload image"
                  onClick={() => uploadInputRef.current?.click()}
                  style={sidebarToolStyle(false)}
                >
                  🖼️
                </button>
              </>
            )}
          </div>

          {sidebarOpen && sidebarPanel && (
            <div
              style={{
                minWidth: "190px",
                padding: "10px",
                borderRadius: "13px",
                background: "rgba(15,23,42,.96)",
                color: "white",
                boxShadow: "0 12px 30px rgba(15,23,42,.3)"
              }}
            >
              <div
                style={{
                  marginBottom: "8px",
                  fontSize: "0.75rem",
                  fontWeight: 800,
                  opacity: 0.8,
                  textTransform: "uppercase",
                  letterSpacing: ".04em"
                }}
              >
                {sidebarPanel === "shapes"
                  ? "Shapes"
                  : sidebarPanel === "maths"
                    ? "Maths tools"
                    : sidebarPanel === "games"
                      ? "Games"
                      : "Background"}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "6px"
                }}
              >
                {sidebarPanel === "shapes" && [
                  ["line", "／ Line"],
                  ["arrow", "➜ Arrow"],
                  ["rectangle", "▭ Rectangle"],
                  ["circle", "◯ Circle"],
                  ["triangle", "△ Triangle"]
                ].map(([shape, label]) => (
                  <button
                    key={shape}
                    type="button"
                    onClick={() => {
                      selectImage(null);
                      setTool(`shape-${shape}`);
                      setSidebarPanel(null);
                    }}
                    style={flyoutButtonStyle(tool === `shape-${shape}`)}
                  >
                    {label}
                  </button>
                ))}

                {sidebarPanel === "maths" && [
                  ["axes", "＋ Axes"],
                  ["ruler", "📏 Ruler"],
                  ["protractor", "📐 Protractor"],
                  ["compass", "◉ Compass"]
                ].map(([mathTool, label]) => (
                  <button
                    key={mathTool}
                    type="button"
                    onClick={() => addMathTool(mathTool)}
                    style={flyoutButtonStyle(false)}
                  >
                    {label}
                  </button>
                ))}

                {sidebarPanel === "games" && [
                  ["dice", "🎲 Dice"],
                  ["counter", "🔴 Counter"],
                  ["coin", "🪙 Coin flip"],
                  ["spinner", "🎯 Spinner 1–10"]
                ].map(([game, label]) => (
                  <button
                    key={game}
                    type="button"
                    onClick={() => addGameTool(game)}
                    style={flyoutButtonStyle(false)}
                  >
                    {label}
                  </button>
                ))}

                {sidebarPanel === "background" && [
                  ["plain", "□ Plain"],
                  ["square", "▦ Square"],
                  ["dotted", "⠿ Dotted"],
                  ["lined", "☰ Lined"]
                ].map(([background, label]) => (
                  <button
                    key={background}
                    type="button"
                    onClick={() => changeBackground(background)}
                    style={flyoutButtonStyle(
                      (pages.find((page) => page.id === activePageId)?.background || "square") === background
                    )}
                  >
                    {label}
                  </button>
                ))}

                {sidebarPanel === "background" && (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      marginTop: "4px",
                      paddingTop: "10px",
                      borderTop: "1px solid rgba(148,163,184,.35)"
                    }}
                  >
                    <span style={{ fontSize: "12px", fontWeight: 800 }}>
                      Board colour
                    </span>
                    <div style={{ display: "flex", gap: "7px", flexWrap: "wrap" }}>
                      {[
                        "#ffffff",
                        "#fff7d6",
                        "#e8f5e9",
                        "#e3f2fd",
                        "#f3e8ff",
                        "#1e293b",
                        "#111827"
                      ].map((boardColour) => {
                        const activeColour =
                          pages.find((page) => page.id === activePageId)?.boardColour ||
                          "#ffffff";
                        return (
                          <button
                            key={boardColour}
                            type="button"
                            title={boardColour}
                            aria-label={`Set board colour to ${boardColour}`}
                            onClick={() => changeBoardColour(boardColour)}
                            style={{
                              width: "28px",
                              height: "28px",
                              padding: 0,
                              borderRadius: "7px",
                              border:
                                activeColour === boardColour
                                  ? "3px solid #60a5fa"
                                  : "2px solid rgba(148,163,184,.65)",
                              background: boardColour,
                              cursor: "pointer",
                              boxShadow: activeColour === boardColour
                                ? "0 0 0 2px rgba(37,99,235,.3)"
                                : "none"
                            }}
                          />
                        );
                      })}
                      <label
                        title="Choose a custom board colour"
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "7px",
                          overflow: "hidden",
                          border: "2px solid rgba(148,163,184,.65)",
                          cursor: "pointer",
                          background: "conic-gradient(red,yellow,lime,aqua,blue,magenta,red)"
                        }}
                      >
                        <input
                          type="color"
                          aria-label="Custom board colour"
                          value={
                            pages.find((page) => page.id === activePageId)?.boardColour ||
                            "#ffffff"
                          }
                          onChange={(event) => changeBoardColour(event.target.value)}
                          style={{ opacity: 0, width: "100%", height: "100%", cursor: "pointer" }}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </aside>

        {calculatorOpen && (
          <section
            aria-label="Scientific calculator"
            style={{
              position: "absolute",
              left: `${calculatorPosition.x}px`,
              top: `${calculatorPosition.y}px`,
              zIndex: 16,
              width: "320px",
              padding: "10px",
              borderRadius: "16px",
              background: "#0f172a",
              color: "white",
              border: "1px solid rgba(148,163,184,.45)",
              boxShadow: "0 18px 45px rgba(15,23,42,.42)",
              userSelect: "none"
            }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <div
              onPointerDown={(event) => {
                event.preventDefault();
                event.currentTarget.setPointerCapture?.(event.pointerId);
                calculatorDragRef.current = {
                  clientX: event.clientX,
                  clientY: event.clientY,
                  x: calculatorPosition.x,
                  y: calculatorPosition.y
                };
              }}
              onPointerMove={moveCalculator}
              onPointerUp={(event) => {
                calculatorDragRef.current = null;
                event.currentTarget.releasePointerCapture?.(event.pointerId);
                applyCalculatorState({
                  position: calculatorStateRef.current.position
                });
              }}
              onPointerCancel={() => {
                calculatorDragRef.current = null;
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "2px 2px 9px",
                cursor: "move",
                touchAction: "none"
              }}
            >
              <strong style={{ fontSize: "0.86rem" }}>
                Scientific calculator
              </strong>
              <button
                type="button"
                title="Close calculator"
                aria-label="Close calculator"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={() => applyCalculatorState({ open: false })}
                style={{
                  width: "28px",
                  height: "28px",
                  border: "none",
                  borderRadius: "8px",
                  background: "#334155",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: 800
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                minHeight: "78px",
                padding: "10px 12px",
                marginBottom: "9px",
                borderRadius: "11px",
                background: "#f8fafc",
                color: "#0f172a",
                textAlign: "right",
                overflow: "hidden"
              }}
            >
              <div
                style={{
                  minHeight: "44px",
                  color: "#64748b",
                  fontSize: "0.88rem",
                  overflowWrap: "anywhere"
                }}
              >
                {calculatorEditor.some((part) =>
                  part.type === "fraction" || part.value
                )
                  ? renderCalculatorExpression()
                  : renderCalculatorField("", 0, "value")}
              </div>
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "1.45rem",
                  fontWeight: 800,
                  overflowWrap: "anywhere"
                }}
              >
                {calculatorResult}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "38px 38px 112px 38px 38px",
                alignItems: "end",
                justifyContent: "space-between",
                gap: "4px",
                margin: "2px 0 12px",
                padding: "0 2px"
              }}
            >
              <div style={{ textAlign: "center" }}>
                <span style={calculatorControlLabelStyle("#fde047")}>SHIFT</span>
                <button
                  type="button"
                  title="Switch between degrees and radians"
                  aria-label={`Angle mode ${calculatorAngleMode}`}
                  onClick={() => applyCalculatorState({
                    angleMode:
                      calculatorStateRef.current.angleMode === "DEG"
                        ? "RAD"
                        : "DEG"
                  })}
                  style={calculatorRoundButtonStyle}
                >
                  {calculatorAngleMode}
                </button>
              </div>

              <div style={{ textAlign: "center" }}>
                <span style={calculatorControlLabelStyle("#f472b6")}>ALPHA</span>
                <button
                  type="button"
                  title="Insert pi"
                  aria-label="Insert pi"
                  onClick={() => pressCalculatorKey("π")}
                  style={calculatorRoundButtonStyle}
                >
                  π
                </button>
              </div>

              <div
                aria-label="Calculator cursor controls"
                style={{
                  position: "relative",
                  width: "112px",
                  height: "78px",
                  borderRadius: "50%",
                  background: "linear-gradient(145deg,#0a0d12,#343b45)",
                  border: "3px solid #020409",
                  boxShadow:
                    "inset 0 0 0 3px #6b7280,0 3px 7px rgba(0,0,0,.42)"
                }}
              >
                {[
                  ["↑", "up", { top: "3px", left: "31px", width: "48px", height: "30px", borderRadius: "55% 55% 20% 20%" }],
                  ["↓", "down", { bottom: "3px", left: "31px", width: "48px", height: "30px", borderRadius: "20% 20% 55% 55%" }],
                  ["←", "left", { top: "21px", left: "3px", width: "38px", height: "35px", borderRadius: "55% 20% 20% 55%" }],
                  ["→", "right", { top: "21px", right: "3px", width: "38px", height: "35px", borderRadius: "20% 55% 55% 20%" }]
                ].map(([symbol, direction, position]) => (
                  <button
                    key={direction}
                    type="button"
                    title={`Move cursor ${direction}`}
                    aria-label={`Move calculator cursor ${direction}`}
                    onClick={() => pressCalculatorKey(symbol)}
                    style={{
                      position: "absolute",
                      ...position,
                      padding: 0,
                      border: "1px solid #111827",
                      background:
                        "linear-gradient(145deg,#f8fafc 0%,#9ca3af 45%,#374151 100%)",
                      color: "#020617",
                      fontSize: "0.9rem",
                      fontWeight: 950,
                      cursor: "pointer",
                      boxShadow:
                        "inset 1px 1px 2px rgba(255,255,255,.9),inset -2px -2px 3px rgba(0,0,0,.38)"
                    }}
                  >
                    {symbol}
                  </button>
                ))}
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "42px",
                    top: "26px",
                    width: "28px",
                    height: "26px",
                    borderRadius: "50%",
                    background: "#05070b",
                    border: "2px solid #9ca3af",
                    pointerEvents: "none"
                  }}
                />
              </div>

              <div style={{ textAlign: "center" }}>
                <span style={calculatorControlLabelStyle("#e2e8f0")}>MENU</span>
                <span style={{ ...calculatorRoundButtonStyle, cursor: "default" }}>
                  M
                </span>
              </div>

              <div style={{ textAlign: "center" }}>
                <span style={calculatorControlLabelStyle("#e2e8f0")}>ON</span>
                <span style={{ ...calculatorRoundButtonStyle, cursor: "default" }}>
                  ON
                </span>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: "6px"
              }}
            >
              {[
                "sin", "cos", "tan", "log", "ln",
                "a⁄b", "(", ")", "x²", "xʸ",
                "7", "8", "9", "÷", "AC",
                "4", "5", "6", "×", "⌫",
                "1", "2", "3", "−", "√",
                "0", ".", "±", "+", "="
              ].map((key) => {
                const actionKey = ["AC", "⌫"].includes(key);
                const equalsKey = key === "=";
                const functionKey = [
                  "sin", "cos", "tan", "log", "ln",
                  "a⁄b", "(", ")", "x²", "xʸ", "√"
                ].includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => pressCalculatorKey(key)}
                    style={{
                      minWidth: 0,
                      height: "42px",
                      border: "none",
                      borderRadius: "9px",
                      background: equalsKey
                        ? "#2563eb"
                        : actionKey
                          ? "#b91c1c"
                          : functionKey
                            ? "#334155"
                            : "#1e293b",
                      color: "white",
                      fontSize: "0.9rem",
                      fontWeight: 750,
                      cursor: "pointer"
                    }}
                  >
                    {key}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {recipientCameraParticipants.length > 0 && (
          <div
            style={{
              position: "fixed",
              top: `${cameraAnchor.top}px`,
              right: `${cameraAnchor.right}px`,
              width: `${cameraAnchor.width}px`,
              zIndex: 12000,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "1.1%",
              maxHeight: `calc(100vh - ${cameraAnchor.top + 96}px)`,
              overflow: "hidden",
              pointerEvents: "none"
            }}
          >
            {recipientCameraParticipants.map(
              (participant) => (
                <WhiteboardCameraTile
                  key={participant.id}
                  participant={participant}
                />
              )
            )}
          </div>
        )}

        {recordingStatus !== "idle" &&
          includeWebcamInRecording &&
          cameraEnabled &&
          localVideoTrack?.readyState === "live" && (
          <div
            style={{
              position: "fixed",
              top: `${recordingCameraAnchor.top}px`,
              right: `${recordingCameraAnchor.right}px`,
              width: `${recordingCameraAnchor.width}px`,
              zIndex: 12010,
              pointerEvents: "none"
            }}
          >
            <RecordingCameraPreview videoTrack={localVideoTrack} />
          </div>
        )}

        {Object.entries(remoteCursors)
          .filter(
            ([, cursor]) =>
              cursor.pageId === activePageId
          )
          .map(([id, cursor]) => (
          <div
            key={id}
            style={{
              position: "absolute",
              left: `${cursor.x * 100}%`,
              top: `${cursor.y * 100}%`,
              transform: "translate(4px, 4px)",
              pointerEvents: "none",
              zIndex: 3
            }}
          >
            <div
              style={{
                width: 0,
                height: 0,
                borderTop: "8px solid transparent",
                borderBottom: "8px solid transparent",
                borderLeft: `12px solid ${cursor.colour}`,
                transform: "rotate(-45deg)",
                transformOrigin: "left center"
              }}
            />
            <span
              style={{
                display: "inline-block",
                marginTop: "1px",
                marginLeft: "9px",
                padding: "0.2rem 0.4rem",
                borderRadius: "6px",
                background: cursor.colour,
                color: "white",
                fontSize: "0.68rem",
                fontWeight: "700",
                whiteSpace: "nowrap",
                boxShadow: "0 2px 8px rgba(15,23,42,.25)"
              }}
            >
              {cursor.name}
            </span>
          </div>
          ))}

        {isTutor && (
          <button
            type="button"
            aria-label="Extend whiteboard downwards"
            title="Add more space beneath this whiteboard"
            onClick={extendWhiteboard}
            style={{
              position: "fixed",
              left: "50%",
              bottom: "88px",
              transform: "translateX(-50%)",
              zIndex: 12003,
              width: "48px",
              height: "38px",
              padding: 0,
              border: "2px solid rgba(255,255,255,.8)",
              borderRadius: "12px",
              background: "rgba(15,23,42,.96)",
              color: "white",
              fontSize: "1.25rem",
              fontWeight: 900,
              lineHeight: 1,
              cursor: "pointer",
              boxShadow: "0 7px 22px rgba(15,23,42,.38)"
            }}
          >
            ↓
          </button>
        )}

        <div
          style={{
            position: "fixed",
            left: "50%",
            bottom: "16px",
            transform: "translateX(-50%)",
            zIndex: 12002,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "clamp(0.25rem, 0.65vw, 0.55rem)",
            width: "max-content",
            maxWidth: "calc(100vw - 28px)",
            boxSizing: "border-box",
            padding: "0.65rem clamp(0.45rem, 0.8vw, 0.8rem)",
            borderRadius: "18px",
            background: "rgba(15,23,42,.96)",
            border: "1px solid rgba(255,255,255,.16)",
            boxShadow: "0 10px 36px rgba(0,0,0,.42)",
            backdropFilter: "blur(10px)",
            flexWrap: "nowrap",
            overflowX: "auto",
            scrollbarWidth: "thin"
          }}
        >
          <button
            type="button"
            onClick={onToggleHandRaised}
            aria-pressed={handRaised}
            style={callControlButtonStyle(
              handRaised
                ? "#b45309"
                : "#253858"
            )}
          >
            {handRaised
              ? "✋ Lower Hand"
              : "✋ Raise Hand"}
          </button>

          <button
            type="button"
            onClick={onToggleMicrophone}
            style={callControlButtonStyle(
              micEnabled
                ? "#253858"
                : "#991b1b"
            )}
          >
            {micEnabled
              ? "🎤 Mute"
              : "🎤 Unmute"}
          </button>

          <button
            type="button"
            onClick={onToggleCamera}
            style={callControlButtonStyle(
              cameraEnabled
                ? "#253858"
                : "#991b1b"
            )}
          >
            {cameraEnabled
              ? "📹 Camera Off"
              : "📹 Camera On"}
          </button>

          <button
            type="button"
            onClick={onStartScreenShare}
            disabled={isScreenSharing}
            style={{
              ...callControlButtonStyle(
                isScreenSharing
                  ? "#475569"
                  : "#253858"
              ),
              opacity:
                isScreenSharing
                  ? 0.58
                  : 1,
              cursor:
                isScreenSharing
                  ? "default"
                  : "pointer"
            }}
          >
            🖥 {isScreenSharing
              ? "Screen Sharing"
              : "Share Screen"}
          </button>

          {isTutor && (
            <button
              type="button"
              onClick={onTutorClose}
              style={callControlButtonStyle("#2563eb")}
            >
              ✏️ Whiteboard
            </button>
          )}

          <button
            type="button"
            onClick={onToggleChat}
            style={{
              ...callControlButtonStyle(
                chatOpen
                  ? "#2563eb"
                  : "#253858"
              ),
              position: "relative"
            }}
          >
            💬 Chat

            {unreadMessages > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "-8px",
                  right: "-8px",
                  minWidth: "20px",
                  height: "20px",
                  padding: "0 4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "999px",
                  background: "#ef4444",
                  color: "white",
                  border: "2px solid #0f172a",
                  fontSize: "0.65rem"
                }}
              >
                {unreadMessages > 99
                  ? "99+"
                  : unreadMessages}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={onLeaveLesson}
            style={callControlButtonStyle("#dc2626")}
          >
            📞 Leave Lesson
          </button>
        </div>

        {pasteMessage && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              bottom: "92px",
              transform: "translateX(-50%)",
              zIndex: 5,
              padding: "0.55rem 0.8rem",
              borderRadius: "999px",
              background: "rgba(15,23,42,.92)",
              color: "white",
              fontSize: "0.78rem",
              fontWeight: "700",
              boxShadow: "0 6px 18px rgba(15,23,42,.28)",
              pointerEvents: "none"
            }}
          >
            {pasteMessage}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

const toolbarButtonStyle = (active, disabled = false) => ({
  border: active
    ? "1px solid #93c5fd"
    : "1px solid rgba(255,255,255,.16)",
  borderRadius: "10px",
  padding: "0.55rem 0.7rem",
  background: active ? "#2563eb" : "#334155",
  color: "white",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.42 : 1,
  fontWeight: "700",
  fontSize: "0.78rem"
});

const sidebarToolStyle = (active) => ({
  width: "46px",
  height: "42px",
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: active
    ? "2px solid #bfdbfe"
    : "1px solid rgba(255,255,255,.16)",
  borderRadius: "10px",
  background: active ? "#2563eb" : "#334155",
  color: "white",
  cursor: "pointer",
  fontSize: "1.05rem",
  fontWeight: 800
});

const flyoutButtonStyle = (active) => ({
  minHeight: "36px",
  padding: "0.45rem 0.55rem",
  border: active
    ? "1px solid #bfdbfe"
    : "1px solid rgba(255,255,255,.14)",
  borderRadius: "8px",
  background: active ? "#2563eb" : "#334155",
  color: "white",
  cursor: "pointer",
  textAlign: "left",
  fontSize: "0.72rem",
  fontWeight: 700
});

const callControlButtonStyle = (background) => ({
  flex: "0 1 auto",
  minWidth: 0,
  border: "none",
  borderRadius: "11px",
  padding: "0.7rem clamp(0.55rem, 1vw, 0.9rem)",
  background,
  color: "white",
  cursor: "pointer",
  fontWeight: "700",
  fontSize: "clamp(0.7rem, 0.9vw, 0.84rem)",
  whiteSpace: "nowrap"
});

const calculatorRoundButtonStyle = {
  width: "34px",
  height: "34px",
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2px solid #111827",
  borderRadius: "50%",
  background: "linear-gradient(145deg,#f8fafc,#9ca3af 52%,#374151)",
  color: "#111827",
  boxShadow:
    "inset 1px 1px 2px rgba(255,255,255,.9),inset -2px -2px 3px rgba(0,0,0,.4)",
  fontSize: "0.58rem",
  fontWeight: 900,
  cursor: "pointer",
  boxSizing: "border-box"
};

const calculatorControlLabelStyle = (color) => ({
  display: "block",
  marginBottom: "3px",
  color,
  fontSize: "0.48rem",
  fontWeight: 900,
  lineHeight: 1,
  letterSpacing: ".02em"
});

export default CollaborativeWhiteboard;
